(() => {
  let isShellToggleGlobalBound = false;
  let headerScrollVisibilityController = null;
  const getRuntimeAdapter = () => window.ThemeRuntimeAdapter || {};
  const THEME_A11Y_STORAGE_KEY = 'theme_a11y_preferences_v1';
  const THEME_A11Y_META_UPDATED_KEY = 'theme_a11y_last_updated';
  const THEME_A11Y_META_SOURCE_KEY = 'theme_a11y_restore_source';
  const THEME_A11Y_ALLOWED_VALUES = {
    font_size: ['sm', 'md', 'lg'],
    contrast: ['default', 'high'],
    motion: ['default', 'reduce'],
    focus_aid: ['off', 'on']
  };
  let themeA11yState = null;
  let themeA11yRestoreSource = 'default';
  let themeA11yStorageAvailable = null;
  let themeA11yActiveRoot = null;
  const THEME_ACCOUNT_OFFER_DISMISS_KEY = 'theme_account_offer_dismiss_until_v1';
  const THEME_ACCOUNT_OFFER_EMAIL_KEY = 'theme_account_offer_email_v1';
  const THEME_ACCOUNT_OFFER_CLAIM_KEY = 'theme_account_offer_claimed_v1';
  const SHOPIFY_PRIVACY_BANNER_SRC =
    'https://cdn.shopify.com/shopifycloud/privacy-banner/storefront-banner.js';
  const SHOPIFY_PRIVACY_BANNER_PATH = '/shopifycloud/privacy-banner/storefront-banner.js';
  const SHOPIFY_PRIVACY_BANNER_LOAD_TIMEOUT = 8000;
  let privacyBannerLoadPromise = null;
  const markFocusFromPointerClick = (element) => window.themeFocusModality?.markProgrammaticFocus(element);

  const getPrivacyBanner = () => {
    const banner = window.privacyBanner;
    return banner && typeof banner.showPreferences === 'function' ? banner : null;
  };

  const findPrivacyBannerScript = () =>
    Array.from(document.scripts).find((script) => {
      try {
        return new URL(script.src, window.location.href).pathname.endsWith(
          SHOPIFY_PRIVACY_BANNER_PATH
        );
      } catch (_error) {
        return false;
      }
    });

  const waitForPrivacyBannerScript = (
    script,
    timeoutMs = SHOPIFY_PRIVACY_BANNER_LOAD_TIMEOUT
  ) =>
    new Promise((resolve, reject) => {
      const finish = (error) => {
        window.clearTimeout(timeoutId);
        script.removeEventListener('load', handleLoad);
        script.removeEventListener('error', handleError);

        if (error) {
          script.dataset.themePrivacyBannerState = 'error';
          reject(error);
          return;
        }

        const banner = getPrivacyBanner();
        if (!banner) {
          script.dataset.themePrivacyBannerState = 'error';
          reject(new Error('Shopify privacy banner API did not initialize.'));
          return;
        }

        script.dataset.themePrivacyBannerState = 'loaded';
        resolve(banner);
      };
      const handleLoad = () => finish();
      const handleError = () => finish(new Error('Shopify privacy banner script failed to load.'));
      const timeoutId = window.setTimeout(
        () => finish(new Error('Shopify privacy banner script load timed out.')),
        timeoutMs
      );

      script.addEventListener('load', handleLoad, { once: true });
      script.addEventListener('error', handleError, { once: true });
    });

  const loadPrivacyBanner = async (timeoutMs = SHOPIFY_PRIVACY_BANNER_LOAD_TIMEOUT) => {
    const availableBanner = getPrivacyBanner();
    if (availableBanner) return availableBanner;

    if (privacyBannerLoadPromise) return privacyBannerLoadPromise;

    privacyBannerLoadPromise = (async () => {
      let script = findPrivacyBannerScript();
      const scriptIsLoading =
        script &&
        (script.dataset.themePrivacyBannerState === 'loading' || document.readyState === 'loading');

      if (scriptIsLoading) {
        try {
          return await waitForPrivacyBannerScript(script, timeoutMs);
        } catch (_error) {
          if (script.parentNode) script.parentNode.removeChild(script);
          script = null;
        }
      } else if (script) {
        if (script.parentNode) script.parentNode.removeChild(script);
        script = null;
      }

      script = document.createElement('script');
      script.src = SHOPIFY_PRIVACY_BANNER_SRC;
      script.async = true;
      script.dataset.themePrivacyBannerState = 'loading';
      document.head.appendChild(script);
      return waitForPrivacyBannerScript(script, timeoutMs);
    })();

    try {
      return await privacyBannerLoadPromise;
    } finally {
      privacyBannerLoadPromise = null;
    }
  };

  const initPrivacyPreferences = (scope = document) => {
    const buttons = [];
    if (scope instanceof Element && scope.matches('[data-theme-privacy-preferences]')) {
      buttons.push(scope);
    }
    buttons.push(...scope.querySelectorAll('[data-theme-privacy-preferences]'));

    buttons.forEach((button) => {
      if (button.dataset.themePrivacyPreferencesBound === 'true') return;

      button.addEventListener('click', async () => {
        const container = button.closest('.theme-site-footer__meta');
        const status = container
          ? container.querySelector('[data-theme-privacy-preferences-status]')
          : null;

        button.disabled = true;
        if (status) status.textContent = 'Opening privacy preferences.';

        try {
          const banner = await loadPrivacyBanner();
          await banner.showPreferences();
          if (status) status.textContent = '';
        } catch (error) {
          console.error('Unable to open Shopify privacy preferences.', error);
          if (status) {
            status.textContent = 'Privacy preferences are unavailable. Please try again.';
          }
        } finally {
          button.disabled = false;
        }
      });

      button.dataset.themePrivacyPreferencesBound = 'true';
    });
  };

  const focusFirstInteractive = (container) => {
    if (!container) return false;

    const firstTarget = container.querySelector(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    if (!firstTarget) return false;
    markFocusFromPointerClick(firstTarget);
    firstTarget.focus();
    return true;
  };

  const scheduleIdleTask = (callback, timeout = 1200) => {
    if (typeof callback !== 'function') return;

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => callback(), { timeout });
      return;
    }

    window.setTimeout(callback, Math.min(timeout, 400));
  };

  const getDefaultA11yState = () => {
    const prefersReducedMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    return {
      font_size: 'md',
      contrast: 'default',
      motion: prefersReducedMotion ? 'reduce' : 'default',
      focus_aid: 'off'
    };
  };

  const sanitizeA11yState = (candidate, fallback) => {
    const defaults = fallback || getDefaultA11yState();
    const next = Object.assign({}, defaults);

    Object.keys(THEME_A11Y_ALLOWED_VALUES).forEach((key) => {
      if (!candidate || typeof candidate[key] !== 'string') return;
      if (THEME_A11Y_ALLOWED_VALUES[key].indexOf(candidate[key]) > -1) {
        next[key] = candidate[key];
      }
    });

    return next;
  };

  const detectA11yStorage = () => {
    if (themeA11yStorageAvailable !== null) {
      return themeA11yStorageAvailable;
    }

    try {
      const probeKey = '__theme_a11y_probe__';
      window.localStorage.setItem(probeKey, '1');
      window.localStorage.removeItem(probeKey);
      themeA11yStorageAvailable = true;
    } catch (error) {
      themeA11yStorageAvailable = false;
    }

    return themeA11yStorageAvailable;
  };

  const readStoredValue = (key) => {
    if (!detectA11yStorage()) return null;

    try {
      return window.localStorage.getItem(key);
    } catch (_error) {
      return null;
    }
  };

  const writeStoredValue = (key, value) => {
    if (!detectA11yStorage()) return;

    try {
      window.localStorage.setItem(key, value);
    } catch (_error) {
      // Intentionally silent.
    }
  };

  const readA11yState = () => {
    const defaults = getDefaultA11yState();

    if (!detectA11yStorage()) {
      return { state: defaults, source: defaults.motion === 'reduce' ? 'os_pref' : 'default' };
    }

    try {
      const raw = window.localStorage.getItem(THEME_A11Y_STORAGE_KEY);
      if (!raw) {
        return { state: defaults, source: defaults.motion === 'reduce' ? 'os_pref' : 'default' };
      }

      const parsed = JSON.parse(raw);
      return { state: sanitizeA11yState(parsed, defaults), source: 'storage' };
    } catch (error) {
      return { state: defaults, source: defaults.motion === 'reduce' ? 'os_pref' : 'default' };
    }
  };

  const writeA11yState = (state, source) => {
    if (!detectA11yStorage()) {
      return;
    }

    try {
      window.localStorage.setItem(THEME_A11Y_STORAGE_KEY, JSON.stringify(state));
      window.localStorage.setItem(THEME_A11Y_META_UPDATED_KEY, String(Date.now()));
      window.localStorage.setItem(THEME_A11Y_META_SOURCE_KEY, source || 'storage');
    } catch (error) {
      // Intentionally silent: memory-state remains active for current session.
    }
  };

  const updateA11yUiStatus = () => {
    const message = detectA11yStorage()
      ? 'Preferences saved and restored automatically.'
      : 'Preferences are active for this session only.';

    document.querySelectorAll('[data-theme-a11y-controls-root]').forEach((root) => {
      const status = root.querySelector('[data-theme-a11y-status]');
      if (status) {
        status.textContent = message;
      }

      root.querySelectorAll('[data-theme-a11y-control]').forEach((button) => {
        const key = button.getAttribute('data-theme-a11y-control');
        const value = button.getAttribute('data-theme-a11y-value');
        const isActive = themeA11yState && themeA11yState[key] === value;
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        button.dataset.themeA11yActive = isActive ? 'true' : 'false';
      });
    });
  };

  const applyA11yState = (state, source) => {
    const next = sanitizeA11yState(state, getDefaultA11yState());
    const root = document.documentElement;
    const body = document.body;
    const targets = [root];

    if (body) {
      targets.push(body);
    }

    targets.forEach((target) => {
      target.setAttribute('data-theme-a11y-font-size', next.font_size);
      target.setAttribute('data-theme-a11y-contrast', next.contrast);
      target.setAttribute('data-theme-a11y-motion', next.motion);
      target.setAttribute('data-theme-a11y-focus-aid', next.focus_aid);
      target.setAttribute('data-theme-a11y-ready', 'true');
    });

    themeA11yState = next;
    themeA11yRestoreSource = source || themeA11yRestoreSource;
    updateA11yUiStatus();
  };

  const bindA11yControls = (scope = document) => {
    const roots = scope.querySelectorAll('[data-theme-a11y-controls-root]');
    roots.forEach((root) => {
      if (root.dataset.themeA11yBound === 'true') return;

      const panel = root.querySelector('.theme-a11y-controls__panel');
      const closeButton = root.querySelector('[data-theme-a11y-close]');
      const reset = root.querySelector('[data-theme-a11y-reset]');
      let lastOpener = null;

      const getOpeners = () => document.querySelectorAll('[data-theme-a11y-open]');

      const syncOpeners = (isExpanded) => {
        getOpeners().forEach((opener) => {
          opener.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        });
      };

      const closePanel = ({ restoreFocus = false } = {}) => {
        if (!panel) return;
        syncOpeners(false);
        panel.hidden = true;
        if (themeA11yActiveRoot === root) {
          themeA11yActiveRoot = null;
        }

        if (restoreFocus && lastOpener?.isConnected) {
          markFocusFromPointerClick(lastOpener);
          lastOpener.focus({ preventScroll: true });
        }

        lastOpener = null;
      };

      const openPanel = (opener) => {
        if (!panel) return;
        if (themeA11yActiveRoot && themeA11yActiveRoot !== root) {
          const activePanel = themeA11yActiveRoot.querySelector('.theme-a11y-controls__panel');
          if (activePanel) activePanel.hidden = true;
        }

        const parentDrawer = opener.closest('details[open]');
        themeA11yActiveRoot = root;
        lastOpener = parentDrawer?.querySelector('summary') || opener;
        if (parentDrawer) parentDrawer.open = false;
        syncOpeners(true);
        panel.hidden = false;
        markFocusFromPointerClick(closeButton || panel);
        (closeButton || panel).focus({ preventScroll: true });
      };

      if (panel) {
        closePanel();

        getOpeners().forEach((opener) => {
          opener.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            const isExpanded = opener.getAttribute('aria-expanded') === 'true';
            if (isExpanded) {
              closePanel({ restoreFocus: true });
              return;
            }

            openPanel(opener);
          });
        });

        if (closeButton) {
          closeButton.addEventListener('click', () => {
            closePanel({ restoreFocus: true });
          });
        }

        document.addEventListener('click', (event) => {
          const clickedOpener = event.target.closest && event.target.closest('[data-theme-a11y-open]');
          if (!root.contains(event.target) && !clickedOpener) {
            closePanel();
          }
        });

        document.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape' || panel.hidden) return;
          event.stopPropagation();
          closePanel({ restoreFocus: true });
        });
      }

      root.querySelectorAll('[data-theme-a11y-control][data-theme-a11y-value]').forEach((button) => {
        button.addEventListener('click', () => {
          const key = button.getAttribute('data-theme-a11y-control');
          const value = button.getAttribute('data-theme-a11y-value');
          if (!key || !value || !THEME_A11Y_ALLOWED_VALUES[key]) return;
          if (THEME_A11Y_ALLOWED_VALUES[key].indexOf(value) === -1) return;

          const next = Object.assign({}, themeA11yState || getDefaultA11yState(), { [key]: value });
          applyA11yState(next, 'storage');
          writeA11yState(next, 'storage');
        });
      });

      if (reset) {
        reset.addEventListener('click', () => {
          const defaults = getDefaultA11yState();
          applyA11yState(defaults, defaults.motion === 'reduce' ? 'os_pref' : 'default');
          writeA11yState(defaults, 'default');
        });
      }

      root.dataset.themeA11yBound = 'true';
    });
  };

  const initA11yControls = (scope = document) => {
    const restored = readA11yState();
    applyA11yState(restored.state, restored.source);
    bindA11yControls(scope);
  };

  const initCartAnnouncementLabel = () => {
    const announce = document.querySelector('.site-cart');
    if (!announce || announce.dataset.themeCartAnnounceBound === 'true') return;

    announce.dataset.themeCartAnnounceBound = 'true';
    announce.addEventListener('focus', () => {
      announce.setAttribute('aria-label', 'Open shopping cart');
    });
  };

  const initAccountOfferRail = (scope = document) => {
    const root = scope.querySelector('[data-theme-account-offer]') || document.querySelector('[data-theme-account-offer]');
    if (!root || root.dataset.themeAccountOfferBound === 'true') return;
    const pathname = window.location.pathname.toLowerCase();
    const accountOfferRoutes = getRuntimeAdapter().accountOfferRoutes || {};
    const isFeaturedRoute = typeof accountOfferRoutes.isFeaturedRoute === 'function'
      ? accountOfferRoutes.isFeaturedRoute(pathname)
      : pathname === '/';
    const isHighFocusRoute = typeof accountOfferRoutes.isHighFocusRoute === 'function'
      ? accountOfferRoutes.isHighFocusRoute(pathname)
      : pathname === '/' || pathname.includes('/collections/') || isFeaturedRoute;

    const bar = root.querySelector('[data-theme-account-offer-bar]');
    const pill = root.querySelector('[data-theme-account-offer-pill]');
    const panel = root.querySelector('[data-theme-account-offer-panel]');
    const openButtons = document.querySelectorAll('[data-theme-account-offer-open], [data-theme-account-offer-pill]');
    const closeButtons = root.querySelectorAll('[data-theme-account-offer-close]');
    const dismissButtons = root.querySelectorAll('[data-theme-account-offer-dismiss]');
    const form = root.querySelector('[data-theme-account-offer-form]');
    const emailInput = form ? form.querySelector('input[type="email"]') : null;
    const verification = root.querySelector('[data-theme-account-offer-verification]');
    const verificationFrame = root.querySelector('[data-theme-account-offer-target]');
    const status = root.querySelector('[data-theme-account-offer-status]');
    const delayMs = Number(root.dataset.themeDelayMs || '24000');
    const dismissDays = Number(root.dataset.themeDismissDays || '14');
    const scrollThreshold = Math.max(0.1, Math.min(0.9, Number(root.dataset.themeScrollThreshold || '40') / 100));
    let isActivated = false;
    let isPanelOpen = false;

    const updateStatus = (message) => {
      if (status) {
        status.textContent = message || '';
      }
    };

    const hideRoot = () => {
      root.hidden = true;
      root.setAttribute('hidden', '');
      root.classList.remove('is-active', 'is-open');
      if (bar) bar.hidden = true;
      if (pill) pill.hidden = true;
      if (panel) {
        panel.hidden = true;
        panel.setAttribute('aria-hidden', 'true');
      }
      isPanelOpen = false;
    };

    const resetVerification = () => {
      if (form) {
        form.hidden = false;
      }

      if (verification) {
        verification.hidden = true;
      }
    };

    const showRoot = () => {
      root.hidden = false;
      root.removeAttribute('hidden');
      root.classList.add('is-active');
      if (bar) bar.hidden = false;
      if (pill) pill.hidden = false;
    };

    const isDismissed = () => {
      const value = Number(readStoredValue(THEME_ACCOUNT_OFFER_DISMISS_KEY) || '0');
      return value > Date.now();
    };

    const isClaimed = () => readStoredValue(THEME_ACCOUNT_OFFER_CLAIM_KEY) === 'true';

    const dismissRail = () => {
      const dismissUntil = Date.now() + dismissDays * 24 * 60 * 60 * 1000;
      writeStoredValue(THEME_ACCOUNT_OFFER_DISMISS_KEY, String(dismissUntil));
      hideRoot();
    };

    const openPanel = () => {
      if (!panel) return;
      showRoot();
      root.classList.add('is-open');
      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      isPanelOpen = true;
      updateStatus('');
      resetVerification();
      if (emailInput) {
        const storedEmail = readStoredValue(THEME_ACCOUNT_OFFER_EMAIL_KEY);
        if (storedEmail && !emailInput.value) {
          emailInput.value = storedEmail;
        }
        emailInput.focus();
      }
    };

    const closePanel = () => {
      if (!panel) return;
      root.classList.remove('is-open');
      panel.hidden = true;
      panel.setAttribute('aria-hidden', 'true');
      isPanelOpen = false;
      resetVerification();
    };

    const activateRail = () => {
      if (isActivated || isDismissed() || isClaimed()) return;
      isActivated = true;
      showRoot();
    };

    const handleScrollTrigger = () => {
      if (isActivated) return;
      const maxScroll = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
      const progress = window.scrollY / maxScroll;
      if (progress >= scrollThreshold) {
        activateRail();
        window.removeEventListener('scroll', handleScrollTrigger);
      }
    };

    hideRoot();

    if (!isHighFocusRoute && !isDismissed() && !isClaimed()) {
      window.setTimeout(activateRail, delayMs);
      window.addEventListener('scroll', handleScrollTrigger, { passive: true });
    }

    openButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        openPanel();
      });
    });

    closeButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        closePanel();
      });
    });

    dismissButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        dismissRail();
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && isPanelOpen) {
        closePanel();
      }
    });

    if (form && emailInput) {
      const phoneInput = form.querySelector('input[name="SMSPHONE"]');
      form.addEventListener('submit', (event) => {
        event.preventDefault();
        updateStatus('');

        const emailValue = String(emailInput.value || '').trim();
        if (!emailValue || !emailInput.checkValidity()) {
          updateStatus('Enter a valid email to continue.');
          emailInput.focus();
          return;
        }

        writeStoredValue(THEME_ACCOUNT_OFFER_EMAIL_KEY, emailValue);
        if (phoneInput) {
          phoneInput.value = String(phoneInput.value || '').trim();
        }

        updateStatus('Opening secure verification...');

        if (verification && verificationFrame) {
          form.hidden = true;
          verification.hidden = false;
        }

        window.requestAnimationFrame(() => {
          form.submit();
        });
      });
    }

    root.dataset.themeAccountOfferBound = 'true';
  };

  const initAccountOfferPrefill = () => {
    const registerEmailField =
      document.querySelector('#ThemeRegisterEmail') || document.querySelector('input[name="customer[email]"]');
    const storedEmail = readStoredValue(THEME_ACCOUNT_OFFER_EMAIL_KEY);

    if (registerEmailField && storedEmail && !registerEmailField.value) {
      registerEmailField.value = storedEmail;
    }

    if (registerEmailField && registerEmailField.form) {
      registerEmailField.form.addEventListener('submit', () => {
        const latestEmail = String(registerEmailField.value || '').trim();
        if (latestEmail) {
          writeStoredValue(THEME_ACCOUNT_OFFER_EMAIL_KEY, latestEmail);
        }
      });
    }

    if (document.body && document.body.dataset.themeCustomerAuthenticated === 'true') {
      writeStoredValue(THEME_ACCOUNT_OFFER_CLAIM_KEY, 'true');
    }
  };

  const syncThemeOptionControlState = (button, { selected = false, soldOut = false, unavailable = false } = {}) => {
    if (!button) return null;
    const disabled = Boolean(soldOut || unavailable);
    const state = soldOut ? 'sold-out' : (unavailable ? 'impossible' : 'available');
    const stateLabel = soldOut ? ' Sold out' : (unavailable ? ' Unavailable' : '');

    button.classList.toggle('is-active', Boolean(selected));
    button.classList.toggle('is-sold-out', Boolean(soldOut));
    button.classList.toggle('is-unavailable', Boolean(unavailable));
    button.setAttribute('aria-pressed', selected ? 'true' : 'false');
    button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
    button.toggleAttribute('disabled', disabled);
    button.dataset.themeOptionState = state;
    const stateNode = button.querySelector('[data-theme-option-control-state]');
    if (stateNode) stateNode.textContent = stateLabel;
    return { state, disabled };
  };

  window.ThemeOptionControl = window.ThemeOptionControl || { sync: syncThemeOptionControlState };

  // One note decision for PDP and Quick Add; inventory counts never determine availability.
  const renderAvailabilityNote = (root, variants, selection, variant, familyHasAvailable) => {
    const note = root?.querySelector('[data-theme-availability-note]');
    if (!note) return '';
    const labels = note.dataset;
    const source = root.querySelector('[data-theme-availability-source]');
    const productAvailable = variants.some((candidate) => candidate.available === true);
    const familyAvailable = familyHasAvailable ?? (source?.dataset.themeAvailabilityFamilyHasAvailable === 'true');
    let optionNames = [];
    try { optionNames = JSON.parse(labels.themeNoteOptions || '[]'); } catch { /* Missing option metadata uses the generic instruction. */ }
    const instructionForAxis = (index) => {
      const name = String(optionNames[index] || '').trim().toLowerCase();
      const axis = name === 'inside circumference' ? 'size' : name;
      if (!['color', 'colour', 'wash', 'size', 'finish', 'style'].includes(axis)) return labels.themeNoteSelected;
      if (axis === 'color') return labels.themeNoteColor;
      return (labels.themeNoteOption || labels.themeNoteSelected).replace('__theme_option__', axis);
    };
    let message = '';
    if (variant && variants.length && !productAvailable && familyAvailable) {
      const familyAxes = optionNames.map((name, index) => /^(color|wash)$/i.test(String(name).trim()) ? index : -1).filter((index) => index >= 0);
      message = familyAxes.length === 1 ? instructionForAxis(familyAxes[0]) : labels.themeNoteSelected;
    } else if (productAvailable && variant && variant.available === false) {
      const selectedOptions = variant.options || selection;
      const changedAxes = new Set();
      variants.filter((candidate) => candidate.available === true).forEach((candidate) => {
        (candidate.options || []).forEach((value, index) => {
          if (value !== selectedOptions[index]) changedAxes.add(index);
        });
      });
      message = changedAxes.size === 1 ? instructionForAxis([...changedAxes][0]) : labels.themeNoteSelected;
      if (changedAxes.size === 1 && message !== labels.themeNoteSelected) {
        const axis = [...changedAxes][0];
        const controls = root.querySelectorAll('button[data-theme-pdp-option], button[data-theme-quick-view-option]');
        const hasVisibleAlternative = Array.from(controls).some((button) => {
          const position = Number(button.getAttribute('data-theme-pdp-option-position') ?? button.getAttribute('data-theme-quick-view-option-position'));
          const value = button.getAttribute('data-theme-pdp-option-value') ?? button.getAttribute('data-theme-quick-view-option-value');
          if (position !== axis || button.disabled || button.getAttribute('aria-disabled') === 'true' || !button.getClientRects().length) return false;
          const visibility = window.getComputedStyle(button).visibility;
          if (visibility === 'hidden' || visibility === 'collapse') return false;
          return variants.some((candidate) => candidate.available === true && candidate.options?.[axis] === value &&
            candidate.options.every((option, index) => index === axis || option === selectedOptions[index]));
        });
        if (hasVisibleAlternative) message = '';
      }
    }
    // Avoid repeating announcements during quantity, gallery or other unchanged syncs.
    if (note.textContent !== message) {
      const status = root.querySelector('[data-theme-availability-note-status]');
      if (status) status.textContent = message || (variant?.available ? status.dataset.themeAvailableLabel || '' : '');
      note.textContent = message || '';
    }
    note.hidden = !message;
    return message || labels.themeNoteAvailable || '';
  };
  window.ThemeAvailabilityNote = { render: renderAvailabilityNote };

  const bindThemeQuantityControl = (scope, { onChange } = {}) => {
    const control = scope?.matches?.('[data-theme-quantity-control]') ? scope : scope?.querySelector?.('[data-theme-quantity-control]');
    const input = control?.querySelector?.('[data-theme-quantity-input]');
    if (!control || !input) return null;

    const normalize = () => {
      const min = Math.max(1, Number.parseInt(input.getAttribute('min') || '1', 10) || 1);
      const step = Math.max(1, Number.parseInt(input.getAttribute('step') || '1', 10) || 1);
      const parsed = Number.parseInt(input.value || String(min), 10);
      const value = Number.isFinite(parsed) ? Math.max(min, parsed) : min;
      input.value = String(value);
      return { min, step, value };
    };
    const notify = () => {
      normalize();
      if (typeof control.__themeQuantityOnChange === 'function') control.__themeQuantityOnChange(input);
    };
    const adjust = (delta) => {
      const { min, step, value } = normalize();
      const next = Math.max(min, value + (step * delta));
      if (next === value) return;
      input.value = String(next);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    };

    control.__themeQuantityOnChange = onChange;
    if (control.dataset.themeQuantityControlBound !== 'true') {
      control.querySelector('[data-theme-quantity-decrease]')?.addEventListener('click', () => adjust(-1));
      control.querySelector('[data-theme-quantity-increase]')?.addEventListener('click', () => adjust(1));
      input.addEventListener('input', notify);
      input.addEventListener('change', notify);
      control.dataset.themeQuantityControlBound = 'true';
    }
    normalize();
    return {
      input,
      reset() {
        input.value = String(Math.max(1, Number.parseInt(input.getAttribute('min') || '1', 10) || 1));
        notify();
      }
    };
  };

  window.ThemeQuantityControl = window.ThemeQuantityControl || { bind: bindThemeQuantityControl };

  let lowStockCountsStale = false;
  const renderLowStock = (node, variant, stale = false) => {
    if (!node) return;
    const quantity = Number(variant?.inventory_quantity);
    const threshold = Number(node.dataset.themeLowStockThreshold);
    const eligible = node.dataset.themeLowStockEnabled === 'true' && !stale &&
      variant?.available === true && variant?.inventory_management === 'shopify' &&
      variant?.inventory_policy === 'deny' && Number.isSafeInteger(quantity) &&
      quantity > 0 && Number.isSafeInteger(threshold) && quantity < threshold;
    if (!eligible) {
      if (node.textContent) node.textContent = '';
      node.hidden = true;
      return;
    }
    const sizeIndex = Number(node.dataset.themeLowStockSizeIndex);
    const size = node.dataset.themeLowStockSizeIndex !== undefined && Array.isArray(variant.options)
      ? String(variant.options[sizeIndex] || '').trim() : '';
    const message = node.dataset.themeLowStockMultipleOptions === 'true'
      ? `Only ${quantity} left for this selection.`
      : `Only ${quantity} left${size ? ` in size ${size}` : ''}.`;
    if (node.textContent.trim() !== message) node.textContent = message;
    node.hidden = false;
  };
  window.ThemeLowStock = { render: renderLowStock };

  const invalidateLowStock = () => {
    lowStockCountsStale = true;
    document.querySelectorAll('.theme-main-product [data-theme-low-stock], [data-theme-quick-view-fragment] [data-theme-low-stock]').forEach((node) => {
      node.textContent = '';
      node.hidden = true;
    });
    document.dispatchEvent(new Event('theme:low-stock:invalidate'));
  };
  document.addEventListener('theme:cart:add', invalidateLowStock);
  document.addEventListener('change', (event) => {
    if (event.target?.matches?.('#CartDrawer .quantity__input')) {
      invalidateLowStock();
      event.target.closest('cart-drawer')?.querySelectorAll('[data-theme-low-stock]').forEach((node) => {
        node.textContent = '';
        node.hidden = true;
      });
    }
  }, true);
  document.addEventListener('click', (event) => {
    if (event.target?.closest?.('[id^="CartDrawer-Remove-"] button')) {
      invalidateLowStock();
      event.target.closest('cart-drawer')?.querySelectorAll('[data-theme-low-stock]').forEach((node) => {
        node.textContent = '';
        node.hidden = true;
      });
    }
  }, true);

  const initPdpEventPayloadSync = (scope = document) => {
    const sections = scope.querySelectorAll('.theme-main-product');
    sections.forEach((section) => {
      if (section.dataset.themePdpSyncBound === 'true') return;

      const variantInput = section.querySelector('#theme-variant-select');
      const variantChips = Array.from(section.querySelectorAll('[data-theme-variant-chip]'));
      const variantOptionButtons = Array.from(section.querySelectorAll('[data-theme-pdp-option]'));
      const variantOptionSelects = Array.from(section.querySelectorAll('[data-theme-pdp-option-select]'));
      const availabilityBadge = section.querySelector('[data-theme-pdp-availability-badge]');
      const variantMatrixNode = section.querySelector('[data-theme-pdp-variant-matrix]');
      const variantForm = variantInput?.form || variantInput?.closest('form');
      const qtyInput = section.querySelector('#theme-qty');
      const addToCartButton = section.querySelector('#theme-pdp-atc');
      const wishlistButton = section.querySelector('[data-theme-wishlist-button]');
      const lowStock = section.querySelector('[data-theme-low-stock]');

      if (!variantInput || !qtyInput || !addToCartButton) return;

      const parseVariantMatrix = () => {
        if (!variantMatrixNode) return [];
        try {
          const matrix = JSON.parse(variantMatrixNode.textContent || '[]');
          if (!Array.isArray(matrix)) return [];
          return matrix.filter((variant) => variant && variant.id != null && Array.isArray(variant.options)).map((variant) => ({
            id: String(variant.id),
            title: String(variant.title || ''),
            price: String(variant.price || ''),
            formatted_price: variant.formatted_price,
            formatted_compare_at_price: variant.formatted_compare_at_price,
            available: variant.available === true,
            inventory_management: variant.inventory_management,
            inventory_policy: variant.inventory_policy,
            inventory_quantity: variant.inventory_quantity,
            options: variant.options.map((value) => String(value ?? ''))
          }));
        } catch (_error) {
          return [];
        }
      };

      const pdpVariants = parseVariantMatrix();
      const initialPdpVariant = pdpVariants.find((variant) => variant.id === String(variantInput.value || '')) ||
        pdpVariants.find((variant) => variant.available) || pdpVariants[0] || null;
      const pdpOptionState = initialPdpVariant && (variantOptionButtons.length || variantOptionSelects.length) ? {
        variants: pdpVariants,
        selection: initialPdpVariant.options.slice(),
        resolvedVariant: initialPdpVariant,
        userInteracted: false
      } : null;

      const resolvePdpVariant = (selection) => {
        if (!pdpOptionState) return null;
        return pdpOptionState.variants.find((variant) =>
          variant.options.length === selection.length && variant.options.every((value, index) => value === selection[index])
        ) || null;
      };

      const resolvePdpCandidate = (position, value) => {
        if (!pdpOptionState) return null;
        const candidateSelection = pdpOptionState.selection.slice();
        candidateSelection[position] = value;
        return resolvePdpVariant(candidateSelection);
      };

      const isPdpOptionReachable = (position, value) => {
        if (!pdpOptionState) return false;
        return pdpOptionState.variants.some((variant) =>
          variant.options.length === pdpOptionState.selection.length &&
          variant.options[position] === value &&
          variant.options.every((candidate, index) => index === position || candidate === pdpOptionState.selection[index])
        );
      };

      const selectedVariant = () => {
        if (pdpOptionState?.resolvedVariant) {
          const variant = pdpOptionState.resolvedVariant;
          return {
            id: variant.id,
            title: variant.title,
            price: variant.price,
            available: variant.available ? 'true' : 'false'
          };
        }
        const selectedVariantId = variantInput.value || addToCartButton.dataset.themeSelectedVariantId || '';
        const activeChip = variantChips.find((chip) => chip.dataset.themeVariantId === selectedVariantId);
        return {
          id: selectedVariantId,
          title: activeChip?.dataset.themeVariantTitle || variantInput.dataset.themeVariantTitle || '',
          price: activeChip?.dataset.themeVariantPrice || variantInput.dataset.themeVariantPrice || '',
          available: activeChip?.dataset.themeVariantAvailable || variantInput.dataset.themeVariantAvailable || 'false'
        };
      };

      const updatePdpOptionUrl = (variant) => {
        if (!pdpOptionState?.userInteracted || !variant || !window.history?.replaceState || !window.location?.href) return;
        try {
          const nextUrl = new URL(window.location.href);
          nextUrl.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
        } catch (_error) {
          // A host page can restrict history access; form selection must still work.
        }
      };

      const ensureVariantFormValue = () => {
        const variant = selectedVariant();
        if (variant.id && variantInput.value !== variant.id) {
          variantInput.value = variant.id;
        }
        return variant;
      };

      const syncAddToCartEventPayload = () => {
        const variant = ensureVariantFormValue();
        const lowStockVariant = pdpOptionState ? pdpOptionState.resolvedVariant :
          pdpVariants.find((candidate) => candidate.id === String(variant.id)) || {
            available: variant.available === 'true',
            inventory_management: variantInput.dataset.themeVariantInventoryManagement,
            inventory_policy: variantInput.dataset.themeVariantInventoryPolicy,
            inventory_quantity: variantInput.dataset.themeVariantInventoryQuantity,
            options: (() => {
              try { return JSON.parse(variantInput.dataset.themeVariantOptions || '[]'); }
              catch (_error) { return []; }
            })()
          };
        renderLowStock(lowStock, lowStockVariant, lowStockCountsStale);
        const labelNode = addToCartButton.querySelector('[data-theme-pdp-atc-label]') || addToCartButton.querySelector('.theme-main-product__atc-label');
        let normalizedQty = parseInt(qtyInput.value, 10);

        renderAvailabilityNote(section, pdpVariants, pdpOptionState?.selection || lowStockVariant.options || [], lowStockVariant);

        if (!variant.id) return;

        if (!normalizedQty || normalizedQty < 1) {
          normalizedQty = 1;
          qtyInput.value = '1';
        }

        addToCartButton.dataset.themeEventItemVariant = variant.title;
        addToCartButton.dataset.themeEventPrice = variant.price;
        addToCartButton.dataset.themeEventQuantity = String(normalizedQty);
        addToCartButton.dataset.themeSelectedVariantId = variant.id;

        const isSoldOut = variant.available === 'false';
        const soldOutLabel = addToCartButton.dataset.themeSoldOutLabel || 'Sold Out';

        if (availabilityBadge) {
          const productAvailable = pdpVariants.length
            ? pdpVariants.some((candidate) => candidate.available)
            : availabilityBadge.dataset.themeAvailabilityProductAvailable === 'true';
          const familyHasAvailable = availabilityBadge.dataset.themeAvailabilityFamilyHasAvailable === 'true';
          const availabilityLabel = productAvailable ? '' : (familyHasAvailable ? 'Color sold out' : 'Sold out');
          availabilityBadge.textContent = availabilityLabel;
          availabilityBadge.hidden = !availabilityLabel;
          availabilityBadge.classList.toggle('theme-availability-badge--color-sold-out', availabilityLabel === 'Color sold out');
          availabilityBadge.classList.toggle('theme-availability-badge--sold-out', availabilityLabel === 'Sold out');
        }

        addToCartButton.dataset.themeSoldOut = isSoldOut ? 'true' : 'false';

        if (isSoldOut) {
          addToCartButton.disabled = true;
          addToCartButton.setAttribute('aria-disabled', 'true');

          if (labelNode) {
            labelNode.textContent = soldOutLabel;
          }
        } else {
          addToCartButton.disabled = false;
          addToCartButton.setAttribute('aria-disabled', 'false');
        }

        if (wishlistButton && variant.id) {
          wishlistButton.dataset.variantId = variant.id;
          wishlistButton.dataset.themeVariantId = variant.id;
        }

        const onVariantChange = getRuntimeAdapter().onVariantChange;
        if (typeof onVariantChange === 'function') {
          onVariantChange({ section, variant, wishlistButton });
        }

        if (variantChips.length) {
          variantChips.forEach((chip) => {
            const isActive = chip.dataset.themeVariantId === variant.id;
            chip.classList.toggle('is-active', isActive);
            chip.setAttribute('aria-pressed', isActive ? 'true' : 'false');
            if (chip.dataset.themeVariantAvailable === 'false') {
              chip.classList.add('is-sold-out');
              chip.disabled = true;
              chip.setAttribute('aria-disabled', 'true');
            } else {
              chip.classList.remove('is-sold-out');
              chip.disabled = false;
              chip.setAttribute('aria-disabled', 'false');
            }
          });
        }
      };

      const refreshPdpOptionState = () => {
        if (!pdpOptionState) return;
        const variant = resolvePdpVariant(pdpOptionState.selection);
        pdpOptionState.resolvedVariant = variant;

        variantOptionButtons.forEach((button) => {
          const position = Number(button.dataset.themePdpOptionPosition);
          const value = button.dataset.themePdpOptionValue || '';
          const selected = pdpOptionState.selection[position] === value;
          const reachable = Number.isInteger(position) && position >= 0 && isPdpOptionReachable(position, value);
          const candidate = reachable ? resolvePdpCandidate(position, value) : null;
          const soldOut = Boolean(candidate && candidate.available === false);

          syncThemeOptionControlState(button, {
            selected,
            soldOut,
            unavailable: !reachable
          });
        });

        const colorGroup = section.querySelector('[data-theme-pdp-color-position]');
        const colorValue = colorGroup?.querySelector('.theme-main-product__color-value');
        if (colorValue) colorValue.textContent = pdpOptionState.selection[Number(colorGroup.dataset.themePdpColorPosition)] || '';

        variantOptionSelects.forEach((select) => {
          const position = Number(select.dataset.themePdpOptionPosition);
          Array.from(select.options).forEach((option) => {
            const candidate = resolvePdpCandidate(position, option.value);
            const label = option.dataset.themePdpOptionLabel || option.value;
            const stateLabel = !candidate ? select.dataset.themeUnavailableLabel :
              candidate.available ? '' : select.dataset.themeSoldOutLabel;
            option.disabled = !candidate;
            option.textContent = stateLabel ? `${label} — ${stateLabel}` : label;
            option.selected = pdpOptionState.selection[position] === option.value;
          });
          select.value = pdpOptionState.selection[position];
        });

        if (variant) {
          const price = section.querySelector('.theme-main-product__price');
          if (price && variant.formatted_price) {
            const currentPrice = document.createElement('span');
            currentPrice.textContent = variant.formatted_price;
            price.replaceChildren(currentPrice);
            if (variant.formatted_compare_at_price) {
              const comparePrice = document.createElement('s');
              comparePrice.textContent = variant.formatted_compare_at_price;
              price.append(comparePrice);
            }
          }
          variantInput.value = variant.id;
          variantInput.dataset.themeVariantTitle = variant.title;
          variantInput.dataset.themeVariantPrice = variant.price;
          variantInput.dataset.themeVariantAvailable = variant.available ? 'true' : 'false';
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          updatePdpOptionUrl(variant);
        }
      };

      variantInput.addEventListener('change', syncAddToCartEventPayload);
      variantForm?.addEventListener('submit', ensureVariantFormValue);
      variantChips.forEach((chip) => {
        chip.addEventListener('click', () => {
          const nextVariantId = chip.dataset.themeVariantId;
          if (!nextVariantId || chip.classList.contains('is-sold-out')) return;
          if (variantInput.value === nextVariantId) return;

          if (chip.dataset.themeVariantAvailable === 'false') return;

          variantInput.value = nextVariantId;
          variantInput.dataset.themeVariantTitle = chip.dataset.themeVariantTitle || '';
          variantInput.dataset.themeVariantPrice = chip.dataset.themeVariantPrice || '';
          variantInput.dataset.themeVariantAvailable = chip.dataset.themeVariantAvailable || 'false';
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
      variantOptionButtons.forEach((button) => {
        button.addEventListener('click', () => {
          const position = Number(button.dataset.themePdpOptionPosition);
          const value = button.dataset.themePdpOptionValue || '';
          if (!pdpOptionState || !Number.isInteger(position) || position < 0 || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
          const candidate = resolvePdpCandidate(position, value);
          if (!candidate || candidate.available === false) return;
          pdpOptionState.selection[position] = value;
          pdpOptionState.userInteracted = true;
          refreshPdpOptionState();
        });
      });
      variantOptionSelects.forEach((select) => {
        select.addEventListener('change', () => {
          const position = Number(select.dataset.themePdpOptionPosition);
          if (!pdpOptionState || !Number.isInteger(position) || position < 0) return;
          if (!resolvePdpCandidate(position, select.value)) return;
          pdpOptionState.selection[position] = select.value;
          pdpOptionState.userInteracted = true;
          refreshPdpOptionState();
        });
      });
      bindThemeQuantityControl(section, { onChange: syncAddToCartEventPayload });
      if (pdpOptionState) refreshPdpOptionState();
      else syncAddToCartEventPayload();
      section.dataset.themePdpSyncBound = 'true';
    });
  };

  const THEME_WISHLIST_AUTH_RETURN_KEY = 'theme_wishlist_auth_return_v1';
  const THEME_WISHLIST_AUTH_RETURN_TTL = 30 * 60 * 1000;

  const initWishlistButtons = (scope = document) => {
    const runtimeAdapter = getRuntimeAdapter();
    const wishlistProvider = runtimeAdapter.wishlistProvider || null;
    const wishlistRoutes = runtimeAdapter.wishlistRoutes || {};

    const wishlistButtons = scope.querySelectorAll('[data-theme-wishlist-button]');
    const wishlistPage = document.querySelector('[data-theme-page-handle="wishlist"]');
    const wishlistPanel = wishlistPage?.querySelector('[data-theme-wishlist-panel]') || null;
    const wishlistOpenTrigger = wishlistPage?.querySelector('[data-theme-wishlist-open]') || null;
    const isCustomerAuthenticated = document.body?.dataset.themeCustomerAuthenticated === 'true';

    if (!wishlistButtons.length && !wishlistPage) {
      return;
    }

    const normalizeReturnUrl = (value) => {
      if (!value) return '/';

      try {
        const url = new URL(String(value), window.location.origin);
        if (url.origin !== window.location.origin) return '/';

        const normalizedPath = url.pathname.replace(/\/+$/, '') || '/';
        return `${normalizedPath}${url.search}${url.hash}`;
      } catch (_error) {
        const raw = String(value);
        const hashIndex = raw.indexOf('#');
        const hash = hashIndex >= 0 ? raw.slice(hashIndex) : '';
        const pathWithSearch = hashIndex >= 0 ? raw.slice(0, hashIndex) : raw;
        const searchIndex = pathWithSearch.indexOf('?');
        const search = searchIndex >= 0 ? pathWithSearch.slice(searchIndex) : '';
        const pathname = searchIndex >= 0 ? pathWithSearch.slice(0, searchIndex) : pathWithSearch;
        const normalizedPath = pathname.replace(/\/+$/, '') || '/';
        return `${normalizedPath}${search}${hash}`;
      }
    };
    const currentReturnUrl = () => normalizeReturnUrl(`${window.location.pathname || '/'}${window.location.search || ''}${window.location.hash || ''}`);
    const wishlistReturnPath = normalizeReturnUrl(
      wishlistPage?.querySelector('[data-theme-wishlist-auth-link]')?.dataset.themeWishlistReturnUrl ||
      wishlistOpenTrigger?.dataset.themeWishlistReturnUrl ||
      wishlistRoutes.returnPath ||
      currentReturnUrl()
    );
    const supportedWishlistContexts = new Set(['card', 'pdp']);
    const wasRecentlyTriggered = (button) => {
      const triggeredAt = Number(button?.dataset.themeWishlistTriggeredAt || 0);
      if (!Number.isFinite(triggeredAt) || triggeredAt <= 0) return false;
      return Date.now() - triggeredAt < 2500;
    };
    const getWishlistAuthStorage = () => {
      try {
        return window.sessionStorage;
      } catch (_error) {
        return null;
      }
    };
    const readWishlistAuthReturn = () => {
      const storage = getWishlistAuthStorage();
      if (!storage) return null;

      try {
        const raw = storage.getItem(THEME_WISHLIST_AUTH_RETURN_KEY);
        if (!raw) return null;

        const parsed = JSON.parse(raw);
        const path = normalizeReturnUrl(parsed?.path || '');
        const savedAt = Number(parsed?.savedAt || 0);

        if (!path || !Number.isFinite(savedAt)) {
          storage.removeItem(THEME_WISHLIST_AUTH_RETURN_KEY);
          return null;
        }

        if (Date.now() - savedAt > THEME_WISHLIST_AUTH_RETURN_TTL) {
          storage.removeItem(THEME_WISHLIST_AUTH_RETURN_KEY);
          return null;
        }

        return { path, savedAt };
      } catch (_error) {
        storage.removeItem(THEME_WISHLIST_AUTH_RETURN_KEY);
        return null;
      }
    };
    const writeWishlistAuthReturn = (path) => {
      const storage = getWishlistAuthStorage();
      if (!storage) return;

      try {
        storage.setItem(THEME_WISHLIST_AUTH_RETURN_KEY, JSON.stringify({
          path: normalizeReturnUrl(path || wishlistReturnPath),
          savedAt: Date.now()
        }));
      } catch (_error) {
        // Storage is optional. Wishlist auth should still work without it.
      }
    };
    const clearWishlistAuthReturn = () => {
      const storage = getWishlistAuthStorage();
      if (!storage) return;

      try {
        storage.removeItem(THEME_WISHLIST_AUTH_RETURN_KEY);
      } catch (_error) {
        // Ignore storage cleanup failures.
      }
    };
    const ensureWishlistSyncBubble = () => {
      let bubble = document.querySelector('[data-theme-wishlist-sync-bubble]');
      if (bubble) return bubble;

      bubble = document.createElement('aside');
      bubble.className = 'theme-wishlist-sync-bubble';
      bubble.hidden = true;
      bubble.setAttribute('aria-hidden', 'true');
      bubble.setAttribute('aria-live', 'polite');
      bubble.setAttribute('data-theme-wishlist-sync-bubble', '');
      bubble.innerHTML = `
        <button type="button" class="theme-wishlist-sync-bubble__close" data-theme-wishlist-sync-close aria-label="Dismiss wishlist sync message">Close</button>
        <p class="theme-wishlist-sync-bubble__eyebrow">Saved for now</p>
        <h3>Sync this wishlist to your account</h3>
        <p>Keep this item beyond the current browser by signing in or creating an account.</p>
        <div class="theme-wishlist-sync-bubble__actions">
          <a href="/account/login" data-theme-wishlist-auth-link data-theme-wishlist-return-current="true">Sign in</a>
          <a href="/account/login" data-theme-wishlist-auth-link data-theme-wishlist-return-current="true">Create account</a>
        </div>
      `;
      document.body.appendChild(bubble);
      return bubble;
    };
    const syncBubble = !isCustomerAuthenticated ? ensureWishlistSyncBubble() : null;
    let syncBubbleTimer = null;
    const hideWishlistSyncBubble = () => {
      if (!syncBubble) return;

      if (syncBubbleTimer) {
        window.clearTimeout(syncBubbleTimer);
        syncBubbleTimer = null;
      }

      syncBubble.hidden = true;
      syncBubble.setAttribute('aria-hidden', 'true');
      syncBubble.dataset.themeState = 'closed';
      delete syncBubble.dataset.themeWishlistContext;
    };
    const showWishlistSyncBubble = (button) => {
      if (!syncBubble || !button) return;

      const context = button.dataset.themeWishlistContext || 'default';
      if (!supportedWishlistContexts.has(context)) return;

      if (syncBubbleTimer) {
        window.clearTimeout(syncBubbleTimer);
      }

      syncBubble.hidden = false;
      syncBubble.setAttribute('aria-hidden', 'false');
      syncBubble.dataset.themeState = 'open';
      syncBubble.dataset.themeWishlistContext = context;
      syncBubbleTimer = window.setTimeout(() => {
        hideWishlistSyncBubble();
      }, 7000);
    };
    const bindWishlistSyncBubble = () => {
      if (!syncBubble || syncBubble.dataset.themeWishlistSyncBound === 'true') return;

      const closeButton = syncBubble.querySelector('[data-theme-wishlist-sync-close]');
      if (closeButton) {
        closeButton.addEventListener('click', () => {
          hideWishlistSyncBubble();
        });
      }

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          hideWishlistSyncBubble();
        }
      });

      document.addEventListener('pointerdown', (event) => {
        if (syncBubble.hidden) return;
        if (syncBubble.contains(event.target)) return;
        hideWishlistSyncBubble();
      });

      syncBubble.dataset.themeWishlistSyncBound = 'true';
    };
    const getWishlistPayload = (button) => {
      const productId = Number(button?.dataset.productId || button?.dataset.themeProductId || 0);
      const variantId = Number(button?.dataset.variantId || button?.dataset.themeVariantId || 0);

      if (!Number.isFinite(productId) || !Number.isFinite(variantId) || productId <= 0 || variantId <= 0) {
        return null;
      }

      return {
        empi: productId,
        epi: variantId
      };
    };
    const getWishlistButtonLabel = (button) => button?.dataset.themeWishlistLabel || 'Save to wishlist';
    const updateWishlistButtonA11y = (button, isAdded) => {
      if (!button) return;

      button.setAttribute('aria-pressed', isAdded ? 'true' : 'false');
      button.setAttribute('aria-label', isAdded ? 'Remove from wishlist' : getWishlistButtonLabel(button));
    };
    const setWishlistButtonRemoving = (button, isRemoving) => {
      if (!button) return;

      button.classList.toggle('theme-wishlist-button--removing', isRemoving);
      if (isRemoving) {
        button.dataset.themeWishlistRemoving = 'true';
        button.setAttribute('aria-busy', 'true');
        return;
      }

      delete button.dataset.themeWishlistRemoving;
      button.removeAttribute('aria-busy');
    };
    const setWishlistButtonSavedState = (button, isAdded) => {
      if (!button) return;

      button.classList.toggle('theme-wishlist-saved', isAdded);
      button.classList.remove('theme-wishlist-loading');

      if (isAdded) {
        button.classList.add('theme-wishlist-loaded');
      } else {
        button.classList.remove('disabled');
      }

      button.dataset.themeWishlistState = isAdded ? 'saved' : 'idle';
      updateWishlistButtonA11y(button, isAdded);
      syncWishlistButtonState(button);
    };
    const removeWishlistItem = (button) => {
      if (!button || button.dataset.themeWishlistRemoving === 'true') {
        return;
      }

      const payload = getWishlistPayload(button);
      if (!payload) return;

      hideWishlistSyncBubble();
      setWishlistButtonRemoving(button, true);

      if (typeof wishlistProvider?.remove === 'function') {
        const handled = wishlistProvider.remove({
          button,
          payload,
          onComplete: ({ isSaved = false } = {}) => {
            if (typeof wishlistProvider.setSavedState === 'function') {
              wishlistProvider.setSavedState(button, isSaved);
            }
            setWishlistButtonRemoving(button, false);
            syncWishlistButtonState(button);
            delete button.dataset.themeWishlistTriggeredAt;
          },
          onError: () => {
            setWishlistButtonRemoving(button, false);
          }
        });

        if (!handled) {
          setWishlistButtonRemoving(button, false);
        }
        return;
      }

      setWishlistButtonRemoving(button, false);
      setWishlistButtonSavedState(button, false);
      delete button.dataset.themeWishlistTriggeredAt;
    };

    bindWishlistSyncBubble();

    const syncWishlistButtonState = (button) => {
      if (!button) return;

      const previousState = button.dataset.themeWishlistState || 'idle';
      const wasInitialized = button.dataset.themeWishlistInitialized === 'true';
      const isAdded = typeof wishlistProvider?.isSaved === 'function'
        ? Boolean(wishlistProvider.isSaved(button))
        : button.classList.contains('theme-wishlist-saved');
      const nextState = isAdded ? 'saved' : 'idle';

      button.dataset.themeWishlistState = nextState;
      updateWishlistButtonA11y(button, isAdded);

      if (!isCustomerAuthenticated && wasInitialized && previousState !== nextState && nextState === 'saved' && wasRecentlyTriggered(button)) {
        showWishlistSyncBubble(button);
      }

      if (previousState !== nextState && nextState === 'saved') {
        delete button.dataset.themeWishlistTriggeredAt;
      }

      button.dataset.themeWishlistInitialized = 'true';
    };

    wishlistButtons.forEach((button) => {
      if (button.dataset.themeWishlistBound === 'true') return;

      syncWishlistButtonState(button);

      const observer = new MutationObserver(() => {
        syncWishlistButtonState(button);
      });

      observer.observe(button, {
        attributes: true,
        attributeFilter: ['class']
      });

      button.addEventListener('click', (event) => {
        const isSaved = typeof wishlistProvider?.isSaved === 'function'
          ? Boolean(wishlistProvider.isSaved(button))
          : button.classList.contains('theme-wishlist-saved') || button.dataset.themeWishlistState === 'saved';
        if (isSaved) {
          event.preventDefault();
          event.stopImmediatePropagation();
          removeWishlistItem(button);
          return;
        }

        button.dataset.themeWishlistTriggeredAt = String(Date.now());
      }, true);

      button.dataset.themeWishlistBound = 'true';
    });

    const wishlistAuthLinks = document.querySelectorAll('[data-theme-wishlist-auth-link]');
    wishlistAuthLinks.forEach((link) => {
      if (link.dataset.themeWishlistAuthBound === 'true') return;

      link.addEventListener('click', (event) => {
        if (event.defaultPrevented) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        if (link.target && link.target !== '_self') return;

        writeWishlistAuthReturn(
          link.dataset.themeWishlistReturnCurrent === 'true'
            ? currentReturnUrl()
            : (link.dataset.themeWishlistReturnUrl || wishlistReturnPath)
        );
      });

      link.dataset.themeWishlistAuthBound = 'true';
    });

    if (document.body?.dataset.themeWishlistAuthResumeBound !== 'true') {
      const pendingWishlistReturn = readWishlistAuthReturn();

      if (pendingWishlistReturn && isCustomerAuthenticated) {
        clearWishlistAuthReturn();

        if (currentReturnUrl() !== pendingWishlistReturn.path) {
          window.location.replace(pendingWishlistReturn.path);
          return;
        }
      }

      if (!pendingWishlistReturn && currentReturnUrl() === wishlistReturnPath) {
        clearWishlistAuthReturn();
      }

      document.body.dataset.themeWishlistAuthResumeBound = 'true';
    }

    if (wishlistPage && wishlistPage.dataset.themeWishlistPageBound !== 'true') {
      wishlistPage.dataset.themeWishlistAuthState = isCustomerAuthenticated ? 'account' : 'guest';

      const markWishlistReady = () => {
        if (!wishlistPanel) return;
        wishlistPanel.dataset.themeWishlistReady = 'true';

        const title = wishlistPanel.querySelector('h2');
        const copy = wishlistPanel.querySelector('p:last-child');

        if (title) title.textContent = 'Your saved items are ready';
        if (copy) {
          copy.textContent = isCustomerAuthenticated
            ? 'The wishlist panel is active and linked to your account. Use the button above to review, remove, or move items to cart.'
            : 'The wishlist panel is active. Your guest saves are available in this browser now, and they can sync after sign-in or account creation.';
        }
      };

      const queueWishlistOpen = () => {
        markWishlistReady();
        if (!wishlistOpenTrigger || wishlistPage.dataset.themeWishlistAutoOpened === 'true') return;
        wishlistPage.dataset.themeWishlistAutoOpened = 'true';
        window.setTimeout(() => {
          wishlistOpenTrigger.click();
        }, 350);
      };

      if (typeof wishlistProvider?.whenReady === 'function') {
        wishlistProvider.whenReady(queueWishlistOpen);
      } else {
        queueWishlistOpen();
      }

      wishlistPage.dataset.themeWishlistPageBound = 'true';
    }
  };

  const initPdpMediaGallery = (scope = document) => {
    const mediaGalleries = scope.querySelectorAll('.theme-main-product__media');
    mediaGalleries.forEach((gallery) => {
      if (gallery.dataset.themeMediaGalleryBound === 'true') return;

      const stageImage = gallery.querySelector('.theme-main-product__media-stage-image');
      const stageTrigger = gallery.querySelector('[data-theme-media-stage-trigger]');
      const stageImageClip = gallery.querySelector('[data-theme-media-image-clip]');
      const thumbs = Array.from(gallery.querySelectorAll('[data-theme-media-thumb]'));
      const stageBadge = gallery.querySelector('.theme-main-product__media-stage-badge');

      if (!stageImage) {
        gallery.dataset.themeMediaGalleryBound = 'true';
        return;
      }

      const readThumbMediaItem = (thumb, fallbackIndex) => {
        const idxFromData = Number.parseInt(thumb.getAttribute('data-theme-media-index') || '', 10);
        return {
          index: Number.isFinite(idxFromData) ? idxFromData : fallbackIndex,
          id: thumb.getAttribute('data-theme-media-id') || '',
          stageSrc: thumb.getAttribute('data-theme-media-src') || '',
          zoomSrc: thumb.getAttribute('data-theme-media-zoom-src') || thumb.getAttribute('data-theme-media-src') || '',
          alt: thumb.getAttribute('data-theme-media-alt') || stageImage.getAttribute('alt') || ''
        };
      };

      const mediaItemsFromThumbs = thumbs.map((thumb, index) => readThumbMediaItem(thumb, index));
      let catalog = null;
      try {
        catalog = JSON.parse(gallery.querySelector('[data-theme-media-catalog]')?.textContent || 'null');
      } catch (_error) {
        catalog = null;
      }
      const fallbackStageSrc = stageImage.getAttribute('src') || '';
      const fallbackStageZoomSrc = stageTrigger?.getAttribute('data-theme-media-stage-zoom-src') || fallbackStageSrc;
      const fallbackStageAlt = stageImage.getAttribute('alt') || '';
      const catalogImages = Array.isArray(catalog?.images) ? catalog.images : [];
      const mediaItems = catalogImages.length
        ? catalogImages.filter((item) => item?.src).map((item, index) => ({
          index, id: String(item.id || ''), stageSrc: item.src,
          zoomSrc: item.zoomSrc || item.src, alt: item.alt || fallbackStageAlt, type: item.type || 'image'
        }))
        : mediaItemsFromThumbs.length
        ? mediaItemsFromThumbs
        : [{ index: 0, stageSrc: fallbackStageSrc, zoomSrc: fallbackStageZoomSrc, alt: fallbackStageAlt }];

      const player = gallery.querySelector('[data-theme-media-player]');
      const clearPlayer = () => {
        player?.querySelectorAll('video').forEach((video) => video.pause());
        // Removing an external iframe stops its playback without relying on a vendor API.
        player?.replaceChildren();
        if (player) player.hidden = true;
      };
      const syncPlayer = (item) => {
        clearPlayer();
        const isImage = !item.type || item.type === 'image';
        if (stageTrigger) stageTrigger.hidden = !isImage;
        if (isImage || !player) return;
        const template = gallery.querySelector(`[data-theme-media-player-template="${item.id}"]`);
        if (template) { player.appendChild(template.content.cloneNode(true)); player.hidden = false; }
      };
      const adjacentIndex = (index, direction, imagesOnly = false) => {
        for (let step = 1; step <= mediaItems.length; step += 1) {
          const next = (index + direction * step + mediaItems.length) % mediaItems.length;
          if (!imagesOnly || !mediaItems[next].type || mediaItems[next].type === 'image') return next;
        }
        return index;
      };
      gallery.querySelector('[data-theme-media-inline-prev]')?.addEventListener('click', () => shiftIndex(-1));
      gallery.querySelector('[data-theme-media-inline-next]')?.addEventListener('click', () => shiftIndex(1));
      let activeIndex = 0;
      let stageLoadToken = 0;
      let lightboxOpen = false;
      let lightboxIndex = 0;
      let zoomLevel = 1;
      let lastFocusedElement = null;
      let stageGesture = null;
      let stageSlide = null;
      let stageSwipeGeneration = 0;
      let suppressStageClick = false;

      const lightbox = gallery.querySelector('[data-theme-media-lightbox]');
      const lightboxImage = lightbox?.querySelector('[data-theme-media-lightbox-image]') || null;
      const lightboxCount = lightbox?.querySelector('[data-theme-media-lightbox-count]') || null;
      lightboxCount?.setAttribute('aria-live', 'polite');
      lightboxCount?.setAttribute('aria-atomic', 'true');
      const lightboxViewport = lightbox?.querySelector('[data-theme-media-lightbox-viewport]') || null;
      const zoomInButton = lightbox?.querySelector('[data-theme-media-zoom-in]') || null;
      const zoomOutButton = lightbox?.querySelector('[data-theme-media-zoom-out]') || null;
      const zoomResetButton = lightbox?.querySelector('[data-theme-media-zoom-reset]') || null;
      const prevButton = lightbox?.querySelector('[data-theme-media-prev]') || null;
      const nextButton = lightbox?.querySelector('[data-theme-media-next]') || null;
      const closeButtons = lightbox ? Array.from(lightbox.querySelectorAll('[data-theme-media-lightbox-close]')) : [];
      const lightboxDialog = lightbox?.querySelector('[role="dialog"]') || null;
      const closeControl = closeButtons.find((button) => button.classList.contains('theme-main-product__lightbox-close'));
      if (closeControl) {
        closeControl.setAttribute('aria-label', 'Close image viewer');
        closeControl.textContent = '×';
      }
      let lightboxBackgroundState = [];

      // Portal the lightbox to <body> so fixed positioning and z-layer behavior
      // stay reliable above sticky shell/navigation and app overlays.
      if (lightbox && lightbox.parentElement !== document.body) {
        document.body.appendChild(lightbox);
        lightbox.dataset.themeMediaLightboxPortaled = 'true';
      }

      const clampIndex = (index) => {
        const max = mediaItems.length - 1;
        return Math.min(Math.max(index, 0), max);
      };

      const isolateLightboxBackground = () => {
        if (!lightbox || lightboxBackgroundState.length) return;

        lightboxBackgroundState = Array.from(document.body.children)
          .filter((element) => element !== lightbox && !['SCRIPT', 'STYLE', 'LINK'].includes(element.tagName))
          .map((element) => ({
            element,
            inert: 'inert' in element ? element.inert : null,
            ariaHidden: element.getAttribute('aria-hidden')
          }));

        lightboxBackgroundState.forEach(({ element }) => {
          if ('inert' in element) element.inert = true;
          element.setAttribute('aria-hidden', 'true');
        });
      };

      const restoreLightboxBackground = () => {
        lightboxBackgroundState.forEach(({ element, inert, ariaHidden }) => {
          if (inert !== null) element.inert = inert;
          if (ariaHidden === null) {
            element.removeAttribute('aria-hidden');
          } else {
            element.setAttribute('aria-hidden', ariaHidden);
          }
        });
        lightboxBackgroundState = [];
      };

      const isPointInsideRect = (x, y, rect) => {
        if (!rect) return false;
        return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
      };

      const imagePreloadCache = new Map();

      const preloadImage = (src) => {
        if (!src) return Promise.resolve(false);
        if (imagePreloadCache.has(src)) return imagePreloadCache.get(src);

        const preloadPromise = new Promise((resolve) => {
          const image = new Image();
          image.decoding = 'async';
          image.onload = () => {
            if (!image.naturalWidth) return resolve(false);
            if (typeof image.decode === 'function') {
              image.decode().then(() => resolve(true), () => resolve(false));
            } else {
              resolve(true);
            }
          };
          image.onerror = () => resolve(false);
          image.src = src;
        });

        imagePreloadCache.set(src, preloadPromise);
        preloadPromise.then((ready) => {
          if (!ready && imagePreloadCache.get(src) === preloadPromise) imagePreloadCache.delete(src);
        });
        return preloadPromise;
      };

      const syncThumbState = (item) => {
        thumbs.forEach((thumb, thumbIndex) => {
          const thumbMedia = mediaItemsFromThumbs[thumbIndex];
          const isActive = thumbMedia && (thumbMedia.id && item.id
            ? thumbMedia.id === item.id : thumbMedia.index === item.index);
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-pressed', isActive ? 'true' : 'false');
          if (isActive) {
            const strip = thumb.closest('.theme-main-product__media-strip');
            if (strip) strip.scrollTo({ left: Math.max(0, thumb.offsetLeft - strip.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2), top: Math.max(0, thumb.offsetTop - strip.offsetTop - (strip.clientHeight - thumb.offsetHeight) / 2), behavior: 'auto' });
          }
        });
      };

      const cancelStageSlide = () => {
        stageSwipeGeneration += 1;
        if (stageSlide?.timer) window.clearTimeout(stageSlide.timer);
        stageSlide?.neighbor.remove();
        stageImage.style.translate = '';
        stageImage.style.transition = '';
        gallery.classList.remove('is-stage-sliding');
        stageSlide = null;
        stageGesture = null;
      };

      const positionStageSlide = (offset) => {
        if (!stageSlide) return;
        const width = stageTrigger.clientWidth;
        const x = Math.max(-width, Math.min(width, offset));
        stageImage.style.translate = `${x}px 0`;
        stageSlide.neighbor.style.translate = `${x + stageSlide.direction * width}px 0`;
      };

      const prepareStageSlide = (gesture, direction) => {
        if (stageSlide?.direction === direction) return;
        if (stageSlide) {
          cancelStageSlide();
          stageGesture = gesture;
        }
        const nextIndex = (activeIndex + direction + mediaItems.length) % mediaItems.length;
        const item = mediaItems[nextIndex];
        if (!item || nextIndex === activeIndex) return;
        gesture.direction = direction;
        preloadImage(item.stageSrc).then((ready) => {
          if (!ready || stageGesture !== gesture || gesture.direction !== direction || stageSlide) return;
          const neighbor = stageImage.cloneNode(false);
          neighbor.removeAttribute('srcset');
          neighbor.removeAttribute('sizes');
          neighbor.removeAttribute('fetchpriority');
          neighbor.setAttribute('src', item.stageSrc);
          neighbor.setAttribute('alt', '');
          neighbor.setAttribute('aria-hidden', 'true');
          neighbor.classList.add('theme-main-product__media-stage-slide-image');
          neighbor.draggable = false;
          (stageImageClip || stageTrigger).appendChild(neighbor);
          stageSlide = { neighbor, direction, nextIndex, timer: null, settling: false };
          gallery.classList.add('is-stage-sliding');
          positionStageSlide(gesture.dx);
        });
      };

      const settleStageSlide = (commit) => {
        if (!stageSlide || stageSlide.settling) return;
        const slide = stageSlide;
        slide.settling = true;
        const finish = () => {
          if (stageSlide !== slide) return;
          const nextIndex = slide.nextIndex;
          cancelStageSlide();
          if (commit) setActiveByIndex(nextIndex);
        };
        if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
          finish();
          return;
        }
        const transition = 'translate 260ms cubic-bezier(0.22, 1, 0.36, 1)';
        stageImage.style.transition = transition;
        slide.neighbor.style.transition = transition;
        positionStageSlide(commit ? -slide.direction * stageTrigger.clientWidth : 0);
        slide.timer = window.setTimeout(finish, 280);
      };

      const viewer = lightboxViewport && lightboxImage ? new window.ThemeMediaViewer({
        viewport: lightboxViewport,
        image: lightboxImage,
        getAdjacent: (direction) => {
          if (mediaItems.length < 2) return null;
          const item = mediaItems[adjacentIndex(lightboxIndex, direction, true)];
          return { src: item.zoomSrc || item.stageSrc, previewSrc: item.stageSrc };
        },
        onNavigate: (direction, source) => shiftIndex(direction, source),
        onUpgrade: (source, previewSrc) => {
          if (lightboxOpen && lightboxImage.getAttribute('src') === previewSrc) lightboxImage.src = source;
        },
        onTap: (point) => {
          if (!lightboxOpen || zoomLevel > 1 || mediaItems.length < 2) return;
          const rect = lightboxViewport.getBoundingClientRect();
          const horizontalOffset = point.x - rect.left;
          if (horizontalOffset <= rect.width * 0.3) viewer?.navigate(-1);
          else if (horizontalOffset >= rect.width * 0.7) viewer?.navigate(1);
        },
        onZoomChange: (value) => {
          zoomLevel = value;
          if (zoomResetButton) zoomResetButton.textContent = `${Math.round(value * 100)}%`;
        }
      }) : null;
      const applyLightboxTransform = () => viewer?.refresh();

      const updateLightboxSizing = () => {
        if (!lightbox || !lightboxViewport) return;

        const viewportWidth = Math.max(window.innerWidth || 0, document.documentElement.clientWidth || 0);
        const viewportHeight = Math.max(window.innerHeight || 0, document.documentElement.clientHeight || 0);
        const isDesktop = viewportWidth >= 960;
        const naturalWidth = lightboxImage?.naturalWidth || 2000;
        const naturalHeight = lightboxImage?.naturalHeight || 2500;
        const imageAspect = naturalWidth > 0 && naturalHeight > 0 ? naturalWidth / naturalHeight : 0.8;
        const clampedAspect = Math.min(Math.max(imageAspect, 0.62), 1.45);

        const dialogHeightCap = Math.min(viewportHeight * 0.92, 900);
        const dialogStyle = lightboxDialog ? window.getComputedStyle(lightboxDialog) : null;
        const dialogVerticalPadding = dialogStyle
          ? (Number.parseFloat(dialogStyle.paddingTop) || 0) + (Number.parseFloat(dialogStyle.paddingBottom) || 0)
          : 0;
        const dialogRowGap = dialogStyle ? Number.parseFloat(dialogStyle.rowGap) || 0 : 0;
        const dialogControlsHeight = lightbox
          ? Array.from(lightbox.querySelectorAll(
            '.theme-main-product__lightbox-close, .theme-main-product__lightbox-toolbar, .theme-main-product__lightbox-nav'
          )).reduce((total, control) => total + control.getBoundingClientRect().height, 0)
          : 0;
        const viewportMaxHeight = Math.max(
          1,
          Math.min(
            viewportHeight * (isDesktop ? 0.7 : 0.64),
            dialogHeightCap - dialogVerticalPadding - dialogControlsHeight - (dialogRowGap * 3)
          )
        );

        const desktopWidthCap = Math.min(viewportWidth * 0.72, 860);
        const mobileWidthCap = viewportWidth * 0.92;
        const viewportWidthCap = isDesktop ? desktopWidthCap : mobileWidthCap;
        const portraitBiasAspect = isDesktop && clampedAspect < 1 ? Math.min(clampedAspect, 0.84) : clampedAspect;
        const computedViewportWidth = Math.min(viewportWidthCap, viewportMaxHeight * portraitBiasAspect);
        const dialogMaxWidth = Math.min(
          viewportWidth * 0.96,
          computedViewportWidth + (isDesktop ? 48 : 24)
        );

        lightbox.style.setProperty('--theme-lightbox-viewport-aspect', String(portraitBiasAspect));
        lightbox.style.setProperty('--theme-lightbox-viewport-width', `${Math.round(computedViewportWidth)}px`);
        lightbox.style.setProperty('--theme-lightbox-viewport-max-height', `${Math.round(viewportMaxHeight)}px`);
        lightbox.style.setProperty('--theme-lightbox-dialog-max-width', `${Math.round(dialogMaxWidth)}px`);
      };

      const resetLightboxTransform = () => viewer?.reset();
      const setZoomLevel = (requestedLevel) => viewer?.setZoom(requestedLevel);

      const setActiveByIndex = (nextIndex, lightboxSource = null) => {
        cancelStageSlide();
        const safeIndex = clampIndex(nextIndex);
        const item = mediaItems[safeIndex];
        if (!item) return;

        const nextToken = stageLoadToken + 1;
        stageLoadToken = nextToken;
        gallery.classList.add('is-loading');

        preloadImage(item.stageSrc)
          .then((ready) => {
            if (stageLoadToken !== nextToken) return;
            gallery.classList.remove('is-loading');
            if (!ready) return;

            activeIndex = safeIndex;
            lightboxIndex = safeIndex;
            syncThumbState(item);
            gallery.dispatchEvent(new CustomEvent('theme:media:source-change'));
            syncPlayer(item);
            const inlineCount = gallery.querySelector('[data-theme-media-inline-count]');
            if (inlineCount) inlineCount.textContent = `${safeIndex + 1} / ${mediaItems.length}`;
            if (lightboxOpen) renderLightbox(lightboxSource);

            if (item.stageSrc && stageImage.getAttribute('src') !== item.stageSrc) {
              stageImage.setAttribute('src', item.stageSrc);
              stageImage.removeAttribute('srcset');
            }

            if (item.alt) {
              stageImage.setAttribute('alt', item.alt);
            }

            if (stageTrigger && item.zoomSrc) {
              stageTrigger.setAttribute('data-theme-media-stage-zoom-src', item.zoomSrc);
            }

          });
      };

      const shiftIndex = (delta, lightboxSource = null) => {
        if (!mediaItems.length) return;
        const wrapped = adjacentIndex(lightboxOpen ? lightboxIndex : activeIndex, delta, lightboxOpen);
        setActiveByIndex(wrapped, lightboxSource);
      };

      const renderLightbox = (source = null) => {
        if (!lightbox || !lightboxImage) return;

        const item = mediaItems[lightboxIndex] || mediaItems[0];
        if (!item) return;

        lightboxImage.setAttribute('src', source || item.zoomSrc || item.stageSrc || '');
        lightboxImage.setAttribute('alt', item.alt || fallbackStageAlt);
        lightboxImage.setAttribute('draggable', 'false');
        if (lightboxCount) {
          const images = mediaItems.filter((media) => !media.type || media.type === 'image');
          lightboxCount.textContent = `${images.indexOf(item) + 1} of ${images.length}`;
        }
        if (prevButton) prevButton.disabled = mediaItems.length < 2;
        if (nextButton) nextButton.disabled = mediaItems.length < 2;
        updateLightboxSizing();
        viewer?.reset({ preserveNavigation: true });
        viewer?.prime();
      };

      const openLightbox = (index = activeIndex) => {
        if (!lightbox || !lightboxImage || (mediaItems[index]?.type && mediaItems[index].type !== 'image')) return;
        lightboxIndex = clampIndex(index);
        lightboxOpen = true;
        lastFocusedElement = document.activeElement;
        lightbox.hidden = false;
        lightbox.setAttribute('aria-hidden', 'false');
        document.body.classList.add('theme-lightbox-open');
        gallery.classList.add('is-lightbox-open');
        isolateLightboxBackground();
        renderLightbox();
        const closeButton = closeButtons.find((button) => button.classList.contains('theme-main-product__lightbox-close')) || closeButtons[0];
        if (typeof trapFocus === 'function') {
          trapFocus(lightbox, closeButton || lightboxDialog || lightbox);
        } else {
          markFocusFromPointerClick(closeButton || lightboxDialog || lightbox);
          (closeButton || lightboxDialog || lightbox).focus();
        }
      };

      const closeLightbox = () => {
        if (!lightbox) return;
        lightboxOpen = false;
        lightbox.hidden = true;
        lightbox.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('theme-lightbox-open');
        gallery.classList.remove('is-lightbox-open');
        resetLightboxTransform();
        restoreLightboxBackground();
        const returnFocus = lastFocusedElement?.isConnected ? lastFocusedElement : stageTrigger;
        if (typeof removeTrapFocus === 'function') {
          removeTrapFocus(returnFocus);
        } else if (returnFocus && typeof returnFocus.focus === 'function') {
          markFocusFromPointerClick(returnFocus);
          returnFocus.focus();
        }
      };

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !lightboxOpen) return;
        event.preventDefault();
        closeLightbox();
      });

      thumbs.forEach((thumb, thumbIndex) => {
        thumb.addEventListener('click', () => {
          const thumbMedia = mediaItemsFromThumbs[thumbIndex];
          if (!thumbMedia) return;
          const nextIndex = mediaItems.findIndex((item) => item.index === thumbMedia.index);
          setActiveByIndex(nextIndex >= 0 ? nextIndex : thumbIndex);
        });
      });

      const warmStageImages = () => {
        if (mediaItems.length < 2) return;
        [-1, 1].forEach((direction) => {
          preloadImage(mediaItems[(activeIndex + direction + mediaItems.length) % mediaItems.length].stageSrc);
        });
      };

      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(warmStageImages, { timeout: 1200 });
      } else {
        window.setTimeout(warmStageImages, 400);
      }

      if (stageTrigger) {
        stageTrigger.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'touch' || !window.matchMedia('(max-width: 47.99rem)').matches || mediaItems.length < 2 || gallery.classList.contains('is-loading')) return;
          if (stageGesture || stageSlide) {
            cancelStageSlide();
            suppressStageClick = true;
            return;
          }
          suppressStageClick = false;
          stageGesture = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, dx: 0, axis: null, direction: 0 };
        });

        stageTrigger.addEventListener('pointermove', (event) => {
          const gesture = stageGesture;
          if (!gesture || gesture.pointerId !== event.pointerId) return;
          const dx = event.clientX - gesture.startX;
          const dy = event.clientY - gesture.startY;
          if (!gesture.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 6) {
            gesture.axis = Math.abs(dx) > Math.abs(dy) * 1.25 ? 'horizontal' : 'vertical';
          }
          if (gesture.axis !== 'horizontal') return;
          gesture.dx = dx;
          suppressStageClick = true;
          const direction = dx < 0 ? 1 : -1;
          if (gesture.direction !== direction) prepareStageSlide(gesture, direction);
          positionStageSlide(dx);
        });

        stageTrigger.addEventListener('pointerup', (event) => {
          const gesture = stageGesture;
          if (!gesture || gesture.pointerId !== event.pointerId) return;
          if (gesture.axis === 'horizontal') {
            const dx = event.clientX - gesture.startX;
            const threshold = Math.max(48, stageTrigger.clientWidth * 0.18);
            if (stageSlide) settleStageSlide(Math.abs(dx) >= threshold);
            else {
              const direction = dx < 0 ? 1 : -1;
              const nextIndex = (activeIndex + direction + mediaItems.length) % mediaItems.length;
              cancelStageSlide();
              if (Math.abs(dx) >= threshold && nextIndex !== activeIndex) {
                const generation = stageSwipeGeneration;
                preloadImage(mediaItems[nextIndex].stageSrc).then((ready) => {
                  if (ready && generation === stageSwipeGeneration) setActiveByIndex(nextIndex);
                });
              }
            }
            suppressStageClick = true;
            window.setTimeout(() => { suppressStageClick = false; }, 400);
          } else {
            stageGesture = null;
          }
        });

        stageTrigger.addEventListener('pointercancel', cancelStageSlide);
        stageTrigger.addEventListener('keydown', cancelStageSlide);
        window.addEventListener('resize', cancelStageSlide);

        stageTrigger.addEventListener('click', (event) => {
          if (suppressStageClick) {
            event.preventDefault();
            suppressStageClick = false;
            return;
          }

          const isKeyboardClick = event.detail === 0;
          if (!isKeyboardClick && mediaItems.length > 1) {
            const badgeRect = stageBadge?.getBoundingClientRect?.() || null;
            if (isPointInsideRect(event.clientX, event.clientY, badgeRect)) {
              openLightbox(activeIndex);
              return;
            }

            const rect = stageTrigger.getBoundingClientRect();
            const horizontalOffset = event.clientX - rect.left;
            const width = rect.width || 1;

            if (horizontalOffset <= width * 0.18) {
              event.preventDefault();
              shiftIndex(-1);
              return;
            }

            if (horizontalOffset >= width * 0.82) {
              event.preventDefault();
              shiftIndex(1);
              return;
            }
          }

          openLightbox(activeIndex);
        });
      }

      closeButtons.forEach((button) => {
        button.addEventListener('click', () => {
          closeLightbox();
        });
      });

      prevButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        viewer?.navigate(-1);
      });

      nextButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        viewer?.navigate(1);
      });

      // Delegated lightbox action handling keeps controls interactive even if
      // nested overlays or dynamic DOM swaps interfere with direct listeners.
      lightbox?.addEventListener('click', (event) => {
        if (!lightboxOpen) return;

        const actionTarget = event.target.closest(
          '[data-theme-media-zoom-in], [data-theme-media-zoom-out], [data-theme-media-zoom-reset], [data-theme-media-prev], [data-theme-media-next]'
        );

        if (!actionTarget) return;

        event.preventDefault();
        event.stopPropagation();

        if (actionTarget.hasAttribute('data-theme-media-zoom-in')) {
          setZoomLevel(zoomLevel + 0.25);
          return;
        }

        if (actionTarget.hasAttribute('data-theme-media-zoom-out')) {
          setZoomLevel(zoomLevel - 0.25);
          return;
        }

        if (actionTarget.hasAttribute('data-theme-media-zoom-reset')) {
          resetLightboxTransform();
          return;
        }

        if (actionTarget.hasAttribute('data-theme-media-prev')) {
          viewer?.navigate(-1);
          return;
        }

        if (actionTarget.hasAttribute('data-theme-media-next')) {
          viewer?.navigate(1);
        }
      });

      zoomInButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        setZoomLevel(zoomLevel + 0.25);
      });

      zoomOutButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        setZoomLevel(zoomLevel - 0.25);
      });

      zoomResetButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        resetLightboxTransform();
      });

      lightboxViewport?.addEventListener('click', (event) => {
        if (!lightboxOpen || viewer?.consumeClick() || zoomLevel > 1 || mediaItems.length < 2) return;

        const rect = lightboxViewport.getBoundingClientRect();
        const horizontalOffset = event.clientX - rect.left;
        const width = rect.width || 1;

        if (horizontalOffset <= width * 0.3) {
          event.preventDefault();
          viewer?.navigate(-1);
          return;
        }

        if (horizontalOffset >= width * 0.7) {
          event.preventDefault();
          viewer?.navigate(1);
        }
      });

      lightboxImage?.addEventListener('dragstart', (event) => {
        event.preventDefault();
      });

      lightboxImage?.addEventListener('load', () => {
        if (!lightboxOpen) return;
        updateLightboxSizing();
        applyLightboxTransform();
      });

      window.addEventListener('resize', () => {
        if (!lightboxOpen) return;
        updateLightboxSizing();
        applyLightboxTransform();
      });

      document.addEventListener('keydown', (event) => {
        if (!lightboxOpen) return;

        if (event.key === 'Escape') {
          closeLightbox();
          return;
        }

        if (event.key === 'ArrowLeft') {
          viewer?.navigate(-1);
          return;
        }

        if (event.key === 'ArrowRight') {
          viewer?.navigate(1);
          return;
        }

        if (event.key === '+' || event.key === '=') {
          event.preventDefault();
          setZoomLevel(zoomLevel + 0.2);
          return;
        }

        if (event.key === '-') {
          event.preventDefault();
          setZoomLevel(zoomLevel - 0.2);
          return;
        }

        if (event.key === '0') {
          event.preventDefault();
          resetLightboxTransform();
        }
      });

      const variantInput = gallery.closest('.theme-main-product')?.querySelector('#theme-variant-select');
      const indexForVariant = (variantId) => {
        const mediaId = catalog?.variants?.[String(variantId || '')];
        return mediaId == null ? -1 : mediaItems.findIndex((item) => item.id === String(mediaId));
      };
      variantInput?.addEventListener('change', () => {
        const nextIndex = indexForVariant(variantInput.value);
        if (nextIndex >= 0 && nextIndex !== activeIndex) {
          setActiveByIndex(nextIndex);
        } else {
          stageLoadToken += 1;
          gallery.classList.remove('is-loading');
          cancelStageSlide();
        }
      });

      const initiallyActiveThumb = thumbs.find((thumb) => thumb.classList.contains('is-active')) || thumbs[0];
      const initialVariantIndex = indexForVariant(variantInput?.value);
      if (initialVariantIndex >= 0) {
        setActiveByIndex(initialVariantIndex);
      } else if (initiallyActiveThumb) {
        const initialThumbIndex = thumbs.indexOf(initiallyActiveThumb);
        const thumbMedia = mediaItemsFromThumbs[initialThumbIndex];
        const mappedIndex = thumbMedia ? mediaItems.findIndex((item) => item.index === thumbMedia.index) : initialThumbIndex;
        setActiveByIndex(mappedIndex >= 0 ? mappedIndex : 0);
      } else {
        setActiveByIndex(0);
      }

      gallery.dataset.themeMediaGalleryBound = 'true';
    });
  };

  const initPdpFitSizeToggle = (scope = document) => {
    // Read only the configured page's authored content. The storefront shell can
    // contain an earlier cart table, which must never become a measurement chart.
    const findSizeGuideTable = (doc) =>
      doc.querySelector('#MainContent .theme-main-page-router--size-chart .theme-main-page-router__content table') ||
      doc.querySelector('#MainContent .theme-main-page-router__content table') ||
      doc.querySelector('#MainContent .theme-main-page__content table');

    const buildPreviewAwareUrl = (rawValue) => {
      const fallbackUrl = new URL('/pages/size-chart', window.location.origin);

      try {
        const nextUrl = new URL(rawValue || fallbackUrl.toString(), window.location.origin);
        const currentUrl = new URL(window.location.href);
        const previewThemeId = currentUrl.searchParams.get('preview_theme_id');

        if (previewThemeId && !nextUrl.searchParams.has('preview_theme_id')) {
          nextUrl.searchParams.set('preview_theme_id', previewThemeId);
        }

        return nextUrl.toString();
      } catch (_error) {
        return fallbackUrl.toString();
      }
    };

    const polishEmbeddedSizeGuideTable = (table) => {
      if (!table) return null;

      table.querySelectorAll('meta, colgroup, col').forEach((node) => node.remove());
      table.removeAttribute('width');
      table.removeAttribute('height');
      table.classList.add('theme-size-chart-table');

      const rows = Array.from(table.querySelectorAll('tr')).filter((row) => row.children.length);
      if (!rows.length) return table;

      if (!table.querySelector('caption')) {
        const caption = document.createElement('caption');
        caption.textContent = 'Size Guide';
        table.prepend(caption);
      }
      table.querySelector('caption')?.removeAttribute('style');

      const firstRow = rows[0];
      if (firstRow.children.length === 5 &&
          !firstRow.children[0].textContent.trim() &&
          !firstRow.children[1].textContent.trim()) {
        firstRow.children[0].textContent = 'US';
        firstRow.children[1].textContent = 'Alpha';
      }
      Array.from(firstRow.children).forEach((cell) => {
        if (cell.tagName.toLowerCase() !== 'td') return;
        const th = document.createElement('th');
        th.innerHTML = cell.innerHTML;
        th.setAttribute('scope', 'col');
        cell.replaceWith(th);
      });

      rows.forEach((row, rowIndex) => {
        Array.from(row.children).forEach((cell) => {
          cell.removeAttribute('width');
          cell.removeAttribute('height');
          cell.style.removeProperty('width');
          cell.style.removeProperty('min-width');
          cell.style.removeProperty('max-width');
          cell.style.removeProperty('white-space');
          cell.classList.add('theme-richtext-table__cell', 'theme-size-chart-table__cell');
          if (cell.tagName.toLowerCase() === 'th') {
            if (!cell.hasAttribute('scope')) cell.setAttribute('scope', rowIndex === 0 ? 'col' : 'row');
            cell.classList.add('theme-richtext-table__cell--head', 'theme-size-chart-table__cell--head');
          }
        });
      });

      table.style.tableLayout = 'auto';
      table.style.width = '';
      return table;
    };

    const equalizeSizeGuideColumns = (table) => {
      const rows = Array.from(table.querySelectorAll('tr')).filter((row) => row.children.length);
      if (!rows.length) return;

      const columnCount = Math.max(...rows.map((row) =>
        Array.from(row.children).reduce((count, cell) => count + (cell.colSpan || 1), 0)
      ));
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      const fontSize = parseFloat(window.getComputedStyle(table).fontSize) || 16;
      const widestWord = Math.max(...rows.flatMap((row) =>
        Array.from(row.children).map((cell) => {
          const style = window.getComputedStyle(cell);
          const padding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
          const borders = parseFloat(style.borderLeftWidth) + parseFloat(style.borderRightWidth);
          const letterSpacing = parseFloat(style.letterSpacing) || 0;
          const words = cell.textContent.trim().split(/\s+/);
          context.font = style.font || (style.fontWeight + ' ' + style.fontSize + ' ' + style.fontFamily);
          const wordWidth = Math.max(...words.map((word) => {
            const renderedWord = style.textTransform === 'uppercase' ? word.toLocaleUpperCase() :
              style.textTransform === 'lowercase' ? word.toLocaleLowerCase() : word;
            return context.measureText(renderedWord).width +
              letterSpacing * Math.max(0, renderedWord.length - 1);
          }));
          return (wordWidth + padding + borders) / (cell.colSpan || 1);
        })
      ));
      const columnWidth = Math.ceil(Math.max(5.5 * fontSize, widestWord)) + 2;

      table.style.minWidth = String(columnWidth * columnCount) + 'px';
      table.style.width = '100%';
      table.style.tableLayout = 'fixed';
    };

    const sections = scope.querySelectorAll('.theme-pdp-fit-size');
    sections.forEach((section) => {
      if (section.dataset.themeFitSizeToggleBound === 'true') return;

      const trigger = section.querySelector('[data-theme-size-guide-toggle]');
      if (!trigger) return;

      const panelId = trigger.getAttribute('aria-controls');
      if (!panelId) return;

      const panel = section.querySelector(`#${panelId}`);
      if (!panel) return;

      const guideUrl = panel.dataset.themeSizeGuideUrl || '/pages/size-chart';
      const status = panel.querySelector('[data-theme-size-guide-status]');
      const content = panel.querySelector('[data-theme-size-guide-content]');
      const shell = panel.querySelector('[data-theme-size-guide-shell]');
      const closeButton = panel.querySelector('[data-theme-size-guide-close]');
      let loadPromise = null;

      const closeGuide = () => panel.close();

      panel.addEventListener('close', () => trigger.focus());
      panel.addEventListener('click', (event) => {
        if (event.target === panel) closeGuide();
      });
      closeButton?.addEventListener('click', closeGuide);

      const loadGuide = async () => {
        if (section.dataset.themeSizeGuideLoaded === 'true') return;
        if (loadPromise) return loadPromise;

        const requestUrl = buildPreviewAwareUrl(guideUrl);

        if (shell) {
          shell.dataset.themeState = 'loading';
        }

        if (status) {
          status.hidden = false;
          status.textContent = 'Loading the size chart...';
        }

        loadPromise = window
          .fetch(requestUrl, {
            credentials: 'same-origin',
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Size chart request failed: ${response.status}`);
            }
            return response.text();
          })
          .then((markup) => {
            const parser = new window.DOMParser();
            const doc = parser.parseFromString(markup, 'text/html');
            const sourceTable = findSizeGuideTable(doc);

            if (!sourceTable || !content) {
              throw new Error('Size chart table not found');
            }

            const polishedTable = polishEmbeddedSizeGuideTable(sourceTable.cloneNode(true));
            const wrapper = document.createElement('div');
            wrapper.className = 'theme-size-chart-table-wrap';
            wrapper.tabIndex = 0;
            wrapper.setAttribute('aria-label', 'Size guide table');
            wrapper.appendChild(polishedTable);

            const viewport = document.createElement('div');
            viewport.className = 'theme-pdp-fit-size__guide-table-viewport';
            viewport.dataset.themeScrollPosition = 'none';
            viewport.appendChild(wrapper);

            const scrollMeta = document.createElement('div');
            scrollMeta.className = 'theme-scroll-rail__meta theme-pdp-fit-size__guide-scroll-meta';
            scrollMeta.setAttribute('aria-hidden', 'true');
            scrollMeta.hidden = true;

            const scrollHint = document.createElement('span');
            scrollHint.className = 'theme-scroll-rail__hint';
            scrollHint.textContent = 'Swipe to view sizes';
            const progressTrack = document.createElement('span');
            progressTrack.className = 'theme-scroll-rail__progress-track';
            const progressBar = document.createElement('span');
            progressBar.className = 'theme-scroll-rail__progress-bar';
            progressTrack.appendChild(progressBar);
            scrollMeta.appendChild(scrollHint);
            scrollMeta.appendChild(progressTrack);

            const meaningfulScrollOverflow = 8;
            const updateScrollCue = () => {
              const maxScroll = wrapper.scrollWidth - wrapper.clientWidth;
              const position = maxScroll <= meaningfulScrollOverflow ? 'none' :
                wrapper.scrollLeft <= 1 ? 'start' :
                wrapper.scrollLeft >= maxScroll - 1 ? 'end' : 'middle';

              viewport.dataset.themeScrollPosition = position;
              content.dataset.themeScrollPosition = position;
              scrollMeta.hidden = position === 'none';
              const thumbScale = position === 'none'
                ? 1
                : Math.max(0.18, Math.min(0.48, wrapper.clientWidth / wrapper.scrollWidth));
              const scrollRatio = position === 'none'
                ? 0
                : Math.max(0, Math.min(1, wrapper.scrollLeft / maxScroll));
              progressBar.style.transform = 'translateX(' + ((1 - thumbScale) * scrollRatio * 100) + '%) scaleX(' + thumbScale + ')';
              wrapper.setAttribute('aria-label', position === 'none'
                ? 'Size guide table'
                : 'Size guide table. Scroll horizontally to see all columns');
            };

            wrapper.addEventListener('scroll', updateScrollCue, { passive: true });
            if (window.ResizeObserver) {
              const resizeObserver = new window.ResizeObserver(updateScrollCue);
              resizeObserver.observe(wrapper);
              resizeObserver.observe(polishedTable);
            }

            content.innerHTML = '';
            content.appendChild(viewport);
            content.appendChild(scrollMeta);
            content.hidden = false;
            equalizeSizeGuideColumns(polishedTable);
            updateScrollCue();

            if (status) {
              status.hidden = true;
            }

            if (shell) {
              shell.dataset.themeState = 'loaded';
            }

            section.dataset.themeSizeGuideLoaded = 'true';
          })
          .catch(() => {
            if (shell) {
              shell.dataset.themeState = 'error';
            }

            if (status) {
              status.hidden = false;
              status.textContent = 'The size guide could not load. Please try again.';
            }

            if (content) {
              content.hidden = true;
            }

          })
          .finally(() => {
            loadPromise = null;
          });

        return loadPromise;
      };

      trigger.addEventListener('click', () => {
        panel.showModal();
        closeButton?.focus();
        void loadGuide();
      });

      section.dataset.themeFitSizeToggleBound = 'true';
    });
  };

  const THEME_TAB_LAUNCH_PARAM = 'theme_tab_launch';
  const THEME_TAB_SOURCE_PARAM = 'theme_tab_source';
  const THEME_TAB_SOURCES = new Set(['pdp', 'cart', 'shopping']);
  const returnLinkState = new WeakMap();
  let returnLinksBound = false;
  let returnTabLaunch = null;

  const consumeReturnTabLaunch = () => {
    const url = new URL(window.location.href);
    const launchedAt = Number(url.searchParams.get(THEME_TAB_LAUNCH_PARAM));
    const source = url.searchParams.get(THEME_TAB_SOURCE_PARAM);
    const hadMetadata = [THEME_TAB_LAUNCH_PARAM, THEME_TAB_SOURCE_PARAM,
      'theme_opened_from', 'theme_return_to', 'theme_return_label'].some((key) => url.searchParams.has(key));
    if (!hadMetadata) return;
    [THEME_TAB_LAUNCH_PARAM, THEME_TAB_SOURCE_PARAM,
      'theme_opened_from', 'theme_return_to', 'theme_return_label'].forEach((key) => url.searchParams.delete(key));
    // Consume before deferred initialization, navigation, or reload can copy it.
    try {
      window.history.replaceState(window.history.state, '', url.toString());
    } catch (_error) {
      // Without consumption, do not create a helper that could recur on reload.
      return;
    }
    if (THEME_TAB_SOURCES.has(source) && launchedAt > 0 &&
      Date.now() - launchedAt >= 0 && Date.now() - launchedAt < 120000) {
      returnTabLaunch = { source };
    }
  };

  const initReturnLinks = (scope = document) => {
    const trustedOrigins = new Set([window.location.origin]);
    // The shared SEO snippet renders the merchant's canonical shop.url.
    try {
      const canonical = new URL(document.querySelector('link[rel="canonical"]')?.href);
      if (/^https?:$/.test(canonical.protocol)) trustedOrigins.add(canonical.origin);
    } catch (_error) { /* No merchant alias available. */ }

    const effectiveTarget = (link) => (link.getAttribute('target') ??
      document.querySelector('base[target]')?.getAttribute('target') ?? '').toLowerCase();
    const classify = (link, rawHref) => {
      if (!(link instanceof HTMLAnchorElement) || link.hasAttribute('download') || !rawHref) return null;
      try {
        const url = new URL(rawHref, document.baseURI);
        if (!/^https?:$/.test(url.protocol) || !trustedOrigins.has(url.origin) || url.username || url.password) return null;
        return url;
      } catch (_error) { return null; }
    };
    const prepare = (link, transient = false) => {
      if (!(link instanceof HTMLAnchorElement)) return;
      let state = returnLinkState.get(link);
      const rawHref = link.getAttribute('href');
      if (!state || rawHref !== state.prepared) state = { original: rawHref, prepared: rawHref };
      returnLinkState.set(link, state);
      const url = classify(link, state.original);
      const eligible = url && (transient || effectiveTarget(link) === '_blank');
      let nextHref = state.original;
      if (eligible) {
        const source = link.closest('.theme-main-product') ? 'pdp'
          : link.closest('cart-drawer, .theme-main-cart') ? 'cart' : 'shopping';
        url.searchParams.set(THEME_TAB_LAUNCH_PARAM, String(Date.now()));
        url.searchParams.set(THEME_TAB_SOURCE_PARAM, source);
        nextHref = url.toString();
        const rel = new Set((link.getAttribute('rel') || '').split(/\s+/).filter(Boolean));
        rel.add('noopener');
        rel.add('noreferrer');
        const nextRel = Array.from(rel).join(' ');
        if (link.getAttribute('rel') !== nextRel) link.setAttribute('rel', nextRel);
      }
      state.prepared = nextHref;
      if (nextHref !== rawHref) {
        if (nextHref === null) link.removeAttribute('href');
        else link.setAttribute('href', nextHref);
      }
    };
    const scan = (root) => {
      if (root.matches?.('a')) prepare(root);
      root.querySelectorAll?.('a[href]').forEach((link) => prepare(link));
    };
    scan(scope);
    if (returnLinksBound) return;
    returnLinksBound = true;
    consumeReturnTabLaunch();

    // Native default actions run after propagation. Observe at the window so
    // cancelled handlers on the link/document have already had their say.
    const onActivation = (event) => {
      const link = event.target?.closest?.('a[href]');
      if (!link || event.defaultPrevented) return;
      const target = effectiveTarget(link);
      const modified = (event.type === 'auxclick' && event.button === 1) ||
        (event.type === 'click' && event.button === 0 && (event.ctrlKey || event.metaKey || event.shiftKey));
      const newContext = target === '_blank' ||
        (modified && ['', '_self', '_top', '_parent'].includes(target));
      if (!newContext || event.altKey) return;
      prepare(link, modified);
      // Ordinary same-tab URLs are decorated only during a native new-tab
      // activation, then restored. No preventDefault or scripted window.open.
      if (target !== '_blank') window.setTimeout(() => prepare(link), 0);
    };
    window.addEventListener('click', onActivation);
    window.addEventListener('auxclick', onActivation);
    document.addEventListener('contextmenu', (event) => {
      const link = event.target?.closest?.('a[href]');
      if (link && !event.defaultPrevented) prepare(link);
    });
    if (typeof MutationObserver === 'function') {
      new MutationObserver((records) => {
        records.forEach((record) => {
          if (record.type === 'childList') record.addedNodes.forEach((node) => scan(node));
          else if (record.target.matches?.('base')) scan(document);
          else {
            const state = returnLinkState.get(record.target);
            // Ignore our own href writes; server reconciliation still changes
            // original href even when the same anchor object survives.
            if (record.attributeName !== 'href' || !state ||
              record.target.getAttribute('href') !== state.prepared) prepare(record.target);
          }
        });
      }).observe(document.documentElement, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['href', 'target', 'download']
      });
    }
    window.addEventListener('pagehide', () => {
      const bubble = document.querySelector('[data-theme-return-tab-bubble]');
      if (bubble) {
        bubble.hidden = true;
        bubble.setAttribute('aria-hidden', 'true');
        bubble.dataset.themeState = 'closed';
      }
      returnTabLaunch = null;
    });
  };

  const initReturnTabBubble = () => {
    if (document.body.dataset.themeReturnTabBubbleBound === 'true') return;

    const launch = returnTabLaunch;
    returnTabLaunch = null;
    if (!launch) {
      document.body.dataset.themeReturnTabBubbleBound = 'true';
      return;
    }

    const ensureBubble = () => {
      let bubble = document.querySelector('[data-theme-return-tab-bubble]');
      if (bubble) return bubble;

      bubble = document.createElement('aside');
      bubble.className = 'theme-return-tab-bubble';
      bubble.hidden = true;
      bubble.setAttribute('aria-hidden', 'true');
      bubble.setAttribute('aria-live', 'polite');
      bubble.setAttribute('data-theme-return-tab-bubble', '');
      bubble.innerHTML = `
        <div class="theme-return-tab-bubble__header">
          <p class="theme-return-tab-bubble__eyebrow">Still shopping?</p>
          <button type="button" class="theme-return-tab-bubble__dismiss" data-theme-return-tab-dismiss aria-label="Dismiss return message">
            <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-close" aria-hidden="true" focusable="false" fill="none" viewBox="0 0 18 17">
              <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor"></path>
            </svg>
          </button>
        </div>
        <h3>Your product page is still open in your other tab.</h3>
        <p class="theme-return-tab-bubble__body" data-theme-return-tab-copy>This page opened separately so your product page stays in place. Close this tab when you are done here.</p>
        <div class="theme-return-tab-bubble__actions">
          <button type="button" data-theme-return-tab-close>Close this tab</button>
        </div>
      `;
      document.body.appendChild(bubble);
      return bubble;
    };

    const bubble = ensureBubble();
    const closeButton = bubble.querySelector('[data-theme-return-tab-close]');
    const dismissButton = bubble.querySelector('[data-theme-return-tab-dismiss]');
    const title = bubble.querySelector('h3');
    const copy = bubble.querySelector('[data-theme-return-tab-copy]');

    if (!(closeButton instanceof HTMLButtonElement) || !(dismissButton instanceof HTMLButtonElement)) {
      document.body.dataset.themeReturnTabBubbleBound = 'true';
      return;
    }

    const closeBubble = () => {
      bubble.hidden = true;
      bubble.setAttribute('aria-hidden', 'true');
      bubble.dataset.themeState = 'closed';
    };

    const isProductReturn = launch.source === 'pdp';
    const previousPage = isProductReturn ? 'product page' : 'shopping page';

    if (title) {
      title.textContent = `Your ${previousPage} is still open in your other tab.`;
    }

    if (copy) {
      copy.textContent = `This page opened separately so your ${previousPage} stays in place. Close this tab when you are done here.`;
    }

    dismissButton.addEventListener('click', closeBubble);

    closeButton.addEventListener('click', () => {
      const openerWindow = window.opener && !window.opener.closed ? window.opener : null;

      if (openerWindow) {
        try {
          openerWindow.focus();
        } catch (error) {
          // Ignore focus failures and still attempt to close this tab.
        }
      }

      try { window.close(); } catch (_error) { /* Show the manual close fallback below. */ }
      window.setTimeout(() => {
        if (!window.closed) {
          if (copy) {
            copy.textContent = `This tab could not close automatically. Close it using your browser's tab controls to return to your other tab.`;
          }
        }
      }, 160);
    });

    bubble.hidden = false;
    bubble.setAttribute('aria-hidden', 'false');
    bubble.dataset.themeState = 'open';
    document.body.dataset.themeReturnTabBubbleBound = 'true';
  };

  const initPdpReviewPresentation = (scope = document) => {
    const reviewPresentation = getRuntimeAdapter().initPdpReviewPresentation;
    if (typeof reviewPresentation === 'function') {
      reviewPresentation(scope);
    }
  };

  const closeAllShellToggles = (exceptToggle = null, { restoreFocus = false } = {}) => {
    const openToggles = Array.from(document.querySelectorAll('.theme-site-header .theme-shell-toggle[open]'))
      .filter((toggle) => !exceptToggle || toggle !== exceptToggle);

    openToggles.forEach((toggle) => {
      if (exceptToggle && toggle === exceptToggle) return;
      if (toggle.hasAttribute('open')) {
        toggle.removeAttribute('open');
      }
    });

    if (restoreFocus) {
      const summary = openToggles[0]?.querySelector(':scope > summary');
      if (summary) {
        markFocusFromPointerClick(summary);
        summary.focus({ preventScroll: true });
      }
    }
  };

  const syncShellState = () => {
    const shell = document.querySelector('.theme-shell');
    const header = document.querySelector('.theme-site-header');
    if (!shell || !header) return;

    let state = 'default';
    if (header.querySelector('.theme-shell-toggle--menu[open]')) {
      state = 'menu-open';
    } else if (header.querySelector('.theme-shell-toggle--search[open]')) {
      state = 'search-open';
    } else if (header.querySelector('.theme-shell-toggle--account[open]')) {
      state = 'account-open';
    }

    shell.setAttribute('data-theme-shell-state', state);
  };

  const initMobileMenuDrillIn = (header) => {
    const drawer = header.querySelector('[data-theme-mobile-menu-drawer]');
    const root = drawer?.querySelector('[data-theme-mobile-menu-root]');
    if (!drawer || !root || drawer.dataset.themeMobileMenuBound === 'true') return;

    const panels = Array.from(drawer.querySelectorAll('[data-theme-mobile-menu-panel]'));
    const controls = Array.from(drawer.querySelectorAll('[data-theme-mobile-menu-open]'));
    const panelById = new Map(panels.map((panel) => [panel.id, panel]));
    const openerByPanel = new Map(
      controls
        .map((control) => [panelById.get(control.getAttribute('aria-controls')), control])
        .filter(([panel]) => panel)
    );
    const scrollPositions = new WeakMap();
    let activePane = root;

    const paneScrollTop = (pane) => (pane === root ? drawer.scrollTop : pane.scrollTop);
    const setPaneScrollTop = (pane, value) => {
      if (pane === root) {
        drawer.scrollTop = value;
      } else {
        pane.scrollTop = value;
      }
    };
    const setControlState = (activePanel = null) => {
      controls.forEach((control) => {
        control.setAttribute('aria-expanded', String(panelById.get(control.getAttribute('aria-controls')) === activePanel));
      });
    };
    const showPane = (nextPane) => {
      if (!nextPane) return;
      scrollPositions.set(activePane, paneScrollTop(activePane));
      if (activePane !== nextPane) activePane.hidden = true;
      panels.forEach((panel) => {
        if (panel !== nextPane) panel.hidden = true;
      });
      nextPane.hidden = false;
      activePane = nextPane;
      setPaneScrollTop(nextPane, scrollPositions.get(nextPane) || 0);
      setControlState(nextPane === root ? null : nextPane);
    };
    const returnToParent = () => {
      if (activePane === root) return false;
      const closingPanel = activePane;
      const parentPane = closingPanel.dataset.themeMobileMenuParent === 'root'
        ? root
        : panelById.get(closingPanel.dataset.themeMobileMenuParent);
      showPane(parentPane || root);
      markFocusFromPointerClick(openerByPanel.get(closingPanel));
      openerByPanel.get(closingPanel)?.focus({ preventScroll: true });
      return true;
    };
    const reset = () => {
      root.hidden = false;
      panels.forEach((panel) => {
        panel.hidden = true;
        panel.scrollTop = 0;
      });
      drawer.scrollTop = 0;
      activePane = root;
      setControlState();
    };
    const presentActiveRoute = () => {
      const activePanel = panels
        .filter((panel) => panel.querySelector('[aria-current="page"]'))
        .sort((left, right) => Number(right.dataset.themeMobileMenuLevel) - Number(left.dataset.themeMobileMenuLevel))[0];
      if (activePanel) {
        showPane(activePanel);
      } else {
        showPane(root);
      }
    };
    const focusActivePane = () => {
      if (activePane === root) {
        focusFirstInteractive(root);
        return;
      }
      const back = activePane.querySelector('[data-theme-mobile-menu-back]');
      markFocusFromPointerClick(back);
      back?.focus({ preventScroll: true });
    };

    controls.forEach((control) => {
      control.addEventListener('click', () => {
        const panel = panelById.get(control.getAttribute('aria-controls'));
        if (panel) showPane(panel);
      });
    });
    panels.forEach((panel) => {
      panel.querySelector('[data-theme-mobile-menu-back]')?.addEventListener('click', returnToParent);
    });
    header.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || activePane === root) return;
      event.preventDefault();
      event.stopPropagation();
      returnToParent();
    });

    drawer.__themeMobileMenu = { focusActivePane, presentActiveRoute, reset };
    drawer.dataset.themeMobileMenuBound = 'true';
    reset();
  };

  const deferMobileSearchFocus = (toggle) => {
    window.requestAnimationFrame(() => {
      if (!toggle?.open) return;

      const input = toggle.querySelector('#theme-shell-search-input-mobile');
      if (input && typeof input.focus === 'function') {
        input.focus({ preventScroll: true });
      }
    });
  };

  const initShellHeaderControls = (scope = document) => {
    const headers = scope.querySelectorAll('.theme-site-header');

    headers.forEach((header) => {
      if (header.dataset.themeShellToggleBound === 'true') return;

      initMobileMenuDrillIn(header);

      const toggles = Array.from(header.querySelectorAll('.theme-shell-toggle'));
      toggles.forEach((toggle) => {
        toggle.addEventListener('toggle', () => {
          if (toggle.open) {
            closeAllShellToggles(toggle);
            const mobileMenu = toggle.querySelector('[data-theme-mobile-menu-drawer]')?.__themeMobileMenu;
            if (mobileMenu) {
              mobileMenu.presentActiveRoute();
              mobileMenu.focusActivePane();
            } else if (toggle.matches('.theme-shell-toggle--search')) {
              deferMobileSearchFocus(toggle);
            } else {
              focusFirstInteractive(toggle.querySelector('.theme-shell-drawer'));
            }
          } else {
            toggle.querySelector('[data-theme-mobile-menu-drawer]')?.__themeMobileMenu?.reset();
          }

          syncShellState();
        });
      });

      header.dataset.themeShellToggleBound = 'true';
    });

    if (!isShellToggleGlobalBound) {
      document.addEventListener('click', (event) => {
        const header = document.querySelector('.theme-site-header');
        if (!header) return;
        if (header.contains(event.target)) return;

        closeAllShellToggles();
        syncShellState();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;

        closeAllShellToggles(null, { restoreFocus: true });
        syncShellState();
      });

      window.addEventListener('resize', () => {
        if (window.innerWidth >= 960) {
          closeAllShellToggles();
          syncShellState();
        }
      });

      isShellToggleGlobalBound = true;
    }

    syncShellState();
  };

  const initHeaderScrollVisibility = (scope = document) => {
    const scopedHeader =
      scope instanceof Element && scope.matches('.theme-site-header')
        ? scope
        : scope.querySelector?.('.theme-site-header');
    const header = scopedHeader || document.querySelector('.theme-site-header');

    if (!header) return;
    if (headerScrollVisibilityController?.header === header) return;

    headerScrollVisibilityController?.destroy();

    const mobileQuery = window.matchMedia('(max-width: 59.99rem)');
    const directionThreshold = 12;
    const topThreshold = 8;
    let previousScrollY = Math.max(0, window.scrollY || window.pageYOffset || 0);
    let scrollDirection = null;
    let directionalDistance = 0;
    let scrollFrame = null;

    const isEditorMode = () => Boolean(window.Shopify?.designMode);
    const isMobileScrollVisibilityEnabled = () => mobileQuery.matches && !isEditorMode();
    const isShellToggleOpen = () =>
      Boolean(
        header.querySelector(
          '.theme-shell-toggle--menu[open], .theme-shell-toggle--search[open], .theme-shell-toggle--account[open]'
        )
      );
    const hasFullscreenThemeOverlay = () =>
      Boolean(
        document.querySelector(
          [
            '.theme-quick-view[open]',
            '[data-theme-media-lightbox]:not([hidden])',
            '.theme-contact-popup[data-theme-state="open"]',
            '.theme-account-offer.is-open',
            'details.theme-collection-filter-bar__drawer[open]',
            'cart-drawer.drawer.active',
            '.cart-drawer.drawer.active',
            'modal-dialog[open]'
          ].join(', ')
        )
      );
    const resetTracking = (scrollY = Math.max(0, window.scrollY || window.pageYOffset || 0)) => {
      previousScrollY = scrollY;
      scrollDirection = null;
      directionalDistance = 0;
    };
    const setVisibility = (visibility) => {
      if (header.dataset.themeScrollVisibility === visibility) return false;
      header.dataset.themeScrollVisibility = visibility;
      resetTracking();
      return true;
    };
    const showAndSuspend = () => {
      setVisibility('shown');
      resetTracking();
    };
    const updateVisibility = () => {
      scrollFrame = null;

      if (!isMobileScrollVisibilityEnabled() || isShellToggleOpen() || hasFullscreenThemeOverlay()) {
        showAndSuspend();
        return;
      }

      const currentScrollY = Math.max(0, window.scrollY || window.pageYOffset || 0);
      const delta = currentScrollY - previousScrollY;

      if (currentScrollY <= topThreshold) {
        setVisibility('shown');
        resetTracking(currentScrollY);
        return;
      }

      if (delta === 0) return;

      const nextDirection = delta > 0 ? 'down' : 'up';
      directionalDistance =
        nextDirection === scrollDirection ? directionalDistance + Math.abs(delta) : Math.abs(delta);
      scrollDirection = nextDirection;
      previousScrollY = currentScrollY;

      if (directionalDistance < directionThreshold) return;

      if (nextDirection === 'down') {
        const hideAfter = header.getBoundingClientRect().height + topThreshold;
        if (currentScrollY > hideAfter) {
          setVisibility('hidden');
        }
        return;
      }

      setVisibility('shown');
    };
    const requestVisibilityUpdate = () => {
      if (scrollFrame !== null) return;
      scrollFrame = window.requestAnimationFrame(updateVisibility);
    };
    const handleToggle = () => {
      if (isShellToggleOpen()) {
        showAndSuspend();
      } else {
        resetTracking();
      }
    };
    const handleFocusIn = () => showAndSuspend();
    const handleMediaChange = () => {
      showAndSuspend();
      requestVisibilityUpdate();
    };
    const isFullscreenOverlayNode = (node) =>
      node instanceof Element &&
      (node.matches('cart-drawer.drawer, .cart-drawer.drawer, modal-dialog') ||
        node.querySelector('cart-drawer.drawer, .cart-drawer.drawer, modal-dialog'));
    const fullscreenOverlayObserver =
      typeof MutationObserver === 'function'
        ? new MutationObserver((records) => {
            const hasFullscreenOverlayMutation = records.some((record) => {
              if (record.type === 'attributes') return isFullscreenOverlayNode(record.target);
              return Array.from(record.addedNodes).some(isFullscreenOverlayNode);
            });

            if (hasFullscreenOverlayMutation) showAndSuspend();
          })
        : null;
    const toggles = Array.from(
      header.querySelectorAll(
        '.theme-shell-toggle--menu, .theme-shell-toggle--search, .theme-shell-toggle--account'
      )
    );

    window.addEventListener('scroll', requestVisibilityUpdate, { passive: true });
    header.addEventListener('focusin', handleFocusIn);
    toggles.forEach((toggle) => toggle.addEventListener('toggle', handleToggle));
    document.addEventListener('click', requestVisibilityUpdate);
    document.addEventListener('toggle', requestVisibilityUpdate, true);
    fullscreenOverlayObserver?.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class', 'open']
    });
    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', handleMediaChange);
    } else {
      mobileQuery.addListener(handleMediaChange);
    }

    setVisibility('shown');
    header.dataset.themeScrollVisibilityBound = 'true';
    headerScrollVisibilityController = {
      header,
      destroy() {
        window.removeEventListener('scroll', requestVisibilityUpdate);
        header.removeEventListener('focusin', handleFocusIn);
        toggles.forEach((toggle) => toggle.removeEventListener('toggle', handleToggle));
        document.removeEventListener('click', requestVisibilityUpdate);
        document.removeEventListener('toggle', requestVisibilityUpdate, true);
        fullscreenOverlayObserver?.disconnect();
        if (typeof mobileQuery.removeEventListener === 'function') {
          mobileQuery.removeEventListener('change', handleMediaChange);
        } else {
          mobileQuery.removeListener(handleMediaChange);
        }
        if (scrollFrame !== null) window.cancelAnimationFrame(scrollFrame);
        header.removeAttribute('data-theme-scroll-visibility-bound');
      }
    };
  };

  const initRichTextTablePolish = (scope = document) => {
    const containers = scope.querySelectorAll(
      '.theme-policy-content__page.rte, .theme-policy-content__body.rte, .theme-main-page-router__content.rte, .theme-generic-page__content.rte'
    );

    const cleanLabel = (value, fallback) => {
      const normalized = String(value || '')
        .replace(/\s+/g, ' ')
        .replace(/\u00a0/g, ' ')
        .trim();
      return normalized || fallback;
    };

    const escapeHtml = (value) =>
      String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const normalizeCellStyle = (cell) => {
      if (!cell || !cell.getAttribute) return;
      const raw = cell.getAttribute('style');
      if (!raw) return;

      const keep = raw
        .split(';')
        .map((part) => part.trim())
        .filter(Boolean)
        .filter((part) => {
          const key = part.split(':')[0]?.trim().toLowerCase();
          if (!key) return false;
          return ![
            'width',
            'height',
            'min-width',
            'max-width',
            'min-height',
            'max-height',
            'padding',
            'margin'
          ].includes(key);
        });

      if (keep.length) {
        cell.setAttribute('style', keep.join('; ') + ';');
      } else {
        cell.removeAttribute('style');
      }
    };

    const normalizeTextNodes = (root) => {
      if (!root || !document.createTreeWalker) return;
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
      const nodes = [];
      let current = walker.nextNode();
      while (current) {
        nodes.push(current);
        current = walker.nextNode();
      }

      nodes.forEach((node) => {
        const nextValue = String(node.nodeValue || '')
          .replace(/\u00a0/g, ' ')
          .replace(/[ \t]{2,}/g, ' ')
          .replace(/\s+\n/g, '\n')
          .replace(/\n\s+/g, '\n');
        node.nodeValue = nextValue;
      });
    };

    const cleanupCellContent = (cell) => {
      if (!cell) return;

      cell.querySelectorAll('div, span, p, strong, em').forEach((node) => {
        if (node.querySelector('img, picture, video, iframe, table')) return;
        const text = cleanLabel(node.textContent, '');
        if (!text) {
          node.remove();
        }
      });

      cell.querySelectorAll('br').forEach((lineBreak) => {
        const next = lineBreak.nextSibling;
        if (next && next.nodeType === Node.TEXT_NODE) {
          next.nodeValue = String(next.nodeValue || '').replace(/^\s+/, '');
        }
      });

      normalizeTextNodes(cell);
    };

    const wrapTable = (table, className) => {
      if (!table || !className) return null;
      const parent = table.parentElement;
      if (parent && parent.classList.contains(className)) return parent;

      const wrapper = document.createElement('div');
      wrapper.className = className;
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
      return wrapper;
    };

    const polishSizeChartTable = (table) => {
      const pageHandle = table.closest('[data-theme-page-handle]')?.dataset.themePageHandle || '';
      if (pageHandle !== 'size-chart') return false;

      const rows = Array.from(table.querySelectorAll('tr')).filter((row) => row.children.length);
      if (!rows.length) return true;

      const headerLabels = ['US', 'Alpha', 'Bust', 'Waist', 'Hips'];
      table.classList.add('theme-size-chart-table');
      wrapTable(table, 'theme-size-chart-table-wrap');

      rows.forEach((row, rowIndex) => {
        const cells = Array.from(row.children);
        cells.forEach((cell, colIndex) => {
          const normalized = cleanLabel(cell.textContent, '');
          const colLabel = headerLabels[colIndex] || `Column ${colIndex + 1}`;

          cell.classList.add('theme-richtext-table__cell', 'theme-size-chart-table__cell');
          cell.classList.remove('theme-richtext-table__cell--price');
          cell.dataset.themeColLabel = colLabel;

          if (rowIndex === 0) {
            cell.classList.add('theme-richtext-table__cell--head', 'theme-size-chart-table__cell--head');
            cell.textContent = colLabel;
            return;
          }

          cell.classList.remove('theme-richtext-table__cell--head');
          cell.textContent = normalized;

          if (colIndex === 1) {
            cell.classList.add('theme-size-chart-table__cell--alpha');
          }

          if (colIndex === 0) {
            cell.classList.add('theme-size-chart-table__cell--numeric');
          }

          if (colIndex >= 2) {
            cell.classList.add('theme-size-chart-table__cell--measurement');
          }
        });
      });

      table.style.tableLayout = 'auto';
      table.style.width = '';
      return true;
    };

    containers.forEach((container) => {
      container.querySelectorAll('table').forEach((table) => {
        if (table.dataset.themeTablePolished === 'true') return;

        table.dataset.themeTablePolished = 'true';
        table.classList.add('theme-richtext-table');
        table.removeAttribute('width');
        table.removeAttribute('height');
        normalizeCellStyle(table);

        table.querySelectorAll('meta, colgroup, col').forEach((node) => node.remove());
        table.querySelectorAll('tr, th, td').forEach((node) => {
          node.removeAttribute('width');
          node.removeAttribute('height');
          normalizeCellStyle(node);
          cleanupCellContent(node);
        });

        table.querySelectorAll('p').forEach((paragraph) => {
          if (paragraph.querySelector('img, picture, video, iframe, table')) return;
          const text = cleanLabel(paragraph.textContent, '');
          const semanticText = text.replace(/[\s\u00a0\-\–\—\|\_]/g, '');
          if (!text || !/[A-Za-z0-9$]/.test(semanticText)) {
            paragraph.remove();
            return;
          }
          paragraph.textContent = text;
        });

        const firstRow = table.querySelector('tr');
        if (firstRow && !table.querySelector('th')) {
          Array.from(firstRow.children).forEach((cell) => {
            if (cell.tagName.toLowerCase() !== 'td') return;
            const th = document.createElement('th');
            th.innerHTML = cell.innerHTML;
            cell.replaceWith(th);
          });
        }

        if (polishSizeChartTable(table)) {
          return;
        }

        const rows = Array.from(table.querySelectorAll('tr'));
        if (!rows.length) return;

        const headerCells = Array.from(rows[0].children);
        const labels = headerCells.map((cell, index) => {
          const fallback = index === 1 ? 'Cost' : `Column ${index + 1}`;
          return cleanLabel(cell.textContent, fallback);
        });

        rows.forEach((row, rowIndex) => {
          const cells = Array.from(row.children);
          cells.forEach((cell, colIndex) => {
            cell.classList.add('theme-richtext-table__cell');
            cell.dataset.themeColLabel = labels[colIndex] || `Column ${colIndex + 1}`;

            if (colIndex === 1 || /cost|price|amount/i.test(cell.dataset.themeColLabel || '')) {
              cell.classList.add('theme-richtext-table__cell--price');
            }

            if (rowIndex === 0) {
              cell.classList.add('theme-richtext-table__cell--head');
              cell.textContent = labels[colIndex] || cleanLabel(cell.textContent, '');
              return;
            }

            if (colIndex === 1 || cell.classList.contains('theme-richtext-table__cell--price')) {
              const amountMatch = cleanLabel(cell.textContent, '').match(/\$\s?\d[\d,]*(?:\.\d{2})?/);
              if (amountMatch && amountMatch[0]) {
                cell.textContent = amountMatch[0].replace(/\s+/g, '');
              } else {
                cell.textContent = cleanLabel(cell.textContent, '').replace(/[^\d$.,-]/g, ' ').replace(/\s+/g, ' ').trim();
              }
            } else {
              const normalized = cleanLabel(cell.textContent, '').replace(/\s{2,}/g, ' ');
              const fallbackPrimary = normalized;
              let primary = normalized;
              let detail = '';

              const parentheticalMatch = normalized.match(/^(.*?)\s*\((.+)\)$/);
              if (parentheticalMatch) {
                primary = cleanLabel(parentheticalMatch[1], fallbackPrimary);
                detail = cleanLabel(parentheticalMatch[2], '');
              } else {
                const freeShippingMatch = normalized.match(/^(.*?)(free\s+standard\s+shipping.+)$/i);
                if (freeShippingMatch) {
                  primary = cleanLabel(freeShippingMatch[1], fallbackPrimary);
                  detail = cleanLabel(freeShippingMatch[2], '');
                }
              }

              const safePrimary = escapeHtml(primary);
              const safeDetail = escapeHtml(detail);
              cell.innerHTML = safeDetail
                ? `<span class="theme-richtext-table__service">${safePrimary}</span><span class="theme-richtext-table__detail">${safeDetail}</span>`
                : `<span class="theme-richtext-table__service">${safePrimary}</span>`;
            }
          });
        });

        if (labels.length === 2) {
          table.style.tableLayout = 'fixed';
          table.style.width = '100%';
        }
      });
    });
  };

  const initContactSupportPopup = (scope = document) => {
    const popup = scope.querySelector('[data-theme-contact-popup]') || document.querySelector('[data-theme-contact-popup]');
    if (!popup || popup.dataset.themeContactPopupBound === 'true') return;

    const panel = popup.querySelector('[data-theme-contact-popup-panel]');
    const closeButtons = Array.from(popup.querySelectorAll('[data-theme-contact-popup-close]'));
    const nameField = popup.querySelector('input[name="contact[name]"]');
    const shell = document.querySelector('.theme-shell');
    const configuredContactPath = getRuntimeAdapter().contactRoutes?.popupPath;
    const contactPath = typeof configuredContactPath === 'string' && configuredContactPath.startsWith('/')
      ? configuredContactPath
      : '/search';
    let lastFocusedElement = null;

    const normalizePath = (value) => {
      if (!value) return '/';
      const normalized = value.replace(/\/+$/, '');
      return normalized || '/';
    };

    const isContactPath = (value) => {
      const normalized = normalizePath(value);
      return normalized === contactPath || normalized.endsWith(contactPath);
    };

    const currentPath = () => normalizePath(window.location.pathname || '/');

    const forceClosed = () => {
      popup.hidden = true;
      popup.setAttribute('hidden', '');
      popup.setAttribute('aria-hidden', 'true');
      popup.dataset.themeState = 'closed';
      popup.style.display = 'none';
      document.body.classList.remove('theme-contact-popup-open');
      if (shell) {
        shell.setAttribute('data-theme-contact-popup-state', 'closed');
      }
    };

    const setPopupState = (isOpen) => {
      if (isOpen) {
        popup.hidden = false;
        popup.removeAttribute('hidden');
        popup.setAttribute('aria-hidden', 'false');
        popup.dataset.themeState = 'open';
        popup.style.display = 'grid';
        document.body.classList.add('theme-contact-popup-open');

        if (shell) {
          shell.setAttribute('data-theme-contact-popup-state', 'open');
        }

        lastFocusedElement = document.activeElement;
        window.requestAnimationFrame(() => {
          if (typeof nameField?.focus === 'function') {
            nameField.focus();
          } else {
            focusFirstInteractive(panel);
          }
        });
        return;
      }

      forceClosed();
      if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
        markFocusFromPointerClick(lastFocusedElement);
        lastFocusedElement.focus();
      }
    };

    const openPopup = () => {
      closeAllShellToggles();
      syncShellState();
      setPopupState(true);
    };

    const closePopup = () => {
      setPopupState(false);
      if (window.location.hash === '#contact-support') {
        window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
      }
    };

    // Force deterministic closed state on load, regardless of cache/cascade.
    forceClosed();

    window.addEventListener('pageshow', () => {
      forceClosed();
    });

    closeButtons.forEach((button) => {
      button.addEventListener('click', closePopup);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && popup.dataset.themeState === 'open') {
        closePopup();
      }
    });

    const isPopupTriggerHash = (hash) => {
      const normalized = (hash || '').toLowerCase();
      return normalized === '' || normalized === '#theme-contact-form' || normalized === '#contact-support';
    };

    const shouldInterceptLink = (link) => {
      if (!link) return false;
      if (link.hasAttribute('download')) return false;
      if (link.target && link.target !== '_self') return false;
      let url;
      try {
        url = new URL(link.getAttribute('href'), window.location.origin);
      } catch (_error) {
        return false;
      }

      const targetPath = normalizePath(url.pathname);
      const isContactLink = isContactPath(targetPath);
      const isContactAnchor = isContactLink && isPopupTriggerHash(url.hash);
      if (!isContactAnchor) return false;
      if (isContactPath(currentPath())) return false;
      return true;
    };

    const bindContactTrigger = (link) => {
      if (!link || link.dataset.themeContactTriggerBound === 'true') return;

      link.addEventListener('click', (event) => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        if (!shouldInterceptLink(link)) return;
        event.preventDefault();
        event.stopPropagation();
        openPopup();
      });

      link.dataset.themeContactTriggerBound = 'true';
    };

    const bindAllContactTriggers = (root = document) => {
      const links = Array.from(root.querySelectorAll('a[href]'));
      links.forEach((link) => {
        if (shouldInterceptLink(link)) {
          bindContactTrigger(link);
        }
      });
    };

    bindAllContactTriggers(document);

    const triggerObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches('a[href]')) {
            if (shouldInterceptLink(node)) bindContactTrigger(node);
            return;
          }
          bindAllContactTriggers(node);
        });
      });
    });
    triggerObserver.observe(document.body, { childList: true, subtree: true });

    popup.dataset.themeContactPopupBound = 'true';
  };

  const initShellAnnouncementCopy = () => {};

  const initContactFormAnchorRouting = (scope = document) => {
    const formWrap = scope.querySelector('#theme-contact-form');
    if (!formWrap) return;
    if (formWrap.dataset.themeContactAnchorBound === 'true') return;
    const configuredContactPaths = getRuntimeAdapter().contactRoutes?.anchorPaths;
    const contactPaths = Array.isArray(configuredContactPaths) && configuredContactPaths.length
      ? configuredContactPaths
      : ['/search'];

    const focusSupportNameField = () => {
      const nameField = formWrap.querySelector('input[name="contact[name]"]');
      if (nameField && typeof nameField.focus === 'function') {
        nameField.focus({ preventScroll: true });
      }
    };

    const scrollToSupportForm = (focusField = false) => {
      formWrap.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
      if (focusField) {
        setTimeout(focusSupportNameField, 320);
      }
    };

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link) return;
      const href = link.getAttribute('href') || '';
      if (!href.includes('#theme-contact-form')) return;

      const isSamePageHash =
        href === '#theme-contact-form' ||
        contactPaths.some((path) => href.endsWith(`${path}#theme-contact-form`) || href.endsWith(`${path}/#theme-contact-form`));

      if (!isSamePageHash && !href.startsWith(window.location.pathname + '#')) {
        return;
      }

      event.preventDefault();
      history.replaceState(null, '', `${window.location.pathname}#theme-contact-form`);
      scrollToSupportForm(true);
    });

    if (window.location.hash === '#theme-contact-form') {
      requestAnimationFrame(() => {
        scrollToSupportForm(true);
      });
    }

    formWrap.dataset.themeContactAnchorBound = 'true';
  };

  const initContactBotGuard = (scope = document) => {
    const forms = scope.querySelectorAll('form');
    if (!forms.length) return;

    forms.forEach((form) => {
      const guardRoot = form.querySelector('[data-theme-contact-guard-root]');
      if (!guardRoot || form.dataset.themeContactGuardBound === 'true') return;

      const startedAtInput = guardRoot.querySelector('[data-theme-contact-guard-started-at]');
      const elapsedInput = guardRoot.querySelector('[data-theme-contact-guard-elapsed]');
      const interactionInput = guardRoot.querySelector('[data-theme-contact-guard-interaction]');
      const resultInput = guardRoot.querySelector('[data-theme-contact-guard-result]');
      const sourcePathInput = guardRoot.querySelector('[data-theme-contact-guard-path]');
      const honeypotInput = guardRoot.querySelector('[data-theme-contact-guard-honeypot]');
      const questionNode = guardRoot.querySelector('[data-theme-contact-guard-question]');
      const answerInput = guardRoot.querySelector('[data-theme-contact-guard-answer]');
      const humanCheckbox = guardRoot.querySelector('[data-theme-contact-guard-human]');
      const errorNode = guardRoot.querySelector('[data-theme-contact-guard-error]');
      const submitControl = form.querySelector('button[type="submit"], input[type="submit"]');

      const startedAt = Date.now();
      let interactionCount = 0;
      let expectedAnswer = null;

      if (startedAtInput) {
        startedAtInput.value = String(startedAt);
      }

      if (sourcePathInput) {
        sourcePathInput.value = window.location.pathname || sourcePathInput.value || '/';
      }

      if (questionNode && answerInput) {
        const left = Math.floor(Math.random() * 7) + 3;
        const right = Math.floor(Math.random() * 7) + 2;
        expectedAnswer = left + right;
        questionNode.textContent = `What is ${left} + ${right}?`;
      }

      const setError = (message) => {
        if (!errorNode) return;
        errorNode.textContent = message;
        errorNode.hidden = false;
      };

      const clearError = () => {
        if (!errorNode) return;
        errorNode.textContent = '';
        errorNode.hidden = true;
      };

      const markInteraction = () => {
        interactionCount += 1;
        if (interactionInput && interactionCount > 0) {
          interactionInput.value = 'true';
        }
      };

      form.addEventListener('pointerdown', markInteraction, { passive: true });
      form.addEventListener('keydown', markInteraction);
      form.addEventListener('input', markInteraction, { passive: true });

      form.addEventListener('submit', (event) => {
        clearError();

        const now = Date.now();
        const elapsedMs = Math.max(0, now - startedAt);

        if (elapsedInput) {
          elapsedInput.value = String(elapsedMs);
        }

        if (honeypotInput && honeypotInput.value.trim() !== '') {
          event.preventDefault();
          if (resultInput) resultInput.value = 'blocked_honeypot';
          setError('Submission blocked. Please refresh the page and try again.');
          return;
        }

        if (elapsedMs < 3200) {
          event.preventDefault();
          if (resultInput) resultInput.value = 'blocked_too_fast';
          setError('Please wait a moment, then submit again.');
          return;
        }

        if (interactionCount < 2) {
          event.preventDefault();
          if (resultInput) resultInput.value = 'blocked_no_interaction';
          setError('Please complete the form fields before submitting.');
          return;
        }

        if (answerInput && expectedAnswer !== null) {
          const actual = parseInt(answerInput.value, 10);
          if (!Number.isFinite(actual) || actual !== expectedAnswer) {
            event.preventDefault();
            if (resultInput) resultInput.value = 'blocked_bad_challenge';
            setError('Security check answer is incorrect. Please try again.');
            answerInput.focus();
            return;
          }
        }

        if (humanCheckbox && !humanCheckbox.checked) {
          event.preventDefault();
          if (resultInput) resultInput.value = 'blocked_unchecked_human';
          setError('Please confirm that you are a real person.');
          humanCheckbox.focus();
          return;
        }

        if (submitControl) {
          if (submitControl.dataset.themeSubmitting === 'true') {
            event.preventDefault();
            return;
          }
          submitControl.dataset.themeSubmitting = 'true';
          submitControl.setAttribute('disabled', 'disabled');
          window.setTimeout(() => {
            submitControl.dataset.themeSubmitting = 'false';
            submitControl.removeAttribute('disabled');
          }, 8000);
        }

        if (resultInput) resultInput.value = 'pass';
      });

      form.dataset.themeContactGuardBound = 'true';
    });
  };

  const initGlobalContactInlineRouting = () => {
    if (document.body.dataset.themeContactInlineRoutingBound === 'true') return;

    const normalizePath = (value) => {
      if (!value) return '/';
      const normalized = value.replace(/\/+$/, '');
      return normalized || '/';
    };

    const configuredContactRoutes = getRuntimeAdapter().contactRoutes || {};
    const primaryContactPath =
      typeof configuredContactRoutes.primaryPath === 'string' && configuredContactRoutes.primaryPath.startsWith('/')
        ? configuredContactRoutes.primaryPath
        : '/search';
    const legacyContactPaths = Array.isArray(configuredContactRoutes.legacyPaths)
      ? configuredContactRoutes.legacyPaths.filter((path) => typeof path === 'string' && path.startsWith('/'))
      : ['/search'];
    const isContactPath = (value) => {
      const normalized = normalizePath(value);
      return (
        normalized === primaryContactPath ||
        normalized.endsWith(primaryContactPath) ||
        legacyContactPaths.some((path) => normalized === path || normalized.endsWith(path))
      );
    };

    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (!link) return;
      if (link.hasAttribute('download')) return;
      if (link.target && link.target !== '_self') return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;

      let url;
      try {
        url = new URL(link.getAttribute('href'), window.location.origin);
      } catch (_error) {
        return;
      }

      if (url.origin !== window.location.origin) return;
      if (!isContactPath(url.pathname)) return;

      const currentPath = normalizePath(window.location.pathname || '/');
      if (isContactPath(currentPath)) return;

      const hash = (url.hash || '').toLowerCase();
      if (hash && hash !== '#theme-contact-form') return;

      event.preventDefault();
      window.location.assign(`${primaryContactPath}#theme-contact-form`);
    }, true);

    document.body.dataset.themeContactInlineRoutingBound = 'true';
  };

  const initFreeShippingPresentation = () => {
    if (!document.body || document.body.dataset.themeFreeShippingPresentationBound === 'true') return;

    const shellSelector = "[class*='free_shipping_card_wrapper_']";
    const titleSelector = "[class*='free_shipping_card_title_']";
    const progressSelector = "[class*='free_shipping_card_progress_']";
    const productsSelector = "[class*='free_shipping_products_wrapper_']";
    const productSelector = "[class*='free_shipping_card_product_']";
    const imageSelector = "[class*='free_shipping_card_product_image_']";
    const metaSelector = "[class*='free_shipping_card_product_meta_']";
    const priceWrapSelector = "[class*='free_shipping_card_product_prices_wrapper_']";
    const ctaSelector = "[class*='free_shipping_card_product_cta_']";

    const decorateShell = (shell) => {
      if (!shell || shell.dataset.themeFreeShippingPresented === 'true') return;

      const isDrawer =
        shell.className.indexOf('placement_side_cart') > -1 ||
        Boolean(shell.closest('.order-value-booster-side-cart, .side-cart, cart-drawer, .drawer'));
      const title = shell.querySelector(titleSelector);
      const progress = shell.querySelector(progressSelector);
      const products = shell.querySelector(productsSelector);

      shell.classList.add('theme-free-shipping-shell');
      shell.classList.toggle('theme-free-shipping-shell--drawer', isDrawer);
      shell.classList.toggle('theme-free-shipping-shell--page', !isDrawer);

      let summary = shell.querySelector('.theme-free-shipping-shell__summary');
      if (!summary) {
        summary = document.createElement('div');
        summary.className = 'theme-free-shipping-shell__summary';
        shell.prepend(summary);
      }

      if (title && title.parentElement !== summary) {
        summary.appendChild(title);
      }

      if (progress && progress.parentElement !== summary) {
        summary.appendChild(progress);
      }

      if (products) {
        products.classList.add('theme-free-shipping-shell__products');
        if (products.parentElement !== shell) {
          shell.appendChild(products);
        }
      }

      shell.querySelectorAll(productSelector).forEach((productCard) => {
        productCard.classList.add('theme-free-shipping-shell__product');

        const image = productCard.querySelector(imageSelector);
        const meta = productCard.querySelector(metaSelector);
        const prices = productCard.querySelector(priceWrapSelector);
        const cta = productCard.querySelector(ctaSelector);

        if (image) image.classList.add('theme-free-shipping-shell__image');
        if (meta) meta.classList.add('theme-free-shipping-shell__meta');
        if (prices) prices.classList.add('theme-free-shipping-shell__prices');
        if (cta) cta.classList.add('theme-free-shipping-shell__cta');
      });

      shell.dataset.themeFreeShippingPresented = 'true';
    };

    const decorateAll = () => {
      document.querySelectorAll(shellSelector).forEach((shell) => decorateShell(shell));
    };

    decorateAll();

    const observer = new MutationObserver(() => {
      decorateAll();
    });

    observer.observe(document.body, { childList: true, subtree: true });
    document.body.dataset.themeFreeShippingPresentationBound = 'true';
  };

  const initNativeFreeShipping = () => {
    if (!document.body || document.body.dataset.themeNativeFreeShippingBound === 'true') return;
    if (document.body.dataset.themeFreeShippingPresentation === 'threshold_reveal') return;

    if (document.body.dataset.themeFreeShippingEnabled !== 'true') {
      document.querySelectorAll('[data-theme-free-shipping-mount]').forEach((mount) => {
        mount.hidden = true;
        mount.innerHTML = '';
      });
      document.body.dataset.themeNativeFreeShippingBound = 'true';
      return;
    }

    const getConfig = () => {
      const body = document.body;
      const parsedThresholdCents = Number.parseInt(body.dataset.themeFreeShippingThresholdCents || '', 10);
      return {
        thresholdCents: Number.isFinite(parsedThresholdCents) ? Math.max(0, parsedThresholdCents) : 0,
        heading: body.dataset.themeFreeShippingHeading || 'Free shipping unlock',
        intro: body.dataset.themeFreeShippingIntro || 'Free standard shipping on orders over {threshold}.',
        progressCopy: body.dataset.themeFreeShippingProgressCopy || 'You’re {remaining} away from free shipping.',
        completeCopy: body.dataset.themeFreeShippingCompleteCopy || 'You’ve unlocked free standard shipping.'
      };
    };

    const formatMoney = (cents, currency) => {
      const amount = Math.max(0, cents) / 100;
      const language = document.documentElement.lang || 'en-US';

      try {
        return new Intl.NumberFormat(language, {
          style: 'currency',
          currency: currency || 'USD',
          maximumFractionDigits: 2
        }).format(amount);
      } catch (_error) {
        return `$${amount.toFixed(2)}`;
      }
    };

    const resolveCopyTokens = (copy, thresholdText, remainingText) => String(copy)
      .replace(/\{threshold\}/g, thresholdText)
      .replace(/\{remaining\}/g, remainingText);

    const resolveMounts = () => {
      const mounts = Array.from(document.querySelectorAll('[data-theme-free-shipping-mount]'));
      const drawerFooter = document.querySelector('cart-drawer .drawer__footer');

      if (drawerFooter) {
        let drawerMount = drawerFooter.querySelector('[data-theme-free-shipping-mount][data-theme-free-shipping-context="drawer"]');
        if (!drawerMount) {
          drawerMount = document.createElement('div');
          drawerMount.className = 'theme-cart-drawer__free-shipping';
          drawerMount.dataset.themeFreeShippingMount = '';
          drawerMount.dataset.themeFreeShippingContext = 'drawer';
          drawerFooter.insertBefore(drawerMount, drawerFooter.firstChild);
        }

        mounts.push(drawerMount);
      }

      return mounts;
    };

    const renderCard = (mount, cart) => {
      if (!mount) return;

      const config = getConfig();
      const threshold = config.thresholdCents;
      const total = Math.max(0, parseInt(cart.total_price || 0, 10));
      const remaining = Math.max(0, threshold - total);
      const progress = threshold > 0 ? Math.min(100, Math.round((total / threshold) * 100)) : 100;
      const thresholdText = formatMoney(threshold, cart.currency);
      const remainingText = formatMoney(remaining, cart.currency);
      const isUnlocked = total >= threshold && cart.item_count > 0;
      const message = isUnlocked
        ? config.completeCopy
        : (cart.item_count > 0 ? config.progressCopy : config.intro);
      const resolvedHeading = resolveCopyTokens(config.heading, thresholdText, remainingText);
      const resolvedMessage = resolveCopyTokens(message, thresholdText, remainingText);

      mount.innerHTML = `
        <section class="theme-free-shipping-native theme-free-shipping-native--${mount.dataset.themeFreeShippingContext || 'page'}" aria-label="${resolvedHeading}">
          <div class="theme-free-shipping-native__header">
            <p class="theme-free-shipping-native__eyebrow">${resolvedHeading}</p>
            <p class="theme-free-shipping-native__message">${resolvedMessage}</p>
          </div>
          <div class="theme-free-shipping-native__progress" aria-hidden="true">
            <span class="theme-free-shipping-native__progress-track">
              <span class="theme-free-shipping-native__progress-bar" style="transform: scaleX(${Math.max(0.08, progress / 100)});"></span>
            </span>
            <span class="theme-free-shipping-native__progress-meta">${isUnlocked ? 'Unlocked' : `${progress}%`}</span>
          </div>
        </section>
      `;
      mount.hidden = false;
    };

    let refreshTimer = null;
    const refresh = () => {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(async () => {
        const mounts = resolveMounts();
        if (!mounts.length) return;

        try {
          const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' }
          });
          if (!response.ok) return;
          const cart = await response.json();
          mounts.forEach((mount) => renderCard(mount, cart));
        } catch (_error) {
          // Intentionally silent.
        }
      }, 80);
    };

    const observer = new MutationObserver((mutations) => {
      const shouldRefresh = mutations.some((mutation) => {
        return Array.from(mutation.addedNodes).some((node) => {
          if (!(node instanceof HTMLElement)) return false;
          return (
            node.matches?.('cart-drawer, cart-drawer-items, .drawer__footer, #MainContent') ||
            node.querySelector?.('cart-drawer, cart-drawer-items, .drawer__footer, [data-theme-free-shipping-mount]')
          );
        });
      });

      if (shouldRefresh) refresh();
    });

    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('theme:cart:render', refresh);
    document.addEventListener('submit', refresh, true);
    document.addEventListener('click', refresh, true);
    document.body.dataset.themeNativeFreeShippingBound = 'true';
    refresh();
  };

  const initPdpCartState = () => {
    const buttons = Array.from(document.querySelectorAll('[data-theme-pdp-atc]'));
    if (!buttons.length) return;
    const cartState = window.__themePdpCartState || {
      lineItemsByVariantId: new Map(),
      refreshPromise: null
    };
    window.__themePdpCartState = cartState;

    const setButtonState = (button, variantId) => {
      if (!button) return;
      const labelNode = button.querySelector('[data-theme-pdp-atc-label]') || button.querySelector('.theme-main-product__atc-label');
      if (!labelNode) return;

      const defaultLabel = button.dataset.themeDefaultLabel || 'Add to cart';
      const inCartLabel = button.dataset.themeInCartLabel || 'Already in cart';
      const soldOutLabel = button.dataset.themeSoldOutLabel || 'Sold Out';
      const isSoldOut = button.dataset.themeSoldOut === 'true';
      const isInCart = variantId && cartState.lineItemsByVariantId.has(String(variantId));

      if (isSoldOut) {
        labelNode.textContent = soldOutLabel;
        button.dataset.themeInCart = 'false';
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        return;
      }

      labelNode.textContent = isInCart ? inCartLabel : defaultLabel;
      button.dataset.themeInCart = isInCart ? 'true' : 'false';
      button.disabled = false;
      if (!button.classList.contains('loading')) button.setAttribute('aria-disabled', 'false');
    };

    const syncAllButtons = () => {
      buttons.forEach((button) => {
        const form = button.closest('product-form');
        const variantSelect = form?.querySelector('select[name="id"], input[name="id"]');
        const selectedVariantId = variantSelect?.value || button.dataset.themeSelectedVariantId;
        setButtonState(button, selectedVariantId);
      });
    };

    const fetchCartState = async () => {
      if (cartState.refreshPromise) return cartState.refreshPromise;

      cartState.refreshPromise = fetch(`${window.routes.cart_url}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then((response) => (response.ok ? response.json() : null))
        .then((cart) => {
          cartState.lineItemsByVariantId.clear();

          if (cart && Array.isArray(cart.items)) {
            cart.items.forEach((item) => {
              if (!item || item.variant_id == null) return;
              cartState.lineItemsByVariantId.set(String(item.variant_id), item.quantity || 0);
            });
          }

          syncAllButtons();
        })
        .catch(() => {
          // Intentionally silent: button fallback label remains usable.
        })
        .finally(() => {
          cartState.refreshPromise = null;
        });

      return cartState.refreshPromise;
    };

    buttons.forEach((button) => {
      if (button.dataset.themeCartStateBound === 'true') return;

      const form = button.closest('product-form');
      const variantSelect = form?.querySelector('select[name="id"], input[name="id"]');

      if (variantSelect) {
        variantSelect.addEventListener('change', (event) => {
          button.dataset.themeSelectedVariantId = event.currentTarget.value;
          syncAllButtons();
        });
      }

      button.dataset.themeCartStateBound = 'true';
    });

    if (document.body.dataset.themePdpCartStateBound !== 'true') {
      document.addEventListener('theme:cart:add', () => {
        fetchCartState();
      });

      document.body.dataset.themePdpCartStateBound = 'true';
      fetchCartState();
    } else {
      syncAllButtons();
    }
  };

  const initHorizontalRails = (scope = document) => {
    const rails = [];
    if (scope instanceof Element && scope.matches('[data-theme-horizontal-rail]')) {
      rails.push(scope);
    }
    rails.push(...scope.querySelectorAll('[data-theme-horizontal-rail]'));
    if (!rails.length) return;

    rails.forEach((root) => {
      if (root.dataset.themeHorizontalRailBound === 'true') return;

      const track = root.querySelector('[data-theme-horizontal-rail-track]');
      const prevButton = root.querySelector('[data-theme-horizontal-rail-prev]');
      const nextButton = root.querySelector('[data-theme-horizontal-rail-next]');
      const progress = root.querySelector('[data-theme-horizontal-rail-progress]');

      if (!track || !prevButton || !nextButton || !progress) return;

      const getScrollAmount = () => {
        const firstCard = track.firstElementChild;
        const styles = window.getComputedStyle(track);
        const gap = parseFloat(styles.columnGap || styles.gap || '0') || 0;
        const cardWidth = firstCard ? firstCard.getBoundingClientRect().width : 0;
        const viewportRatio = window.innerWidth >= 960 ? 0.78 : 0.9;
        return Math.max(cardWidth + gap, track.clientWidth * viewportRatio, 220);
      };

      const updateRailState = () => {
        const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
        const overflow = maxScrollLeft > 8;
        const scrollLeft = Math.max(0, Math.min(track.scrollLeft, maxScrollLeft));
        const progressRatio = overflow ? Math.min(1, scrollLeft / maxScrollLeft) : 0;
        const thumbScale = overflow
          ? Math.max(0.18, Math.min(0.48, track.clientWidth / Math.max(track.scrollWidth, 1)))
          : 1;
        const travel = 1 - thumbScale;

        root.dataset.themeHorizontalRailOverflow = overflow ? 'true' : 'false';
        root.dataset.themeCanScrollLeft = overflow && scrollLeft > 6 ? 'true' : 'false';
        root.dataset.themeCanScrollRight = overflow && scrollLeft < maxScrollLeft - 6 ? 'true' : 'false';

        progress.style.transform = `translateX(${travel * progressRatio * 100}%) scaleX(${thumbScale})`;
      };

      const scrollRailTo = (targetScrollLeft) => {
        const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
        const scrollLeft = Math.max(0, Math.min(track.scrollLeft, maxScrollLeft));
        const nextScrollLeft = Math.max(0, Math.min(maxScrollLeft, targetScrollLeft));

        if (nextScrollLeft === scrollLeft) return false;

        track.scrollBy({
          left: nextScrollLeft - scrollLeft,
          behavior: getDefaultA11yState().motion === 'reduce' || document.body.dataset.themeA11yMotion === 'reduce' ? 'instant' : 'smooth'
        });

        return true;
      };

      const scrollRail = (direction) => scrollRailTo(track.scrollLeft + (getScrollAmount() * direction));

      const handleTrackKeydown = (event) => {
        if (event.target !== track) return;

        let didScroll = false;
        if (event.key === 'ArrowLeft') {
          didScroll = scrollRail(-1);
        } else if (event.key === 'ArrowRight') {
          didScroll = scrollRail(1);
        } else if (event.key === 'Home') {
          didScroll = scrollRailTo(0);
        } else if (event.key === 'End') {
          didScroll = scrollRailTo(track.scrollWidth - track.clientWidth);
        }

        if (didScroll) event.preventDefault();
      };

      prevButton.addEventListener('click', () => scrollRail(-1));
      nextButton.addEventListener('click', () => scrollRail(1));
      track.addEventListener('keydown', handleTrackKeydown);
      track.addEventListener('scroll', updateRailState, { passive: true });
      window.addEventListener('resize', updateRailState, { passive: true });

      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(() => updateRailState());
        observer.observe(track);
        root.themeHorizontalRailObserver = observer;
      }

      root.themeHorizontalRailCleanup = () => {
        window.removeEventListener('resize', updateRailState);
        root.themeHorizontalRailObserver?.disconnect();
      };
      root.dataset.themeHorizontalRailBound = 'true';
      updateRailState();
    });
  };

  const cartRecommendationBindings = new WeakMap();
  const cartRecommendationRequests = new Map();

  // Read the independent cart-row span and subtract module chrome. Fit the
  // shared vertical card to that real block budget; intrinsic content may grow.
  const bindCartRecommendationHeight = (owner, section) => {
    let frame = 0;
    let observedRows = [];
    let disposed = false;
    const measure = () => {
      frame = 0;
      if (disposed || !owner.isConnected || !section.isConnected) return;
      const rows = [...owner.querySelectorAll('[data-theme-item-key]')].filter((row) => row.getBoundingClientRect().height > 0).slice(0, 2);
      if (!rows.length || section.getBoundingClientRect().width <= 0) return;
      const first = rows[0].getBoundingClientRect();
      const last = rows[1]?.getBoundingClientRect();
      const rowGap = parseFloat(getComputedStyle(rows[0].parentElement).rowGap) || 0;
      const budget = last ? last.bottom - first.top : first.height * 2 + rowGap;
      const track = section.querySelector('[data-theme-horizontal-rail-track]');
      const cards = [...section.querySelectorAll('[data-theme-product-card]')];
      if (!track || !cards.length) return;
      const style = getComputedStyle(section);
      const margins = (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
      const cardHeight = Math.max(...cards.map((card) => card.getBoundingClientRect().height));
      const overhead = section.getBoundingClientRect().height + margins - cardHeight;
      const availableBlockSize = Math.max(0, budget - overhead);
      const trackStyle = getComputedStyle(track);
      const availableInlineSize = (track.clientWidth - (parseFloat(trackStyle.paddingLeft) || 0) -
        (parseFloat(trackStyle.paddingRight) || 0)) * 0.96;
      // Each variant owns its preferred whole-card ratio and legible width
      // floor. A mixed rail uses the most restrictive target and widest floor.
      // The height remains intrinsic: no clipping, altered media ratio or scroll.
      const inputs = cards.map((card) => {
        const cardStyle = getComputedStyle(card);
        return {
          ratio: parseFloat(cardStyle.getPropertyValue('--theme-product-card-preferred-ratio')),
          minimum: parseFloat(cardStyle.getPropertyValue('--theme-product-card-min-inline-size'))
        };
      });
      const preferred = Math.min(...inputs.map(({ ratio }) => availableBlockSize * ratio));
      const minimum = Math.max(...inputs.map(({ minimum }) => minimum));
      const width = Math.min(availableInlineSize, Math.max(minimum, preferred));
      const current = parseFloat(section.style.getPropertyValue('--theme-cart-recommendation-inline-size'));
      if (!Number.isFinite(current) || Math.abs(current - width) > 0.5) {
        section.style.setProperty('--theme-cart-recommendation-inline-size', `${width}px`);
      }
    };
    const schedule = () => {
      if (!disposed && !frame) frame = window.requestAnimationFrame(measure);
    };
    const resizeObserver = typeof window.ResizeObserver === 'function' ? new window.ResizeObserver(schedule) : null;
    const refresh = () => {
      observedRows.forEach((row) => resizeObserver?.unobserve(row));
      observedRows = [...owner.querySelectorAll('[data-theme-item-key]')].slice(0, 2);
      observedRows.forEach((row) => resizeObserver?.observe(row, { box: 'border-box' }));
      schedule();
    };
    resizeObserver?.observe(section);
    if (!resizeObserver) window.addEventListener('resize', schedule);
    refresh();
    return { refresh, cleanup: () => {
      disposed = true;
      resizeObserver?.disconnect();
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
    } };
  };

  const initPdpRecommendations = (scope = document) => {
    const recommendationSections = [];
    if (scope instanceof Element && scope.matches('.theme-main-product__recommendations[data-theme-recommendation-product-id]')) {
      recommendationSections.push(scope);
    }
    recommendationSections.push(...scope.querySelectorAll('.theme-main-product__recommendations[data-theme-recommendation-product-id]'));
    const complementaryTemplates = scope.querySelectorAll('template[data-theme-complementary-recommendations-template]');
    const cartTemplates = scope.querySelectorAll('template[data-theme-cart-complementary-template]');
    if (!recommendationSections.length && !complementaryTemplates.length && !cartTemplates.length) return;

    const recommendationRoot = window.Shopify?.routes?.root || '/';
    const fetchRecommendationFragment = ({ productId, limit, intent, recommendationSectionId }) => fetch(
      `${recommendationRoot}recommendations/products?section_id=${encodeURIComponent(recommendationSectionId)}&product_id=${encodeURIComponent(productId)}&limit=${encodeURIComponent(limit)}&intent=${encodeURIComponent(intent)}`,
      {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' }
      }
    )
      .then((response) => (response.ok ? response.text() : ''))
      .then((markup) => {
        if (!markup) return null;

        const html = new DOMParser().parseFromString(markup, 'text/html');
        const fragment = html.querySelector('[data-theme-recommendation-fragment]');
        const itemCount = Number.parseInt(fragment?.dataset?.themeEventItemsCount || '', 10) || 0;

        if (!fragment || fragment.dataset.themeRecommendationIntent !== intent || itemCount < 1 || !fragment.children.length) {
          return null;
        }

        return fragment;
      });

    const syncRecommendationAnalytics = (section, fragment) => {
      const track = section.querySelector('[data-theme-horizontal-rail-track]');
      const railCta = section.querySelector('.theme-home-discovery-grid__rail-cta');
      const listId = section.dataset.themeEventListId || fragment.dataset.themeRecommendationListId || '';
      const listName = section.dataset.themeEventListName || fragment.dataset.themeRecommendationListName || '';
      const rawItems = String(fragment.dataset.themeEventItemsJson || '[]');
      let parsedItems = [];

      try {
        parsedItems = JSON.parse(rawItems);
      } catch (_error) {
        parsedItems = [];
      }

      const normalizedItems = Array.isArray(parsedItems)
        ? parsedItems.map((item, index) => ({
            ...item,
            item_category: listName,
            item_list_id: listId,
            item_list_name: listName,
            index: index + 1
          }))
        : [];

      section.dataset.themeModule = listId;
      section.dataset.themeEventListId = listId;
      section.dataset.themeEventListName = listName;
      section.dataset.themeEventItemsCount = String(
        Number.parseInt(fragment.dataset.themeEventItemsCount || '', 10) || normalizedItems.length
      );
      section.dataset.themeEventItemsJson = JSON.stringify(normalizedItems);

      track.querySelectorAll('[data-theme-product-card]').forEach((card, index) => {
        const position = String(index + 1);
        const syncAnalyticsTarget = (target) => {
          target.dataset.themeEventItemCategory = listName;
          target.dataset.themeEventListId = listId;
          target.dataset.themeEventListName = listName;
          target.dataset.themeEventPositionIndex = position;
        };

        syncAnalyticsTarget(card);
        card.querySelectorAll('[data-theme-product-link], [data-theme-quick-view-trigger]').forEach(syncAnalyticsTarget);
      });

      if (railCta) {
        railCta.dataset.themeEventListId = listId;
        railCta.dataset.themeEventListName = listName;
        railCta.dataset.themeEventCtaTarget = railCta.getAttribute('href') || '';
      }
    };

    const applyRecommendationFragment = (section, fragment) => {
      const track = section.querySelector('[data-theme-horizontal-rail-track]');
      if (!track) return false;

      track.innerHTML = fragment.innerHTML;
      syncRecommendationAnalytics(section, fragment);
      initWishlistButtons(track);
      track.scrollLeft = 0;
      track.dispatchEvent(new Event('scroll'));
      window.dispatchEvent(new Event('resize'));
      return true;
    };

    const dispatchImpression = (section, source) => {
      section.dataset.themeEventLoadDispatched = 'true';
      window.__themeDispatchFromElement?.(section, source);
    };

    cartTemplates.forEach((template) => {
      if (template.dataset.themeRecommendationBound === 'true') return;
      template.dataset.themeRecommendationBound = 'true';
      const productIds = [...new Set((template.dataset.themeCartProductIds || '').split(',').filter((id) => /^\d+$/.test(id)))];
      if (!productIds.length) return;
      const limit = Math.min(10, Math.max(1, Number(template.dataset.themeRecommendationLimit) || 6));
      const recommendationSectionId = 'theme-pdp-complementary-recommendations';
      const cartProductIds = new Set(productIds);
      const cacheKey = JSON.stringify([window.location.host, recommendationRoot, template.dataset.themeRecommendationCurrency,
        template.dataset.themeRecommendationThemeId, limit, [...productIds].sort()]);
      const storageKey = 'theme_cart_complementary_v1';
      let active = true;
      let renderedSection;
      let heightBinding;
      const invalidate = () => {
        active = false;
        heightBinding?.cleanup();
        renderedSection?.querySelector('[data-theme-horizontal-rail]')?.themeHorizontalRailCleanup?.();
        renderedSection?.remove();
        try { window.sessionStorage.removeItem(storageKey); } catch (_storageError) { /* Optional cache. */ }
      };
      const owner = template.closest('cart-drawer, .theme-main-cart');
      const ownerId = template.dataset.themeRecommendationOwnerId || 'theme-cart-complementary';
      const isDrawer = template.dataset.themeRecommendationSurface === 'drawer';
      cartRecommendationBindings.get(owner)?.cleanup();
      const onAdd = (event) => {
        const response = event.detail?.response;
        const addedItems = Array.isArray(response?.items) ? response.items : response?.product_id ? [response] : [];
        if (!addedItems.length || addedItems.some((item) => !cartProductIds.has(String(item.product_id)))) invalidate();
      };
      document.addEventListener('theme:cart:add', onAdd);
      const drawer = document.querySelector('cart-drawer');
      let observer;
      if (!isDrawer && drawer && typeof MutationObserver !== 'undefined') {
        observer = new MutationObserver(() => {
          const confirmedIds = new Set([...drawer.querySelectorAll('[data-theme-cart-product-id]')].map((row) => row.dataset.themeCartProductId));
          if (confirmedIds.size === cartProductIds.size && [...confirmedIds].every((id) => cartProductIds.has(id))) return;
          invalidate();
          observer.disconnect();
        });
        observer.observe(drawer, { attributes: true, attributeFilter: ['data-cart-count'] });
      }
      cartRecommendationBindings.set(owner, { template, isActive: () => active, refreshHeight: () => heightBinding?.refresh(), cleanup: () => {
        active = false;
        heightBinding?.cleanup();
        renderedSection?.querySelector('[data-theme-horizontal-rail]')?.themeHorizontalRailCleanup?.();
        renderedSection?.remove();
        observer?.disconnect();
        document.removeEventListener('theme:cart:add', onAdd);
      } });


      const renderAggregate = (fragments) => {
        if (!active || !template.isConnected) return;
        const aggregate = document.createElement('div');
        const items = [];
        const seen = new Set(cartProductIds);
        for (const fragment of fragments) {
          if (!fragment) continue;
          let metadata;
          try { metadata = JSON.parse(fragment.dataset.themeEventItemsJson || '[]'); } catch (_error) { continue; }
          if (!Array.isArray(metadata)) continue;
          for (const card of fragment.querySelectorAll('[data-theme-product-card]')) {
            const productId = card.dataset.themeProductId;
            const item = metadata.find((entry) => String(entry.item_id) === productId);
            if (!item || !productId || seen.has(productId) || card.dataset.themeProductAvailable !== 'true') continue;
            seen.add(productId);
            const clonedCard = card.cloneNode(true);
            const idMap = new Map();
            clonedCard.querySelectorAll('[id]').forEach((element) => {
              const previousId = element.id;
              element.id = `${ownerId}-${productId}-${previousId}`;
              idMap.set(previousId, element.id);
            });
            clonedCard.querySelectorAll('[aria-labelledby], [aria-describedby], [aria-controls], [for]').forEach((element) => {
              for (const attribute of ['aria-labelledby', 'aria-describedby', 'aria-controls', 'for']) {
                if (!element.hasAttribute(attribute)) continue;
                element.setAttribute(attribute, element.getAttribute(attribute).split(/\s+/).map((id) => idMap.get(id) || id).join(' '));
              }
            });
            clonedCard.querySelectorAll('[data-theme-event-template-name]').forEach((element) => {
              element.dataset.themeEventTemplateName = 'cart';
              element.dataset.themeEventTemplateType = 'core_commerce';
              if (element.hasAttribute('data-theme-quick-view-trigger')) {
                element.dataset.themeQuickViewSurface = isDrawer ? 'drawer_recommendation_rail' : 'cart_recommendation_rail';
                element.dataset.themeQuickViewListId = 'cart_complementary_products';
                element.dataset.themeQuickViewListName = 'Complete the set';
                element.dataset.themeQuickViewPositionIndex = String(items.length + 1);
              }
            });
            aggregate.append(clonedCard);
            items.push(item);
            if (items.length >= limit) break;
          }
          if (items.length >= limit) break;
        }
        if (!items.length) return;
        aggregate.dataset.themeEventItemsJson = JSON.stringify(items);
        aggregate.dataset.themeEventItemsCount = String(items.length);
        const section = template.content.firstElementChild?.cloneNode(true);
        if (!(section instanceof Element) || !applyRecommendationFragment(section, aggregate)) return;
        section.classList.add('theme-cart-complementary');
        section.dataset.themeEventTemplateName = 'cart';
        section.dataset.themeEventTemplateType = 'core_commerce';
        section.dataset.themeRecommendationBound = 'true';
        renderedSection = section;
        template.before(section);
        initHorizontalRails(section);
        heightBinding = bindCartRecommendationHeight(owner, section);
        dispatchImpression(section, 'cart_complementary_recommendations');
      };

      let cached;
      try {
        const record = JSON.parse(window.sessionStorage.getItem(storageKey) || 'null');
        if (record?.key === cacheKey && Date.now() - record.createdAt < 300000 && Array.isArray(record.fragments)) {
          cached = record.fragments.map((markup) => markup ? new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-theme-recommendation-fragment]') : null);
        }
      } catch (_storageError) { /* Recommendations work without session storage. */ }
      if (cached) { renderAggregate(cached); return; }

      let request = cartRecommendationRequests.get(cacheKey);
      if (!request) {
        const fragments = new Array(productIds.length);
        let nextIndex = 0;
        const fetchNext = async () => {
          while (nextIndex < productIds.length) {
            const index = nextIndex++;
            try {
              fragments[index] = await fetchRecommendationFragment({ productId: productIds[index], limit, intent: 'complementary', recommendationSectionId });
            } catch (_error) { fragments[index] = null; }
          }
        };
        request = Promise.all(Array.from({ length: Math.min(3, productIds.length) }, fetchNext)).then(() => fragments);
        cartRecommendationRequests.set(cacheKey, request);
        request.finally(() => { cartRecommendationRequests.delete(cacheKey); });
      }
      request.then((fragments) => {
        if (!active || !template.isConnected) return;
        try {
          window.sessionStorage.setItem(storageKey, JSON.stringify({ key: cacheKey, createdAt: Date.now(), fragments: fragments.map((fragment) => fragment?.outerHTML || '') }));
        } catch (_storageError) { /* Optional cache cannot block the cart. */ }
        renderAggregate(fragments);
      });
    });

    complementaryTemplates.forEach((template) => {
      if (template.dataset.themeRecommendationBound === 'true') return;
      template.dataset.themeRecommendationBound = 'true';

      const productId = template.dataset.themeRecommendationProductId;
      const limit = Math.max(1, Number.parseInt(template.dataset.themeRecommendationLimit || '6', 10) || 6);
      const recommendationSectionId = template.dataset.themeRecommendationSectionId || 'theme-pdp-related-recommendations';

      if (!productId || !recommendationSectionId) return;

      fetchRecommendationFragment({
        productId,
        limit,
        intent: 'complementary',
        recommendationSectionId
      })
        .then((fragment) => {
          if (!fragment) return;

          const section = template.content.firstElementChild?.cloneNode(true);
          if (!(section instanceof Element) || !applyRecommendationFragment(section, fragment)) return;

          section.dataset.themeRecommendationBound = 'true';
          template.replaceWith(section);
          initHorizontalRails(section);
          dispatchImpression(section, 'complementary_recommendations');
        })
        .catch(() => {
          // Complementary products are optional; an unavailable response remains absent.
        });
    });

    recommendationSections.forEach((section) => {
      if (section.dataset.themeRecommendationIntent !== 'related' || section.dataset.themeRecommendationBound === 'true') return;
      section.dataset.themeRecommendationBound = 'true';

      const productId = section.dataset.themeRecommendationProductId;
      const limit = Math.max(1, Number.parseInt(section.dataset.themeRecommendationLimit || '6', 10) || 6);
      const recommendationSectionId = section.dataset.themeRecommendationSectionId || 'theme-pdp-related-recommendations';
      let impressionDispatched = false;

      if (!productId || !recommendationSectionId) return;

      const dispatchCurrentImpression = (source) => {
        if (impressionDispatched) return;
        impressionDispatched = true;
        dispatchImpression(section, source || 'load');
      };

      fetchRecommendationFragment({
        productId,
        limit,
        intent: 'related',
        recommendationSectionId
      })
        .then((fragment) => {
          if (!fragment || !applyRecommendationFragment(section, fragment)) {
            dispatchCurrentImpression('related_fallback');
            return;
          }

          dispatchCurrentImpression('related_recommendations');
        })
        .catch(() => {
          dispatchCurrentImpression('related_fallback_error');
        });
    });
  };

  document.addEventListener('theme:cart:render', (event) => {
    const owner = event.detail?.cart || event.detail?.drawer;
    if (owner instanceof Element) {
      const template = owner.querySelector('template[data-theme-cart-complementary-template]');
      if (cartRecommendationBindings.get(owner)?.template === template && template?.isConnected &&
          cartRecommendationBindings.get(owner)?.isActive()) {
        cartRecommendationBindings.get(owner).refreshHeight();
        return;
      }
      cartRecommendationBindings.get(owner)?.cleanup();
      cartRecommendationBindings.delete(owner);
      if (template) delete template.dataset.themeRecommendationBound;
      if (owner.isConnected) initPdpRecommendations(owner);
    }
  });

  const THEME_COLLECTION_STATE_VERSION = 2;
  const THEME_COLLECTION_STATE_STORAGE_PREFIX = 'theme_collection_state_v2:';
  const THEME_COLLECTION_CONTINUATION_PHASES = new Set(['pending', 'restoring', 'settled']);
  const THEME_COLLECTION_SCROLL_MODES = new Set(['auto', 'manual']);
  const THEME_COLLECTION_SOURCE_PAGE_ATTRIBUTE = 'data-theme-collection-source-page-url';
  const THEME_COLLECTION_ENHANCED_ATTRIBUTE = 'data-theme-collection-enhanced';
  const THEME_COLLECTION_FOCUSABLE_SELECTOR =
    'a[href], area[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  const getCollectionQueryKey = (rawUrl = window.location.href) => {
    try {
      const url = new URL(rawUrl, window.location.origin);
      const entries = [];
      const isSearchRoute = /\/search\/?$/.test(url.pathname);

      url.searchParams.forEach((value, key) => {
        // Product-result identity is limited to the parameters that Shopify
        // uses to select results. Navigation/decorator parameters are not
        // reliably carried by pagination links and must not split the state.
        if (key !== 'sort_by' && !key.startsWith('filter.') &&
            !(isSearchRoute && ['q', 'type', 'options[prefix]', 'options[unavailable]'].includes(key))) return;
        // Native GET forms can submit inactive filter fields as blank values;
        // Shopify omits those fields from pagination links.
        if (!value) return;
        entries.push([key, value]);
      });

      entries.sort((left, right) => {
        if (left[0] < right[0]) return -1;
        if (left[0] > right[0]) return 1;
        if (left[1] < right[1]) return -1;
        if (left[1] > right[1]) return 1;
        return 0;
      });

      const sortedParams = new URLSearchParams();
      entries.forEach(([key, value]) => sortedParams.append(key, value));
      return `${url.pathname || '/'}?${sortedParams.toString()}`;
    } catch (_error) {
      return '';
    }
  };

  const normalizeCollectionPageUrl = (rawUrl) => {
    if (!rawUrl) return '';

    try {
      const url = new URL(rawUrl, window.location.origin);
      if (url.origin !== window.location.origin) return '';
      url.searchParams.delete('section_id');
      url.hash = '';
      return `${url.pathname || '/'}${url.search}`;
    } catch (_error) {
      return '';
    }
  };

  const normalizeSameOriginUrl = (rawUrl) => {
    if (!rawUrl) return '';

    try {
      const url = new URL(rawUrl, window.location.origin);
      if (url.origin !== window.location.origin) return '';
      return `${url.pathname || '/'}${url.search}${url.hash}`;
    } catch (_error) {
      return '';
    }
  };

  const normalizeCollectionContinuation = (continuation, expectedQueryKey) => {
    if (!continuation || typeof continuation !== 'object') return null;

    const id = typeof continuation.id === 'string' ? continuation.id.trim() : '';
    const phase = typeof continuation.phase === 'string' ? continuation.phase : '';
    const sourceUrl = normalizeCollectionPageUrl(continuation.sourceUrl);
    const targetHref = normalizeSameOriginUrl(continuation.targetHref);
    const previousMode = typeof continuation.previousMode === 'string' ? continuation.previousMode : '';

    if (!id || !THEME_COLLECTION_CONTINUATION_PHASES.has(phase) || !sourceUrl || !targetHref) return null;
    if (!THEME_COLLECTION_SCROLL_MODES.has(previousMode)) return null;
    if (getCollectionQueryKey(sourceUrl) !== expectedQueryKey) return null;

    return { id, phase, sourceUrl, targetHref, previousMode };
  };

  const normalizeCollectionState = (state, expectedQueryKey) => {
    if (!state || state.version !== THEME_COLLECTION_STATE_VERSION) return null;

    const queryKey = state.queryKey || state.key;
    if (!queryKey || queryKey !== expectedQueryKey) return null;
    if (!Array.isArray(state.loadedPageUrls)) return null;

    const loadedPageUrls = [];
    state.loadedPageUrls.forEach((rawUrl) => {
      const pageUrl = normalizeCollectionPageUrl(rawUrl);
      if (!pageUrl || getCollectionQueryKey(pageUrl) !== expectedQueryKey || loadedPageUrls.includes(pageUrl)) {
        return;
      }
      loadedPageUrls.push(pageUrl);
    });

    if (loadedPageUrls.length !== state.loadedPageUrls.length) return null;

    const nextUrl = state.nextUrl ? normalizeCollectionPageUrl(state.nextUrl) : '';
    if (state.nextUrl && (!nextUrl || getCollectionQueryKey(nextUrl) !== expectedQueryKey)) return null;

    const scrollY = Number(state.scrollY);
    if (!Number.isFinite(scrollY) || scrollY < 0) return null;

    const selectedProductId = state.selectedProductId ? String(state.selectedProductId) : '';
    const selectedProductHref = state.selectedProductHref
      ? normalizeSameOriginUrl(state.selectedProductHref)
      : '';
    if (state.selectedProductHref && !selectedProductHref) return null;

    const continuation = state.continuation
      ? normalizeCollectionContinuation(state.continuation, expectedQueryKey)
      : null;
    if (state.continuation && !continuation) return null;

    return {
      version: THEME_COLLECTION_STATE_VERSION,
      queryKey,
      loadedPageUrls,
      nextUrl,
      selectedProductId,
      selectedProductHref,
      scrollY,
      focusFromProductLink: state.focusFromProductLink === true,
      ...(continuation ? { continuation } : {})
    };
  };

  const getCollectionStorage = () => {
    try {
      return window.sessionStorage;
    } catch (_error) {
      return null;
    }
  };

  const getCollectionStorageKey = (queryKey) =>
    `${THEME_COLLECTION_STATE_STORAGE_PREFIX}${encodeURIComponent(queryKey)}`;

  const readCollectionSessionState = (queryKey) => {
    const storage = getCollectionStorage();
    if (!storage || !queryKey) return null;

    try {
      const raw = storage.getItem(getCollectionStorageKey(queryKey));
      if (!raw) return null;
      return normalizeCollectionState(JSON.parse(raw), queryKey);
    } catch (_error) {
      return null;
    }
  };

  const writeCollectionSessionState = (state) => {
    const storage = getCollectionStorage();
    if (!storage || !state?.queryKey) return;

    try {
      storage.setItem(getCollectionStorageKey(state.queryKey), JSON.stringify(state));
    } catch (_error) {
      // Collection continuity remains optional when sessionStorage is unavailable.
    }
  };

  const clearCollectionSessionState = (queryKey) => {
    const storage = getCollectionStorage();
    if (!storage || !queryKey) return;

    try {
      storage.removeItem(getCollectionStorageKey(queryKey));
    } catch (_error) {
      // Collection continuity remains optional when sessionStorage is unavailable.
    }
  };

  const readCollectionHistoryState = (queryKey) => {
    try {
      const state = window.history?.state;
      const candidate = state?.themeCollectionState || state?.themeCollection || null;
      return normalizeCollectionState(candidate, queryKey);
    } catch (_error) {
      return null;
    }
  };

  const writeCollectionHistoryState = (state) => {
    try {
      const history = window.history;
      if (!history || typeof history.replaceState !== 'function' || !state?.queryKey) return false;
      const existingState = history.state && typeof history.state === 'object'
        ? history.state
        : {};
      const nextHistoryState = { ...existingState, themeCollectionState: state };
      const currentHref = getCollectionLocationHref?.() || window.location.href;
      try {
        history.replaceState(nextHistoryState, document.title, currentHref);
      } catch (_writeError) {
        try {
          history.replaceState(existingState, document.title, currentHref);
        } catch (_rollbackError) {
          // A failed rollback leaves native history state authoritative.
        }
        return false;
      }
      const writtenState = history.state?.themeCollectionState;
      if (!writtenState || writtenState.version !== state.version || writtenState.queryKey !== state.queryKey ||
        (state.continuation
          ? writtenState.continuation?.id !== state.continuation.id
          : writtenState.continuation)) {
        try {
          history.replaceState(existingState, document.title, currentHref);
        } catch (_rollbackError) {
          // The caller still restores its owned scroll claim and leaves navigation native.
        }
        return false;
      }
      return true;
    } catch (_error) {
      // History state is an enhancement; native navigation remains authoritative.
      return false;
    }
  };

  const getCollectionHistoryCandidate = () => {
    try {
      const state = window.history?.state;
      return {
        present: Boolean(state && typeof state === 'object' &&
          (Object.prototype.hasOwnProperty.call(state, 'themeCollectionState') ||
            Object.prototype.hasOwnProperty.call(state, 'themeCollection'))),
        value: state?.themeCollectionState || state?.themeCollection || null
      };
    } catch (_error) {
      return { present: false, value: null };
    }
  };

  const trySetCollectionScrollRestoration = (mode) => {
    if (!THEME_COLLECTION_SCROLL_MODES.has(mode)) return { ok: false, previousMode: '' };

    let history;
    let previousMode;
    try {
      history = window.history;
      if (!history) return { ok: false, previousMode: '' };
      previousMode = history.scrollRestoration;
    } catch (_error) {
      return { ok: false, previousMode: '' };
    }

    if (!THEME_COLLECTION_SCROLL_MODES.has(previousMode)) return { ok: false, previousMode: '' };

    try {
      history.scrollRestoration = mode;
      if (history.scrollRestoration !== mode) return { ok: false, previousMode };
    } catch (_error) {
      return { ok: false, previousMode };
    }

    return { ok: true, previousMode };
  };

  const focusWithoutScroll = (element) => {
    if (!element || typeof element.focus !== 'function') return;

    markFocusFromPointerClick(element);
    try {
      element.focus({ preventScroll: true });
    } catch (_error) {
      element.focus();
    }
  };

  const THEME_COLLECTION_TITLE_ROW_SELECTOR = '.theme-main-collection__title-row';
  const THEME_COLLECTION_TITLE_ROW_INLINE_CLASS = 'theme-main-collection__title-row--inline';
  const collectionTitleRowBindings = new WeakMap();

  const getCollectionElementInlineSize = (element) => {
    if (!element) return 0;

    const rect = element.getBoundingClientRect?.();
    if (rect && Number.isFinite(rect.width)) return rect.width;

    const offsetWidth = Number(element.offsetWidth);
    if (Number.isFinite(offsetWidth)) return offsetWidth;

    const scrollWidth = Number(element.scrollWidth);
    return Number.isFinite(scrollWidth) ? scrollWidth : 0;
  };

  const getCollectionTitleRowChild = (row, selector) =>
    Array.from(row?.children || []).find((child) => child?.matches?.(selector)) || null;

  const createCollectionTitleMeasurement = (title) => {
    if (!title || typeof title.cloneNode !== 'function') return null;

    const measurement = title.cloneNode(true);
    measurement.setAttribute?.('aria-hidden', 'true');
    measurement.setAttribute?.('inert', '');
    if ('inert' in measurement) measurement.inert = true;
    measurement.removeAttribute?.('id');

    const style = measurement.style;
    if (!style) return measurement;

    style.position = 'absolute';
    style.visibility = 'hidden';
    style.pointerEvents = 'none';
    style.flex = '0 0 auto';
    style.inlineSize = 'max-content';
    style.width = 'max-content';
    style.maxInlineSize = 'none';
    style.maxWidth = 'none';
    style.minInlineSize = 'max-content';
    style.minWidth = 'max-content';
    style.whiteSpace = 'nowrap';
    style.overflow = 'visible';
    style.margin = '0';
    style.insetInlineStart = '0';
    style.insetBlockStart = '0';

    return measurement;
  };

  const measureCollectionTitleNaturalWidth = (row, title) => {
    const measurement = createCollectionTitleMeasurement(title);
    if (!measurement) return 0;

    const append = typeof row.appendChild === 'function'
      ? (child) => row.appendChild(child)
      : typeof row.append === 'function'
        ? (child) => row.append(child)
        : null;
    if (!append) return 0;

    append(measurement);
    try {
      return getCollectionElementInlineSize(measurement);
    } finally {
      if (typeof measurement.remove === 'function') measurement.remove();
      else if (typeof row.removeChild === 'function') row.removeChild(measurement);
    }
  };

  const getCollectionTitleRowColumnGap = (row) => {
    const computed = typeof window.getComputedStyle === 'function'
      ? window.getComputedStyle(row)
      : null;
    const rawGap = computed?.columnGap || computed?.gap || row?.style?.columnGap || row?.style?.gap;
    const gap = parseFloat(rawGap);
    return Number.isFinite(gap) && gap >= 0 ? gap : 0;
  };

  const setCollectionTitleRowInline = (row, inline) => {
    if (typeof row.classList?.toggle === 'function') {
      row.classList.toggle(THEME_COLLECTION_TITLE_ROW_INLINE_CLASS, inline);
      return;
    }

    if (inline) row.classList?.add(THEME_COLLECTION_TITLE_ROW_INLINE_CLASS);
    else row.classList?.remove(THEME_COLLECTION_TITLE_ROW_INLINE_CLASS);
  };

  const initCollectionTitleFit = (scope = document) => {
    const rows = [];
    const addRow = (row) => {
      if (row && !rows.includes(row)) rows.push(row);
    };

    if (scope instanceof Element && scope.matches(THEME_COLLECTION_TITLE_ROW_SELECTOR)) addRow(scope);
    scope.querySelectorAll(THEME_COLLECTION_TITLE_ROW_SELECTOR).forEach(addRow);

    rows.forEach((row) => {
      const title = getCollectionTitleRowChild(row, 'h1');
      const count = getCollectionTitleRowChild(row, '.theme-main-collection__count');
      const existingBinding = collectionTitleRowBindings.get(row);
      if (!title || !count) {
        existingBinding?.dispose();
        return;
      }

      if (existingBinding && existingBinding.title === title && existingBinding.count === count) {
        existingBinding.measure();
        return;
      }
      existingBinding?.dispose();

      const binding = {
        title,
        count,
        disposed: false,
        resizeObserver: null,
        resizeHandler: null,
        measure: null,
        dispose: null
      };

      binding.measure = () => {
        if (binding.disposed) return;

        setCollectionTitleRowInline(row, false);
        const titleNaturalWidth = measureCollectionTitleNaturalWidth(row, title);
        const rowWidth = getCollectionElementInlineSize(row);
        const countWidth = getCollectionElementInlineSize(count);
        const columnGap = getCollectionTitleRowColumnGap(row);
        const fits = titleNaturalWidth + countWidth + columnGap <= rowWidth + 0.5;

        setCollectionTitleRowInline(row, fits);
      };

      binding.dispose = () => {
        if (binding.disposed) return;
        binding.disposed = true;
        binding.resizeObserver?.disconnect();
        if (binding.resizeHandler && typeof window.removeEventListener === 'function') {
          window.removeEventListener('resize', binding.resizeHandler);
        }
        if (collectionTitleRowBindings.get(row) === binding) collectionTitleRowBindings.delete(row);
      };

      collectionTitleRowBindings.set(row, binding);
      binding.measure();

      const ResizeObserverConstructor = typeof window.ResizeObserver === 'function'
        ? window.ResizeObserver
        : typeof ResizeObserver === 'function'
          ? ResizeObserver
          : null;
      if (ResizeObserverConstructor) {
        try {
          binding.resizeObserver = new ResizeObserverConstructor(() => binding.measure());
          binding.resizeObserver.observe(row);
        } catch (_error) {
          binding.resizeObserver = null;
        }
      }

      if (!binding.resizeObserver && typeof window.addEventListener === 'function') {
        binding.resizeHandler = () => binding.measure();
        window.addEventListener('resize', binding.resizeHandler);
      }

      const fontsReady = document.fonts?.ready;
      if (fontsReady && typeof fontsReady.then === 'function') {
        fontsReady.then(
          () => {
            if (!binding.disposed) binding.measure();
          },
          () => {}
        );
      }
    });
  };

  const collectionFilterDisclosureBindings = new WeakMap();

  const getCollectionFilterDisclosures = (scope) => {
    const disclosures = [];
    const isDisclosure = (element) => {
      if (!element) return false;
      if (typeof element.matches === 'function' && element.matches('details[data-theme-collection-filter-disclosure]')) {
        return true;
      }
      return element.tagName === 'DETAILS' && element.hasAttribute?.('data-theme-collection-filter-disclosure');
    };

    if (isDisclosure(scope)) disclosures.push(scope);
    scope?.querySelectorAll?.('details[data-theme-collection-filter-disclosure]')?.forEach((disclosure) => {
      if (!disclosures.includes(disclosure)) disclosures.push(disclosure);
    });
    return disclosures;
  };

  const abortCollectionFilterDisclosureBindings = (scope) => {
    getCollectionFilterDisclosures(scope || document).forEach((disclosure) => {
      collectionFilterDisclosureBindings.get(disclosure)?.dispose();
    });
  };

  const initCollectionFilterEnhancements = (scope = document) => {
    const THEME_COLLECTION_FILTER_PANEL_GAP = 8;
    const THEME_COLLECTION_FILTER_PANEL_GUTTER = 16;
    const THEME_COLLECTION_FILTER_PANEL_PREFERRED_HEIGHT = 448;
    const THEME_COLLECTION_FILTER_PANEL_MAX_WIDTH = 448;
    const THEME_COLLECTION_FILTER_POINTER_SUPPRESSION_TIMEOUT = 500;
    const collectionRoots = [];
    if (scope instanceof Element && scope.matches('[data-theme-collection-infinite-root="true"]')) {
      collectionRoots.push(scope);
    }
    collectionRoots.push(...scope.querySelectorAll('[data-theme-collection-infinite-root="true"]'));

    const filterBars = [];
    const addFilterBar = (bar) => {
      if (bar && !filterBars.includes(bar)) filterBars.push(bar);
    };

    if (scope instanceof Element && scope.matches('.theme-collection-filter-bar, [data-theme-collection-filter-bar]')) {
      addFilterBar(scope);
    }
    scope.querySelectorAll('.theme-collection-filter-bar, [data-theme-collection-filter-bar]').forEach(addFilterBar);
    scope.querySelectorAll('details[data-theme-collection-filter-disclosure]').forEach((disclosure) => {
      addFilterBar(disclosure.closest('.theme-collection-filter-bar, [data-theme-collection-filter-bar]') || disclosure);
    });

    const isMobile = () => {
      if (typeof window.matchMedia === 'function') {
        return window.matchMedia('(max-width: 47.999rem)').matches;
      }
      return window.innerWidth < 768;
    };

    const getFocusable = (container) =>
      Array.from(container?.querySelectorAll(THEME_COLLECTION_FOCUSABLE_SELECTOR) || []).filter((element) => {
        return !element.hidden && element.getAttribute('aria-hidden') !== 'true' && !element.disabled;
      });

    filterBars.forEach((filterBar) => {
      const details = [];
      if (filterBar.matches('details')) details.push(filterBar);
      const scopedDisclosures = Array.from(filterBar.querySelectorAll('details[data-theme-collection-filter-disclosure]'));
      if (scopedDisclosures.length) {
        details.push(...scopedDisclosures);
      } else {
        // Test/fragment scopes may not implement compound selectors. The outer
        // disclosure is always the first details element in the filter bar.
        const fallbackDisclosure = filterBar.querySelectorAll('details')[0];
        if (fallbackDisclosure && !details.includes(fallbackDisclosure)) details.push(fallbackDisclosure);
      }

      details.forEach((disclosure) => {
        if (collectionFilterDisclosureBindings.has(disclosure)) return;
        if (disclosure.dataset.themeCollectionFilterBound === 'true') {
          // The WeakMap is authoritative. A marker without a live binding is
          // stale after a section unload or a reused test/fragment node.
          disclosure.removeAttribute?.('data-theme-collection-filter-bound');
        }

        const summary = disclosure.querySelector(':scope > summary') || disclosure.querySelector('summary');
        const panel = disclosure.querySelector('[data-theme-collection-filter-panel]') ||
          disclosure.querySelector('.theme-collection-filter-bar__panel');

        if (!summary || !panel) return;

        const closeButton = panel.querySelector('[data-theme-collection-filter-close]');
        const heading = panel.querySelector('[data-theme-collection-filter-heading], h2, h3');
        const searchFilterForm = panel.querySelector('form[data-theme-search-filter-form]');
        const searchFilterApplyButton = searchFilterForm?.querySelector('button[type="submit"]');
        if (!summary.hasAttribute('aria-controls') && panel.id) summary.setAttribute('aria-controls', panel.id);
        if (heading && !heading.id && panel.id) heading.id = `${panel.id}-heading`;
        if (heading && heading.id && !panel.hasAttribute('aria-labelledby')) panel.setAttribute('aria-labelledby', heading.id);
        const originalRole = panel.getAttribute('role') || 'region';
        const originalAriaModal = panel.getAttribute('aria-modal');
        let shouldRestoreFocus = false;
        let desktopPanelPositionFramePending = false;
        let suppressedOutsidePointerActivation = null;
        let suppressedOutsidePointerActivationTimer = null;
        const controller = typeof AbortController === 'function'
          ? new AbortController()
          : typeof window.AbortController === 'function'
            ? new window.AbortController()
            : null;
        const binding = {
          controller,
          disposed: false,
          listeners: [],
          dispose: null
        };

        collectionFilterDisclosureBindings.set(disclosure, binding);

        const listen = (target, type, handler, listenerOptions) => {
          if (!target || typeof target.addEventListener !== 'function') return;

          const options = {
            ...(listenerOptions || {}),
            ...(controller?.signal ? { signal: controller.signal } : {})
          };
          const hasOptions = Object.keys(options).length > 0;
          let appliedOptions = hasOptions ? options : undefined;
          try {
            target.addEventListener(type, handler, appliedOptions);
          } catch (_error) {
            // Older event targets may not accept the signal option. The
            // listener is still tracked for explicit teardown below.
            appliedOptions = listenerOptions;
            target.addEventListener(type, handler, appliedOptions);
          }
          binding.listeners.push({ target, type, handler, options: appliedOptions });
        };

        const clearOutsidePointerActivationSuppression = () => {
          suppressedOutsidePointerActivation = null;
          if (suppressedOutsidePointerActivationTimer !== null) {
            window.clearTimeout?.(suppressedOutsidePointerActivationTimer);
            suppressedOutsidePointerActivationTimer = null;
          }
        };

        const suppressOutsidePointerActivation = (event, eventPath) => {
          clearOutsidePointerActivationSuppression();
          suppressedOutsidePointerActivation = {
            target: event.target,
            path: eventPath.slice()
          };
          if (typeof window.setTimeout === 'function') {
            suppressedOutsidePointerActivationTimer = window.setTimeout(() => {
              suppressedOutsidePointerActivation = null;
              suppressedOutsidePointerActivationTimer = null;
            }, THEME_COLLECTION_FILTER_POINTER_SUPPRESSION_TIMEOUT);
          }
        };

        const setPanelStyle = (property, value) => {
          if (typeof panel.style?.setProperty === 'function') panel.style.setProperty(property, value);
          else if (panel.style) panel.style[property] = value;
        };

        const removePanelStyle = (property) => {
          if (typeof panel.style?.removeProperty === 'function') panel.style.removeProperty(property);
          else if (panel.style) panel.style[property] = '';
        };

        const clearDesktopPanelPosition = () => {
          desktopPanelPositionFramePending = false;
          if (panel.dataset) delete panel.dataset.themeCollectionFilterPanelPlacement;
          panel.removeAttribute?.('data-theme-collection-filter-panel-placement');
          removePanelStyle('--theme-collection-filter-panel-inline-start');
          removePanelStyle('--theme-collection-filter-panel-max-block-size');
          removePanelStyle('--theme-collection-filter-panel-block-offset');
          removePanelStyle('width');
        };

        binding.dispose = () => {
          if (binding.disposed) return;
          binding.disposed = true;
          clearOutsidePointerActivationSuppression();
          controller?.abort();
          binding.listeners.forEach(({ target, type, handler, options }) => {
            try {
              target.removeEventListener?.(type, handler, options);
            } catch (_error) {
              target.removeEventListener?.(type, handler);
            }
          });
          binding.listeners = [];
          clearDesktopPanelPosition();
          disclosure.dataset.themeCollectionFilterModal = 'false';
          if (!document.querySelector('[data-theme-collection-filter-modal="true"]')) {
            document.body?.classList?.remove('theme-collection-filter-modal-open');
          }
          disclosure.removeAttribute?.(THEME_COLLECTION_ENHANCED_ATTRIBUTE);
          disclosure.removeAttribute?.('data-theme-collection-filter-bound');
          if (collectionFilterDisclosureBindings.get(disclosure) === binding) {
            collectionFilterDisclosureBindings.delete(disclosure);
          }
        };

        const positionDesktopPanel = () => {
          if (binding.disposed || !disclosure.open || isMobile()) {
            clearDesktopPanelPosition();
            return;
          }

          const triggerRect = summary.getBoundingClientRect?.();
          const visualViewport = window.visualViewport;
          const viewportHeight = Number.isFinite(visualViewport?.height)
            ? visualViewport.height
            : window.innerHeight;
          const viewportWidth = Number.isFinite(visualViewport?.width)
            ? visualViewport.width
            : window.innerWidth;
          if (
            !triggerRect ||
            !Number.isFinite(triggerRect.top) ||
            !Number.isFinite(triggerRect.bottom) ||
            !Number.isFinite(triggerRect.left) ||
            !Number.isFinite(viewportHeight) ||
            !Number.isFinite(viewportWidth)
          ) {
            return;
          }

          const panelScrollHeight = Number.isFinite(panel.scrollHeight)
            ? panel.scrollHeight
            : THEME_COLLECTION_FILTER_PANEL_PREFERRED_HEIGHT;
          const natural = Math.min(panelScrollHeight, THEME_COLLECTION_FILTER_PANEL_PREFERRED_HEIGHT);
          const below = Math.max(
            0,
            viewportHeight - THEME_COLLECTION_FILTER_PANEL_GUTTER - triggerRect.bottom - THEME_COLLECTION_FILTER_PANEL_GAP
          );
          const above = Math.max(
            0,
            triggerRect.top - THEME_COLLECTION_FILTER_PANEL_GAP - THEME_COLLECTION_FILTER_PANEL_GUTTER
          );
          const placeBelow = below >= natural || below >= above;
          const available = placeBelow ? below : above;
          const width = Math.min(
            THEME_COLLECTION_FILTER_PANEL_MAX_WIDTH,
            Math.max(0, viewportWidth - THEME_COLLECTION_FILTER_PANEL_GUTTER * 2)
          );
          const left = Math.min(
            Math.max(THEME_COLLECTION_FILTER_PANEL_GUTTER, triggerRect.left),
            Math.max(THEME_COLLECTION_FILTER_PANEL_GUTTER, viewportWidth - THEME_COLLECTION_FILTER_PANEL_GUTTER - width)
          );
          const blockOffset = placeBelow
            ? triggerRect.bottom + THEME_COLLECTION_FILTER_PANEL_GAP
            : viewportHeight - triggerRect.top + THEME_COLLECTION_FILTER_PANEL_GAP;

          const placement = placeBelow ? 'below' : 'above';
          if (panel.dataset) panel.dataset.themeCollectionFilterPanelPlacement = placement;
          panel.setAttribute?.('data-theme-collection-filter-panel-placement', placement);
          setPanelStyle('--theme-collection-filter-panel-inline-start', `${left}px`);
          setPanelStyle('--theme-collection-filter-panel-max-block-size', `${available}px`);
          setPanelStyle('--theme-collection-filter-panel-block-offset', `${blockOffset}px`);
          setPanelStyle('width', `${width}px`);
        };

        const scheduleDesktopPanelPosition = () => {
          if (!disclosure.open || isMobile()) {
            clearDesktopPanelPosition();
            return;
          }
          if (desktopPanelPositionFramePending) return;

          desktopPanelPositionFramePending = true;
          const update = () => {
            desktopPanelPositionFramePending = false;
            if (!binding.disposed) positionDesktopPanel();
          };
          if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(update);
          else if (typeof window.setTimeout === 'function') window.setTimeout(update, 0);
          else update();
        };

        const setSummaryState = () => {
          summary.setAttribute('aria-expanded', disclosure.open ? 'true' : 'false');
        };

        const containsNode = (ancestor, node) => {
          if (!ancestor || !node) return false;
          if (typeof ancestor.contains === 'function' && ancestor.contains(node)) return true;
          for (let current = node; current; current = current.parentNode) {
            if (current === ancestor) return true;
          }
          return false;
        };

        const getEventPath = (event) => {
          if (typeof event?.composedPath === 'function') {
            const path = event.composedPath();
            if (Array.isArray(path)) return path;
          }
          return event?.target ? [event.target] : [];
        };

        const pathsMatch = (firstPath, secondPath) => {
          if (!Array.isArray(firstPath) || !Array.isArray(secondPath) || firstPath.length !== secondPath.length) {
            return false;
          }
          return firstPath.every((entry, index) => entry === secondPath[index]);
        };

        const restoreSummaryFocusIfSafe = () => {
          if (!isMobile()) {
            focusWithoutScroll(summary);
            return;
          }

          const activeElement = document.activeElement;
          const focusIsInside = activeElement === summary ||
            activeElement === heading ||
            activeElement === closeButton ||
            containsNode(disclosure, activeElement) ||
            containsNode(panel, activeElement);
          if (activeElement === document.body || focusIsInside) {
            focusWithoutScroll(summary);
          }
        };

        const closeDisclosure = ({ restoreFocus = true } = {}) => {
          shouldRestoreFocus = restoreFocus;
          disclosure.open = false;
        };

        const unlockBody = () => {
          disclosure.dataset.themeCollectionFilterModal = 'false';
          if (!document.querySelector('[data-theme-collection-filter-modal="true"]')) {
            document.body.classList.remove('theme-collection-filter-modal-open');
          }
        };

        const setDesktopPanelState = () => {
          if (!disclosure.open || isMobile()) clearDesktopPanelPosition();
          panel.setAttribute('role', originalRole);
          if (originalAriaModal === null) panel.removeAttribute('aria-modal');
          else panel.setAttribute('aria-modal', originalAriaModal);
          unlockBody();
        };

        const focusPanelEntry = () => {
          if (binding.disposed || !disclosure.open || !isMobile()) return;
          const target = heading || closeButton || getFocusable(panel)[0];
          if (heading && !heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
          focusWithoutScroll(target);
        };

        const setMobilePanelState = (focusEntry = false) => {
          clearDesktopPanelPosition();
          panel.setAttribute('role', 'dialog');
          panel.setAttribute('aria-modal', 'true');
          disclosure.dataset.themeCollectionFilterModal = 'true';
          document.body.classList.add('theme-collection-filter-modal-open');
          if (focusEntry) {
            if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(focusPanelEntry);
            else window.setTimeout(focusPanelEntry, 0);
          }
        };

        const syncDisclosureMode = () => {
          setSummaryState();
          if (!disclosure.open) {
            setDesktopPanelState();
            if (shouldRestoreFocus) {
              shouldRestoreFocus = false;
              restoreSummaryFocusIfSafe();
            }
            return;
          }

          if (isMobile()) setMobilePanelState(false);
          else {
            setDesktopPanelState();
            scheduleDesktopPanelPosition();
          }
        };

        listen(disclosure, 'toggle', () => {
          if (binding.disposed) return;
          setSummaryState();
          if (disclosure.open && isMobile()) {
            setMobilePanelState(true);
            return;
          }
          if (!disclosure.open) {
            setDesktopPanelState();
            if (shouldRestoreFocus) {
              shouldRestoreFocus = false;
              restoreSummaryFocusIfSafe();
            }
          } else {
            setDesktopPanelState();
            scheduleDesktopPanelPosition();
          }
        });

        listen(disclosure, 'keydown', (event) => {
          if (!disclosure.open || event.key !== 'Escape') return;

          event.preventDefault();
          closeDisclosure({ restoreFocus: true });
        });

        listen(panel, 'keydown', (event) => {
          if (!disclosure.open) return;

          if (!isMobile()) return;

          if (event.key !== 'Tab') return;
          const focusable = getFocusable(panel);
          if (event.shiftKey && heading && document.activeElement === heading) {
            event.preventDefault();
            focusWithoutScroll(focusable[focusable.length - 1] || closeButton || summary);
            return;
          }

          if (!focusable.length) {
            event.preventDefault();
            focusWithoutScroll(closeButton || summary);
            return;
          }

          const first = focusable[0];
          const last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            focusWithoutScroll(last);
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            focusWithoutScroll(first);
          }
        });

        listen(closeButton, 'click', (event) => {
          event.preventDefault();
          closeDisclosure({ restoreFocus: true });
        });

        /* Some merchant/app stacks re-handle native GET submissions inside a
         * modal details panel and can drop checked Search facets. Serialize the
         * successful controls explicitly for Search while leaving the native
         * form as the no-JavaScript fallback. */
        const submitSearchFilters = (event) => {
          const controls = Array.from(searchFilterForm.elements || []);
          const parameters = [];
          controls.forEach((control) => {
            if (!control?.name || control.disabled) return;
            if ((control.type === 'checkbox' || control.type === 'radio') && !control.checked) return;

            const values = control.tagName === 'SELECT' && control.multiple
              ? Array.from(control.selectedOptions || []).map((option) => option.value)
              : [control.value];
            values.forEach((value) => {
              const normalizedValue = String(value ?? '').trim();
              if (normalizedValue === '') return;
              parameters.push(`${encodeURIComponent(control.name)}=${encodeURIComponent(normalizedValue)}`);
            });
          });

          event.preventDefault();
          const destination = String(searchFilterForm.action || window.location.pathname || '/search').split('?')[0];
          window.location.href = parameters.length ? `${destination}?${parameters.join('&')}` : destination;
        };
        listen(searchFilterForm, 'submit', submitSearchFilters, { capture: true });
        listen(searchFilterApplyButton, 'click', submitSearchFilters, { capture: true });

        listen(document, 'pointerdown', (event) => {
          if (!disclosure.open) return;
          const eventPath = getEventPath(event);
          if (eventPath?.includes?.(disclosure) || containsNode(disclosure, event.target)) return;

          suppressOutsidePointerActivation(event, eventPath);
          event.preventDefault?.();
          event.stopPropagation?.();
          closeDisclosure({ restoreFocus: true });
        }, { capture: true });

        listen(document, 'click', (event) => {
          if (!suppressedOutsidePointerActivation) return;

          const eventPath = getEventPath(event);
          const sameTarget = event.target === suppressedOutsidePointerActivation.target;
          const samePath = pathsMatch(eventPath, suppressedOutsidePointerActivation.path);
          if (!sameTarget || !samePath) return;

          event.preventDefault?.();
          event.stopPropagation?.();
          clearOutsidePointerActivationSuppression();
        }, { capture: true });

        listen(document, 'pointercancel', clearOutsidePointerActivationSuppression, { capture: true });

        const handleViewportChange = () => {
          if (!disclosure.open) {
            clearDesktopPanelPosition();
            setDesktopPanelState();
            return;
          }
          if (isMobile()) setMobilePanelState(false);
          else {
            setDesktopPanelState();
            scheduleDesktopPanelPosition();
          }
        };

        listen(window, 'resize', handleViewportChange);
        listen(window, 'scroll', handleViewportChange);
        if (typeof window.visualViewport?.addEventListener === 'function') {
          listen(window.visualViewport, 'resize', handleViewportChange);
          listen(window.visualViewport, 'scroll', handleViewportChange);
        }

        disclosure.setAttribute(THEME_COLLECTION_ENHANCED_ATTRIBUTE, 'true');
        disclosure.dataset.themeCollectionFilterBound = 'true';
        syncDisclosureMode();
      });

      const sortForms = filterBar.querySelectorAll('form.theme-collection-filter-bar__sort, form[data-theme-collection-sort-form]');
      sortForms.forEach((sortForm) => {
        if (sortForm.dataset.themeCollectionSortBound === 'true') return;

        const sortSelect = sortForm.querySelector('[data-theme-collection-sort], select[name="sort_by"]');
        if (!sortSelect) return;

        const submitFallback = sortForm.querySelector('[data-theme-collection-sort-submit], button[type="submit"], input[type="submit"]');
        sortForm.setAttribute(THEME_COLLECTION_ENHANCED_ATTRIBUTE, 'true');
        sortSelect.setAttribute(THEME_COLLECTION_ENHANCED_ATTRIBUTE, 'true');
        if (submitFallback) {
          submitFallback.hidden = true;
          submitFallback.setAttribute('aria-hidden', 'true');
        }

        sortSelect.addEventListener('change', () => {
          sortSelect.dataset.themeEventSortOption = sortSelect.value;
          if (typeof sortForm.requestSubmit === 'function') {
            sortForm.requestSubmit();
          } else if (typeof sortForm.submit === 'function') {
            sortForm.submit();
          }
        });

        sortForm.dataset.themeCollectionSortBound = 'true';
      });
    });

    collectionRoots.forEach((root) => {
      root.setAttribute(THEME_COLLECTION_ENHANCED_ATTRIBUTE, 'true');
    });
  };

  const collectionRootStates = new WeakMap();
  const collectionScrollLifecycle = {
    listenersBound: false,
    rootState: null,
    owner: null,
    restorationPromise: null,
    restorationRunId: '',
    pageshowSeen: false
  };
  let collectionContinuationSequence = 0;

  const getCollectionLocationHref = () => {
    try {
      return window.location?.href || '';
    } catch (_error) {
      return '';
    }
  };

  const getCollectionCurrentPageUrl = () => normalizeCollectionPageUrl(getCollectionLocationHref());
  const getCollectionCurrentQueryKey = () => getCollectionQueryKey(getCollectionLocationHref());

  const readCollectionScrollY = () => {
    try {
      const scrollY = Number(window.scrollY);
      return Number.isFinite(scrollY) && scrollY >= 0 ? scrollY : null;
    } catch (_error) {
      return null;
    }
  };

  const createCollectionContinuationId = () => {
    try {
      const randomUuid = window.crypto?.randomUUID;
      if (typeof randomUuid === 'function') {
        const id = randomUuid.call(window.crypto);
        if (id) return String(id);
      }
    } catch (_error) {
      // Fall through to the local identifier for older browsers and test hosts.
    }

    collectionContinuationSequence += 1;
    let randomPart = '';
    try {
      randomPart = Math.random().toString(36).slice(2);
    } catch (_error) {
      randomPart = 'local';
    }
    return `collection-${Date.now()}-${collectionContinuationSequence}-${randomPart}`;
  };

  const hasCollectionRoot = (root) => {
    if (!root) return false;
    if (typeof root.matches === 'function' && root.matches('[data-theme-collection-infinite-root="true"]') === false &&
      root.getAttribute?.('data-theme-collection-infinite-root') !== 'true') {
      return false;
    }
    return Boolean(root.querySelector?.('[data-theme-collection-infinite-scroll]') &&
      root.querySelector?.('.theme-main-collection__grid'));
  };

  const getCollectionHistoryStateForQuery = (queryKey) => {
    try {
      return readCollectionHistoryState(queryKey);
    } catch (_error) {
      return null;
    }
  };

  const resolveCollectionContinuation = (queryKey = getCollectionCurrentQueryKey()) => {
    if (!queryKey) return null;

    const historyState = getCollectionHistoryStateForQuery(queryKey);
    const continuation = historyState?.continuation;
    if (!historyState || !continuation) return null;
    if (continuation.sourceUrl !== getCollectionCurrentPageUrl()) return null;

    // History state is authoritative. Session state is a mirror only, and is
    // intentionally read after exact query/id validation so an unrelated tab
    // or stale query can never arm a restoration on its own.
    let sessionState = null;
    const mirroredState = readCollectionSessionState(queryKey);
    if (mirroredState?.continuation?.id === continuation.id &&
      mirroredState.continuation.sourceUrl === continuation.sourceUrl) {
      sessionState = mirroredState;
    }

    return { historyState, continuation, sessionState };
  };

  const isCollectionOwnerCurrent = (owner = collectionScrollLifecycle.owner, phases = ['pending', 'restoring']) => {
    if (!owner || !phases.includes(owner.phase)) return false;
    if (owner.queryKey !== getCollectionCurrentQueryKey() || owner.sourceUrl !== getCollectionCurrentPageUrl()) {
      return false;
    }

    const resolved = resolveCollectionContinuation(owner.queryKey);
    if (!resolved || resolved.continuation.id !== owner.id || !phases.includes(resolved.continuation.phase)) {
      return false;
    }

    const manualResult = trySetCollectionScrollRestoration('manual');
    if (!manualResult.ok) return false;
    return true;
  };

  const clearCollectionHistoryContinuation = (owner, { force = false } = {}) => {
    let candidate;
    try {
      candidate = getCollectionHistoryCandidate();
    } catch (_error) {
      return false;
    }
    if (!candidate.present) return true;

    const queryKey = getCollectionCurrentQueryKey();
    const state = candidate.value ? normalizeCollectionState(candidate.value, queryKey) : null;
    if (!state) {
      if (!force) return false;
      try {
        const history = window.history;
        const existingState = history?.state && typeof history.state === 'object' ? history.state : {};
        const nextState = { ...existingState };
        delete nextState.themeCollectionState;
        delete nextState.themeCollection;
        if (typeof history?.replaceState !== 'function') return false;
        history.replaceState(nextState, document.title, getCollectionLocationHref());
        return true;
      } catch (_error) {
        return false;
      }
    }

    if (!state.continuation || (owner && state.continuation.id !== owner.id)) return true;
    const stateWithoutContinuation = { ...state };
    delete stateWithoutContinuation.continuation;
    return writeCollectionHistoryState(stateWithoutContinuation);
  };

  const isExactCollectionScrollOwner = (owner) => {
    if (!owner || collectionScrollLifecycle.owner !== owner) return false;
    if (owner.queryKey !== getCollectionCurrentQueryKey() || owner.sourceUrl !== getCollectionCurrentPageUrl()) {
      return false;
    }

    const resolved = resolveCollectionContinuation(owner.queryKey);
    const continuation = resolved?.continuation;
    return Boolean(continuation &&
      continuation.id === owner.id &&
      continuation.phase === owner.phase &&
      continuation.sourceUrl === owner.sourceUrl &&
      continuation.targetHref === owner.targetHref &&
      continuation.previousMode === owner.previousMode);
  };

  const releaseCollectionScrollOwnership = (owner = collectionScrollLifecycle.owner) => {
    if (!owner) return;

    if (isExactCollectionScrollOwner(owner)) {
      trySetCollectionScrollRestoration(owner.previousMode);
    }
    if (collectionScrollLifecycle.owner === owner) {
      collectionScrollLifecycle.owner = null;
      collectionScrollLifecycle.restorationRunId = '';
    }
  };

  const abandonCollectionContinuation = (reason = 'abandoned', { clearHistory = true } = {}) => {
    const owner = collectionScrollLifecycle.owner;
    if (!owner) return;

    if (owner.rootState?.abortActiveLoad) owner.rootState.abortActiveLoad();
    owner.abandonReason = reason;
    releaseCollectionScrollOwnership(owner);
    if (clearHistory && owner.queryKey === getCollectionCurrentQueryKey() && owner.sourceUrl === getCollectionCurrentPageUrl()) {
      clearCollectionHistoryContinuation(owner, { force: true });
    }
    clearCollectionSessionState(owner.queryKey);
  };

  const ensureCollectionScrollOwnership = (continuation, rootState = null) => {
    if (!continuation) return false;

    const existingOwner = collectionScrollLifecycle.owner;
    if (existingOwner && existingOwner.id !== continuation.id) {
      releaseCollectionScrollOwnership(existingOwner);
    }

    const manualResult = trySetCollectionScrollRestoration('manual');
    if (!manualResult.ok) return false;

    collectionScrollLifecycle.owner = {
      ...continuation,
      queryKey: getCollectionCurrentQueryKey(),
      rootState: rootState || existingOwner?.rootState || null
    };
    return true;
  };

  const updateCollectionContinuationPhase = (rootState, phase) => {
    const owner = collectionScrollLifecycle.owner;
    if (!owner || !THEME_COLLECTION_CONTINUATION_PHASES.has(phase)) return false;

    const resolved = resolveCollectionContinuation(owner.queryKey);
    if (!resolved || resolved.continuation.id !== owner.id) return false;

    const nextContinuation = { ...resolved.continuation, phase };
    const nextState = { ...resolved.historyState, continuation: nextContinuation };
    if (!writeCollectionHistoryState(nextState)) return false;
    writeCollectionSessionState(nextState);
    owner.phase = phase;
    owner.rootState = rootState || owner.rootState;
    return true;
  };

  const armCollectionContinuation = (rootState, link) => {
    if (!rootState || !link || !hasCollectionRoot(rootState.root)) return false;

    const queryKey = rootState.queryKey;
    const sourceUrl = getCollectionCurrentPageUrl();
    const targetHref = normalizeSameOriginUrl(link.getAttribute?.('href') || link.href || '');
    const scrollY = readCollectionScrollY();
    if (!queryKey || !sourceUrl || !targetHref || scrollY === null) return false;
    if (getCollectionQueryKey(sourceUrl) !== queryKey) return false;

    const existingOwner = collectionScrollLifecycle.owner;
    if (existingOwner) abandonCollectionContinuation('rearmed');

    const manualResult = trySetCollectionScrollRestoration('manual');
    if (!manualResult.ok) return false;

    const continuation = {
      id: createCollectionContinuationId(),
      phase: 'pending',
      sourceUrl,
      targetHref,
      previousMode: manualResult.previousMode
    };
    const selectedProductId = rootState.getProductId(link) ||
      link.closest?.('[data-theme-product-id]')?.dataset?.themeProductId || '';
    const state = rootState.makeCollectionState({
      selectedProductId,
      selectedProductHref: targetHref,
      scrollY,
      focusFromProductLink: true,
      continuation
    });

    if (!writeCollectionHistoryState(state)) {
      trySetCollectionScrollRestoration(manualResult.previousMode);
      return false;
    }

    collectionScrollLifecycle.owner = { ...continuation, queryKey, rootState };
    writeCollectionSessionState(state);
    rootState.abortActiveLoad();
    return true;
  };

  const isCollectionPrimaryProductActivation = (event, link) => {
    if (!event || !link || event.defaultPrevented) return false;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (event.button !== undefined && event.button !== 0) return false;

    const target = (link.getAttribute?.('target') || link.target || '').trim().toLowerCase();
    if (target && target !== '_self') return false;
    if (link.hasAttribute?.('download') || link.download) return false;

    return Boolean(normalizeSameOriginUrl(link.getAttribute?.('href') || link.href || ''));
  };

  const findCollectionRootState = () => {
    const state = collectionScrollLifecycle.rootState;
    return state && hasCollectionRoot(state.root) ? state : null;
  };

  const handleCollectionPageShow = (event = {}) => {
    collectionScrollLifecycle.pageshowSeen = true;
    const queryKey = getCollectionCurrentQueryKey();
    const resolved = resolveCollectionContinuation(queryKey);
    const rootState = findCollectionRootState();

    // BFCache restores form values independently of the server-rendered results.
    // Reconcile every collection entry, including ordinary sort/filter traversal
    // that has no product-navigation continuation.
    if (rootState?.queryKey === queryKey) rootState.syncNativeSort();

    if (!resolved) {
      const owner = collectionScrollLifecycle.owner;
      if (owner && (owner.queryKey !== queryKey || owner.sourceUrl !== getCollectionCurrentPageUrl())) {
        abandonCollectionContinuation('pageshow-query-mismatch');
      } else if (owner) {
        abandonCollectionContinuation('pageshow-owner-mismatch');
      }
      return Promise.resolve(false);
    }

    if (!rootState) {
      if (document.readyState !== 'loading') {
        clearCollectionHistoryContinuation(null, { force: true });
        clearCollectionSessionState(queryKey);
        if (collectionScrollLifecycle.owner) releaseCollectionScrollOwnership();
      }
      return Promise.resolve(false);
    }

    if (!ensureCollectionScrollOwnership(resolved.continuation, rootState)) {
      clearCollectionHistoryContinuation(resolved.continuation, { force: true });
      clearCollectionSessionState(queryKey);
      releaseCollectionScrollOwnership();
      return Promise.resolve(false);
    }

    const owner = collectionScrollLifecycle.owner;
    owner.rootState = rootState;
    if (resolved.continuation.phase === 'settled') return Promise.resolve(false);
    if (collectionScrollLifecycle.restorationRunId === owner.id || collectionScrollLifecycle.restorationPromise) {
      return collectionScrollLifecycle.restorationPromise || Promise.resolve(false);
    }

    return beginCollectionRestoration(rootState, resolved.historyState, resolved.continuation, event);
  };

  const handleCollectionPageHide = () => {
    const rootState = collectionScrollLifecycle.rootState;
    rootState?.abortActiveLoad?.();

    const owner = collectionScrollLifecycle.owner;
    if (!owner) return;

    const resolved = resolveCollectionContinuation(owner.queryKey);
    if (!resolved || resolved.continuation.id !== owner.id) {
      releaseCollectionScrollOwnership(owner);
      return;
    }

    if (resolved.continuation.phase === 'pending') {
      // The original collection page may enter BFCache with this entry-owned
      // claim. Keep the verified manual mode and pending state intact.
      if (!trySetCollectionScrollRestoration('manual').ok) {
        abandonCollectionContinuation('pending-pagehide-unsupported');
      }
      return;
    }

    abandonCollectionContinuation(`${resolved.continuation.phase}-pagehide`);
  };

  const handleCollectionPopState = () => {
    const rootState = collectionScrollLifecycle.rootState;
    const currentQueryKey = getCollectionCurrentQueryKey();
    if (rootState && rootState.queryKey !== currentQueryKey) {
      rootState.abortActiveLoad();
      rootState.observer?.disconnect?.();
      if (collectionScrollLifecycle.owner) abandonCollectionContinuation('canonical-query-popstate');
      return;
    }

    const owner = collectionScrollLifecycle.owner;
    if (owner && (!resolveCollectionContinuation(owner.queryKey) || owner.sourceUrl !== getCollectionCurrentPageUrl())) {
      abandonCollectionContinuation('popstate-owner-mismatch');
    }
  };

  const bindCollectionLifecycleListeners = () => {
    if (collectionScrollLifecycle.listenersBound) return;
    collectionScrollLifecycle.listenersBound = true;
    if (typeof window.addEventListener !== 'function') return;
    window.addEventListener('pageshow', handleCollectionPageShow);
    window.addEventListener('pagehide', handleCollectionPageHide);
    window.addEventListener('popstate', handleCollectionPopState);
  };

  const bootstrapCollectionScrollOwnership = () => {
    const queryKey = getCollectionCurrentQueryKey();
    const resolved = resolveCollectionContinuation(queryKey);
    if (!resolved || resolved.continuation.phase === 'settled') {
      return;
    }

    if (!ensureCollectionScrollOwnership(resolved.continuation)) {
      clearCollectionHistoryContinuation(resolved.continuation, { force: true });
      clearCollectionSessionState(queryKey);
      releaseCollectionScrollOwnership();
      return;
    }
    collectionScrollLifecycle.owner.rootState = null;
  };

  const initCollectionInfiniteScroll = (scope = document) => {
    const roots = [];
    if (scope instanceof Element && scope.matches('[data-theme-collection-infinite-root="true"]')) {
      roots.push(scope);
    }

    roots.push(...scope.querySelectorAll('[data-theme-collection-infinite-root="true"]'));

    if (!roots.length) {
      return;
    }

    roots.forEach((root) => {
      const loader = root.querySelector('[data-theme-collection-infinite-scroll]');
      const grid = root.querySelector('.theme-main-collection__grid');

      if (!loader || !grid) return;
      const existingRootState = collectionRootStates.get(root);
      if (existingRootState) {
        collectionScrollLifecycle.rootState = existingRootState;
        if (collectionScrollLifecycle.owner?.queryKey === existingRootState.queryKey) {
          collectionScrollLifecycle.owner.rootState = existingRootState;
        }
        return;
      }

      const status = loader.querySelector('[data-theme-collection-infinite-status]');
      const button = loader.querySelector('[data-theme-collection-infinite-button]');
      const sectionId = root.dataset.themeSectionId || '';
      const queryKey = getCollectionQueryKey(window.location.href);
      const initialPageUrl = normalizeCollectionPageUrl(window.location.href);
      let nextUrl = normalizeCollectionPageUrl(loader.dataset.themeNextUrl || loader.getAttribute('href') || '');
      let loadedPageUrls = initialPageUrl ? [initialPageUrl] : [];
      let isLoading = false;
      let activeController = null;
      let observer;
      let errorState = false;

      const setStatus = (message, isError = false) => {
        if (!status) return;
        status.textContent = message;
        if (isError) {
          status.setAttribute('role', 'alert');
          status.setAttribute('aria-live', 'assertive');
          status.setAttribute('aria-atomic', 'true');
          status.dataset.themeCollectionError = 'true';
        } else if (status.dataset.themeCollectionError !== 'true') {
          status.setAttribute('role', 'status');
          status.setAttribute('aria-live', 'polite');
        }
      };

      const getSourcePageUrl = (item) =>
        normalizeCollectionPageUrl(item?.getAttribute?.(THEME_COLLECTION_SOURCE_PAGE_ATTRIBUTE) || '') || initialPageUrl;

      const tagGridItems = (sourcePageUrl, gridToTag = grid) => {
        if (!sourcePageUrl || !gridToTag) return;
        Array.from(gridToTag.children).forEach((item) => {
          if (!item.hasAttribute(THEME_COLLECTION_SOURCE_PAGE_ATTRIBUTE)) {
            item.setAttribute(THEME_COLLECTION_SOURCE_PAGE_ATTRIBUTE, sourcePageUrl);
          }
        });
      };

      const getProductId = (item) =>
        item?.dataset?.themeProductId || item?.querySelector?.('[data-theme-product-id]')?.dataset?.themeProductId || '';

      const getLoadedProductIds = () => {
        const ids = new Set();
        grid.querySelectorAll('[data-theme-product-id]').forEach((item) => {
          const id = item.dataset.themeProductId;
          if (id) ids.add(id);
        });
        return ids;
      };

      const setLoadMoreHref = () => {
        if (!button || button.tagName !== 'A' || !nextUrl) return;
        button.setAttribute('href', nextUrl);
      };

      const syncLoaderState = () => {
        if (errorState) return;

        const hasNextPage = Boolean(nextUrl);
        loader.hidden = !hasNextPage;
        root.dataset.themeCollectionInfiniteState = hasNextPage ? 'idle' : 'complete';
        setLoadMoreHref();

        if (button) {
          button.hidden = !hasNextPage;
          if ('disabled' in button) button.disabled = !hasNextPage;
          if (!hasNextPage) button.removeAttribute('aria-disabled');
        }

        setStatus(hasNextPage ? 'Scroll to load more' : 'All products loaded');

        if (!hasNextPage && observer) observer.disconnect();
      };

      const abortActiveLoad = () => {
        if (activeController) activeController.abort();
        activeController = null;
      };

      const buildSectionUrl = (href) => {
        const url = new URL(href, window.location.origin);
        if (url.origin !== window.location.origin) throw new Error('Collection page must stay same-origin.');
        if (sectionId) url.searchParams.set('section_id', sectionId);
        return url.toString();
      };

      const readResponseQueryKey = (response, nextRoot, requestedPageUrl) => {
        const responsePageUrl = normalizeCollectionPageUrl(response?.url || requestedPageUrl);
        if (!responsePageUrl) throw new Error('Collection response URL must stay same-origin.');

        const responseKey = getCollectionQueryKey(responsePageUrl);
        if (responseKey !== queryKey) throw new Error('Collection response query state does not match the active collection.');

        const marker = nextRoot?.matches?.(
          '[data-theme-collection-query-key], [data-theme-collection-state-key], [data-theme-canonical-state-key]'
        )
          ? nextRoot
          : nextRoot?.querySelector?.(
            '[data-theme-collection-query-key], [data-theme-collection-state-key], [data-theme-canonical-state-key]'
          );
        const markerKey = marker?.dataset?.themeCollectionQueryKey ||
          marker?.dataset?.themeCollectionStateKey ||
          marker?.dataset?.themeCanonicalStateKey ||
          marker?.getAttribute?.('data-theme-collection-query-key') ||
          marker?.getAttribute?.('data-theme-collection-state-key') ||
          marker?.getAttribute?.('data-theme-canonical-state-key') ||
          '';
        if (markerKey && markerKey !== queryKey) {
          throw new Error('Collection response state marker does not match the active query.');
        }
      };

      const appendResponseItems = (nextGrid, sourcePageUrl) => {
        const existingProductIds = getLoadedProductIds();
        const appendedIds = new Set();
        const items = Array.from(nextGrid.children);

        items.forEach((item) => {
          const productId = getProductId(item);
          if (productId && (existingProductIds.has(productId) || appendedIds.has(productId))) return;
          item.setAttribute(THEME_COLLECTION_SOURCE_PAGE_ATTRIBUTE, sourcePageUrl);
          grid.appendChild(item);
          if (productId) {
            existingProductIds.add(productId);
            appendedIds.add(productId);
          }
        });
      };

      const makeCollectionState = (selection = {}) => ({
        version: THEME_COLLECTION_STATE_VERSION,
        queryKey,
        loadedPageUrls: Array.from(new Set(loadedPageUrls)),
        nextUrl: nextUrl || '',
        selectedProductId: selection.selectedProductId || '',
        selectedProductHref: selection.selectedProductHref || '',
        scrollY: Number.isFinite(selection.scrollY) ? selection.scrollY : readCollectionScrollY() ?? 0,
        focusFromProductLink: selection.focusFromProductLink === true,
        ...(selection.continuation
          ? { continuation: selection.continuation }
          : collectionScrollLifecycle.owner?.rootState === rootState &&
            collectionScrollLifecycle.owner?.queryKey === queryKey
              ? { continuation: {
                id: collectionScrollLifecycle.owner.id,
                phase: collectionScrollLifecycle.owner.phase,
                sourceUrl: collectionScrollLifecycle.owner.sourceUrl,
                targetHref: collectionScrollLifecycle.owner.targetHref,
                previousMode: collectionScrollLifecycle.owner.previousMode
              } }
              : {})
      });

      const persistCollectionState = (selection = {}) => {
        const state = makeCollectionState(selection);
        writeCollectionSessionState(state);
        return writeCollectionHistoryState(state);
      };

      const setLoadError = (error) => {
        if (error?.name === 'AbortError') return;
        console.error(error);
        errorState = true;
        if (observer) observer.disconnect();
        root.dataset.themeCollectionInfiniteState = 'error';
        loader.hidden = false;
        loader.dataset.themeCollectionError = 'true';
        if (button) {
          button.hidden = false;
          if ('disabled' in button) button.disabled = false;
          button.removeAttribute('aria-disabled');
          if (button.tagName === 'A' && nextUrl) button.setAttribute('href', nextUrl);
        }
        setStatus('Unable to load more right now. Use Load more to continue.', true);
      };

      const loadPage = async (rawPageUrl, { restoring = false, persist = true } = {}) => {
        const requestedPageUrl = normalizeCollectionPageUrl(rawPageUrl);
        if (!requestedPageUrl || isLoading || errorState) return false;
        if (getCollectionQueryKey(window.location.href) !== queryKey) return false;
        if (restoring && !isCollectionOwnerCurrent()) return false;

        isLoading = true;
        const controller = new AbortController();
        activeController = controller;
        const requestQueryKey = queryKey;
        root.dataset.themeCollectionInfiniteState = restoring ? 'restoring' : 'loading';
        loader.dataset.themeCollectionLoading = 'true';
        loader.dataset.themeCollectionRestoring = restoring ? 'true' : 'false';
        loader.hidden = false;
        setLoadMoreHref();
        setStatus(restoring ? 'Restoring more products' : 'Loading more products');
        if (button && 'disabled' in button) button.disabled = true;

        try {
          const response = await fetch(buildSectionUrl(requestedPageUrl), {
            credentials: 'same-origin',
            headers: { Accept: 'text/html' },
            signal: controller.signal
          });

          if (!response.ok) {
            throw new Error(`Failed to load collection page: ${response.status}`);
          }

          // Validate the final response URL before consuming or parsing a
          // redirected document. A foreign redirect must never be treated as
          // a same-path collection fragment.
          readResponseQueryKey(response, null, requestedPageUrl);
          const html = await response.text();
          if (requestQueryKey !== queryKey || getCollectionQueryKey(window.location.href) !== requestQueryKey) {
            return false;
          }
          if (restoring && !isCollectionOwnerCurrent()) return false;

          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const responseRoots = Array.from(parsed.querySelectorAll('[data-theme-collection-infinite-root="true"]'));
          const nextRoot = responseRoots.find((candidate) => {
            return !sectionId || candidate.dataset.themeSectionId === sectionId;
          });
          const nextGrid = nextRoot?.querySelector('.theme-main-collection__grid');

          if (!nextRoot || !nextGrid || (sectionId && nextRoot.dataset.themeSectionId !== sectionId)) {
            throw new Error('Collection response is missing the active section.');
          }

          if (restoring && !isCollectionOwnerCurrent()) return false;
          readResponseQueryKey(response, nextRoot, requestedPageUrl);
          appendResponseItems(nextGrid, requestedPageUrl);
          tagGridItems(requestedPageUrl, grid);

          if (!loadedPageUrls.includes(requestedPageUrl)) loadedPageUrls.push(requestedPageUrl);
          const nextLoader = nextRoot.querySelector('[data-theme-collection-infinite-scroll]');
          nextUrl = normalizeCollectionPageUrl(nextLoader?.dataset?.themeNextUrl || nextLoader?.getAttribute?.('href') || '');
          loader.dataset.themeNextUrl = nextUrl;
          if (persist) persistCollectionState();

          init(grid);
          syncLoaderState();
          return true;
        } catch (error) {
          if (error?.name === 'AbortError') return false;
          if (requestQueryKey !== queryKey || getCollectionQueryKey(window.location.href) !== requestQueryKey) return false;
          setLoadError(error);
          return false;
        } finally {
          if (activeController === controller) activeController = null;
          isLoading = false;
          loader.dataset.themeCollectionLoading = 'false';
          loader.dataset.themeCollectionRestoring = 'false';
          if (button && !errorState) {
            if (nextUrl && 'disabled' in button) button.disabled = false;
            if (!nextUrl) syncLoaderState();
          }
        }
      };

      const loadNextPage = () => {
        if (!nextUrl || isLoading || errorState) return;
        loadPage(nextUrl);
      };

      const syncNativeSort = () => {
        const sortSelect = root.querySelector('[data-theme-collection-sort], select[name="sort_by"]');
        if (!sortSelect) return;

        const url = new URL(window.location.href);
        const requestedSort = url.searchParams.get('sort_by');
        const serverDefault = sortSelect.dataset.themeCollectionDefaultSort ||
          sortSelect.querySelector('option[selected]')?.value || sortSelect.value;
        const nextSort = requestedSort || serverDefault;
        if (!nextSort) return;
        if (Array.from(sortSelect.options || []).some((option) => option.value === nextSort)) {
          sortSelect.value = nextSort;
        }
        sortSelect.dataset.themeEventSortOption = sortSelect.value;
      };

      const findSelectedProductLink = (state) => {
        if (!state?.focusFromProductLink || (!state.selectedProductId && !state.selectedProductHref)) return null;

        const productLinks = Array.from(grid.querySelectorAll('[data-theme-product-link]'));
        return productLinks.find((link) => {
          const id = link.dataset.themeProductId || link.closest('[data-theme-product-id]')?.dataset?.themeProductId || '';
          return state.selectedProductId && id === state.selectedProductId;
        }) || productLinks.find((link) => normalizeSameOriginUrl(link.getAttribute?.('href') || link.href || '') === state.selectedProductHref) || null;
      };

      const rootState = {
        root,
        loader,
        grid,
        status,
        button,
        queryKey,
        initialPageUrl,
        get loadedPageUrls() {
          return loadedPageUrls;
        },
        get nextUrl() {
          return nextUrl;
        },
        setNextUrl(value) {
          nextUrl = normalizeCollectionPageUrl(value || '');
          loader.dataset.themeNextUrl = nextUrl;
        },
        get isLoading() {
          return isLoading;
        },
        get errorState() {
          return errorState;
        },
        get observer() {
          return observer;
        },
        getProductId,
        makeCollectionState,
        persistCollectionState,
        abortActiveLoad,
        loadPage,
        syncLoaderState,
        syncNativeSort,
        tagGridItems,
        getSourcePageUrl,
        findSelectedProductLink,
        getLoadedProductIds
      };

      root.addEventListener('click', (event) => {
        const link = event.target?.closest?.('a');
        if (!link || root.contains?.(link) === false) return;

        if (link.matches('[data-theme-product-link]')) {
          // Modified, new-tab, and download activations remain native and do
          // not create a continuation for this collection entry.
          if (isCollectionPrimaryProductActivation(event, link)) {
            armCollectionContinuation(rootState, link);
          }
          return;
        }

        if (link.matches('[data-theme-collection-infinite-button]')) {
          if (errorState || event.defaultPrevented) return;
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey ||
            (event.button !== undefined && event.button !== 0)) return;
          event.preventDefault();
          loadNextPage();
          return;
        }

        abortActiveLoad();
        abandonCollectionContinuation('non-product-navigation');
      });

      root.addEventListener('submit', (event) => {
        if (event.target?.matches?.('form')) {
          abortActiveLoad();
          abandonCollectionContinuation('filter-or-sort-submit');
        }
      });

      root.addEventListener('change', (event) => {
        if (event.target?.matches?.('[data-theme-collection-sort], select[name="sort_by"]')) {
          abortActiveLoad();
          abandonCollectionContinuation('sort-navigation');
        }
      });

      if (button) {
        button.addEventListener('click', (event) => {
          if (errorState || event.defaultPrevented) return;
          if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey ||
            (event.button !== undefined && event.button !== 0)) return;
          event.preventDefault();
          loadNextPage();
        });
      }

      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) loadNextPage();
          });
        }, { rootMargin: '320px 0px' });

        observer.observe(loader);
      }

      tagGridItems(initialPageUrl, grid);
      loader.dataset.themeCollectionInfiniteBound = 'true';
      syncLoaderState();
      syncNativeSort();

      collectionRootStates.set(root, rootState);
      const previousRootState = collectionScrollLifecycle.rootState;
      if (previousRootState && previousRootState !== rootState) {
        previousRootState.abortActiveLoad();
        previousRootState.observer?.disconnect?.();
      }
      collectionScrollLifecycle.rootState = rootState;
      if (collectionScrollLifecycle.owner?.queryKey === queryKey) {
        collectionScrollLifecycle.owner.rootState = rootState;
      }

      if (collectionScrollLifecycle.pageshowSeen && collectionScrollLifecycle.owner?.queryKey === queryKey &&
        ['pending', 'restoring'].includes(collectionScrollLifecycle.owner.phase)) {
        handleCollectionPageShow({ type: 'pageshow', lateInit: true });
      }
    });
  };

  const scheduleCollectionDoubleFrame = (callback) => {
    const runLater = () => {
      try {
        if (typeof window.setTimeout === 'function') {
          window.setTimeout(callback, 0);
          return;
        }
      } catch (_error) {
        // Fall through to the synchronous fallback below.
      }
      callback();
    };

    try {
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(() => {
          try {
            window.requestAnimationFrame(callback);
          } catch (_error) {
            runLater();
          }
        });
        return;
      }
    } catch (_error) {
      // Fall through to a task when rAF is unavailable or throws.
    }
    runLater();
  };

  const beginCollectionRestoration = (rootState, historyState, continuation) => {
    if (!rootState || !historyState || !continuation) return Promise.resolve(false);
    if (collectionScrollLifecycle.restorationRunId === continuation.id || collectionScrollLifecycle.restorationPromise) {
      return collectionScrollLifecycle.restorationPromise || Promise.resolve(false);
    }

    if (!isCollectionOwnerCurrent(collectionScrollLifecycle.owner, ['pending', 'restoring'])) {
      abandonCollectionContinuation('restoration-owner-invalid');
      return Promise.resolve(false);
    }

    const owner = collectionScrollLifecycle.owner;
    collectionScrollLifecycle.restorationRunId = continuation.id;

    if (continuation.phase === 'pending') {
      if (!updateCollectionContinuationPhase(rootState, 'restoring')) {
        abandonCollectionContinuation('restoration-phase-write-failed');
        return Promise.resolve(false);
      }
    }

    const promise = (async () => {
      try {
        rootState.abortActiveLoad();
        rootState.tagGridItems(rootState.initialPageUrl, rootState.grid);

        const currentLoadedPageUrls = Array.from(rootState.grid.children)
          .map((item) => rootState.getSourcePageUrl(item))
          .filter(Boolean);
        rootState.loadedPageUrls.splice(0, rootState.loadedPageUrls.length,
          ...Array.from(new Set(currentLoadedPageUrls.length
            ? currentLoadedPageUrls
            : rootState.initialPageUrl
              ? [rootState.initialPageUrl]
              : [])));

        for (const pageUrl of historyState.loadedPageUrls) {
          if (!isCollectionOwnerCurrent(owner, ['restoring'])) {
            throw new Error('Collection restoration ownership was lost.');
          }
          if (rootState.loadedPageUrls.includes(pageUrl)) continue;
          const restored = await rootState.loadPage(pageUrl, { restoring: true, persist: false });
          if (!restored) throw new Error('Collection restoration failed to load a saved page.');
          if (!isCollectionOwnerCurrent(owner, ['restoring'])) {
            throw new Error('Collection restoration ownership was lost.');
          }
        }

        rootState.loadedPageUrls.splice(0, rootState.loadedPageUrls.length,
          ...Array.from(new Set(historyState.loadedPageUrls.concat(rootState.loadedPageUrls))));
        rootState.setNextUrl(historyState.nextUrl);
        rootState.syncLoaderState();

        if (!rootState.persistCollectionState({
          selectedProductId: historyState.selectedProductId,
          selectedProductHref: historyState.selectedProductHref,
          scrollY: historyState.scrollY,
          focusFromProductLink: historyState.focusFromProductLink,
          continuation: {
            id: owner.id,
            phase: owner.phase,
            sourceUrl: owner.sourceUrl,
            targetHref: owner.targetHref,
            previousMode: owner.previousMode
          }
        })) {
          throw new Error('Collection restoration state could not be persisted.');
        }

        if (!isCollectionOwnerCurrent(owner, ['restoring'])) {
          throw new Error('Collection restoration ownership was lost.');
        }

        await new Promise((resolve) => scheduleCollectionDoubleFrame(resolve));
        if (!isCollectionOwnerCurrent(owner, ['restoring'])) {
          throw new Error('Collection restoration ownership was lost.');
        }

        if (typeof window.scrollTo !== 'function') {
          throw new Error('Collection restoration requires window.scrollTo.');
        }
        window.scrollTo({ top: historyState.scrollY, behavior: 'auto' });
        const selectedLink = rootState.findSelectedProductLink(historyState);
        if (selectedLink) focusWithoutScroll(selectedLink);
        window.scrollTo({ top: historyState.scrollY, behavior: 'auto' });

        if (!isCollectionOwnerCurrent(owner, ['restoring'])) {
          throw new Error('Collection restoration ownership was lost.');
        }
        if (!updateCollectionContinuationPhase(rootState, 'settled')) {
          throw new Error('Collection restoration could not settle its continuation.');
        }
        return true;
      } catch (error) {
        if (collectionScrollLifecycle.owner?.id === owner.id) {
          abandonCollectionContinuation(error?.name === 'AbortError' ? 'restoration-aborted' : 'restoration-failed');
        }
        return false;
      } finally {
        if (collectionScrollLifecycle.restorationPromise === promise) {
          collectionScrollLifecycle.restorationPromise = null;
        }
      }
    })();

    collectionScrollLifecycle.restorationPromise = promise;
    return promise;
  };

  bindCollectionLifecycleListeners();
  bootstrapCollectionScrollOwnership();

  const footerDisclosureBindings = new WeakMap();

  const initFooterDisclosures = (scope = document) => {
    const roots = [];
    if (scope instanceof Element && scope.matches('[data-theme-footer]')) roots.push(scope);
    if (scope.querySelectorAll) roots.push(...scope.querySelectorAll('[data-theme-footer]'));

    roots.forEach((root) => {
      if (footerDisclosureBindings.has(root)) return;

      const groups = Array.from(root.querySelectorAll('.theme-site-footer__group'))
        .map((group) => ({
          button: group.querySelector('[data-theme-footer-disclosure]'),
          panel: group.querySelector('[data-theme-footer-panel]')
        }))
        .filter(({ button, panel }) => button && panel);

      if (!groups.length || typeof window.matchMedia !== 'function') return;

      const mobileQuery = window.matchMedia('(max-width: 749px)');
      const setGroupState = ({ button, panel }, expanded) => {
        button.setAttribute('aria-expanded', String(expanded));
        panel.hidden = !expanded;
      };
      const syncBreakpointState = () => {
        root.classList.add('is-disclosure-ready');
        groups.forEach((group) => {
          if (mobileQuery.matches) {
            group.button.hidden = false;
            setGroupState(group, false);
          } else {
            group.button.hidden = true;
            setGroupState(group, true);
          }
        });
      };

      groups.forEach((group) => {
        group.button.addEventListener('click', () => {
          if (!mobileQuery.matches) return;
          setGroupState(group, group.button.getAttribute('aria-expanded') !== 'true');
        });
      });

      if (typeof mobileQuery.addEventListener === 'function') {
        mobileQuery.addEventListener('change', syncBreakpointState);
      } else {
        mobileQuery.addListener(syncBreakpointState);
      }
      syncBreakpointState();
      footerDisclosureBindings.set(root, { mobileQuery, syncBreakpointState });
    });
  };

  const initCritical = (scope = document) => {
    initA11yControls(scope);
    initPrivacyPreferences(scope);
    initShellAnnouncementCopy();
    initShellHeaderControls(scope);
    initHeaderScrollVisibility(scope);
    initCartAnnouncementLabel();
    initHorizontalRails(scope);
    initCollectionTitleFit(scope);
    initCollectionFilterEnhancements(scope);
    initCollectionInfiniteScroll(scope);
    initFooterDisclosures(scope);
    initReturnLinks(scope);
  };

  const initDeferred = (scope = document) => {
    initAccountOfferPrefill();
    initAccountOfferRail(scope);
    initRichTextTablePolish(scope);
    initWishlistButtons(scope);
    initGlobalContactInlineRouting();
    initContactFormAnchorRouting(scope);
    initContactBotGuard(scope);
    initPdpEventPayloadSync(scope);
    initPdpMediaGallery(scope);
    initPdpRecommendations(scope);
    initPdpFitSizeToggle(scope);
    initPdpReviewPresentation(scope);
    initPdpCartState();
    initReturnTabBubble();
    initNativeFreeShipping();
  };

  const init = (scope = document) => {
    initCritical(scope);
    scheduleIdleTask(() => {
      initDeferred(scope);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    const scope = event && event.target ? event.target : document;
    init(scope);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    abortCollectionFilterDisclosureBindings(event?.target || document);
  });
})();
